/**
 * tool-uploader.js — behaviour for <x-tool-uploader> blade component.
 *
 * Finds every .tool-uploader element on the page, wires a hidden <input type="file">
 * through to it, handles drag-enter/over/leave/drop + click + keyboard activation,
 * pre-validates file size, primes the bytes through FileBytes (so FILE_LOST can't
 * surprise the processing pipeline later), and emits `tool-uploader:file` —
 * a CustomEvent the tool listens for to kick off its own logic.
 *
 * Everything tool-specific (post-upload UI, processing, previews, progress
 * overlays, filename display) stays in the tool's blade. This module owns only
 * the picker + drag-drop + size-gate + cache-prime behaviour that every tool
 * currently re-implements.
 */
(function(){
'use strict';

    // Zones registered for body-level paste (see the visibility rule below).
    var __tuPasteZones = [];

function fmtSize(bytes){
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
    if (bytes >= 1048576)    return Math.round(bytes / 1048576) + ' MB';
    if (bytes >= 1024)       return Math.round(bytes / 1024) + ' KB';
    return bytes + ' B';
}

// Built-in engine specs auto-registered when a tool declares them via
// the warm-engines attribute (avoids 30+ tools repeating the same init
// boilerplate for the universal FFmpeg engine). Register-if-absent
// semantics: a tool that needs a non-default config (e.g. required:true,
// custom onProgress) just calls EngineWarmer.register() itself at script
// init — this fallback only fires when isRegistered returns false.
var BUILTIN_ENGINES = {
    ffmpeg: {
        name: 'ffmpeg', sizeMB: 30, label: 'FFmpeg', required: false,
        init: function(p){
            return window.FFmpegProcessor ? window.FFmpegProcessor.init(p) : Promise.resolve(false);
        },
        /* Дверь к БАЙТАМ без инстанса: на слабой памяти EngineWarmer.warm()
           берёт её вместо init(), а инстанс поднимается уже в ensureWarm() —
           там, где движок реально нужен. Почему это важно и во что обошлось
           отсутствие такой двери — см. комментарий в engine-warmer.js. */
        prefetch: function(p){
            return window.FFmpegProcessor && window.FFmpegProcessor.prefetch
                ? window.FFmpegProcessor.prefetch(p) : Promise.resolve(false);
        }
    }
};

/* Warm a BUILTIN engine on the tool's own schedule instead of on file-pick.
 *
 * `warm-engines="ffmpeg"` starts the download the moment a file is chosen,
 * which is right for a tool whose only lane is FFmpeg. It is 9 MB of wasted
 * mobile data (measured on a cold visit: ffmpeg-core.wasm, compressed) for a
 * tool that now converts most files on the hardware lane and only needs FFmpeg
 * as a fallback. Such a tool drops the attribute and calls this instead, at the
 * moment it learns the fallback will actually be used — same register-if-absent
 * semantics, no second copy of the engine spec. */
window.__warmBuiltinEngine = function(slug, names){
    if (!window.EngineWarmer || typeof EngineWarmer.warm !== 'function') return;
    slug = slug || ((document.querySelector('meta[name="tool-slug"]') || {}).content) || window.__TOOL_SLUG || '';
    if (!slug) return;
    var list = String(names || 'ffmpeg').split(',').map(function(s){ return s.trim(); }).filter(Boolean);
    for (var i = 0; i < list.length; i++) {
        var spec = BUILTIN_ENGINES[list[i]];
        if (!spec) continue;
        if (typeof EngineWarmer.isRegistered === 'function' && EngineWarmer.isRegistered(slug, list[i])) continue;
        try { EngineWarmer.register(slug, [spec]); } catch(_) {}
    }
    try { EngineWarmer.warm(slug, list.join(',')); } catch(_) {}
};

// Localized "too large" text.
var REJECT_L = {
    en: function(sz, max){ return 'File too large: ' + sz + ' exceeds ' + max + ' MB'; },
    ru: function(sz, max){ return 'Файл слишком большой: ' + sz + ', максимум ' + max + ' МБ'; },
    ko: function(sz, max){ return '파일이 너무 큽니다: ' + sz + ', 최대 ' + max + ' MB'; },
    ar: function(sz, max){ return 'الملف كبير جدًا: ' + sz + '، الحد الأقصى ' + max + ' م.ب'; },
    id: function(sz, max){ return 'File terlalu besar: ' + sz + ', maksimum ' + max + ' MB'; },
    pt: function(sz, max){ return 'Arquivo muito grande: ' + sz + ', máximo ' + max + ' MB'; },
    de: function(sz, max){ return 'Datei zu groß: ' + sz + ' überschreitet ' + max + ' MB'; },
    es: function(sz, max){ return 'Archivo demasiado grande: ' + sz + ' supera ' + max + ' MB'; },
    fr: function(sz, max){ return 'Fichier trop volumineux : ' + sz + ' dépasse ' + max + ' Mo'; },
    uk: function(sz, max){ return 'Файл завеликий: ' + sz + ' перевищує ' + max + ' МБ'; },
    tr: function(sz, max){ return 'Dosya çok büyük: ' + sz + ', maksimum ' + max + ' MB'; },
};
// Empty-file reject. Without this gate a 0-byte upload silently propagates to
// downstream `decodeAudioData` / FFmpeg, where some browsers hang instead of
// rejecting — leaving the user staring at an empty workspace.
// Only reached AFTER window.__probeEmptyFile (timbrica-core.js) failed to pull
// any bytes out of the file — a 0-size pick is probed first, because Android
// content providers routinely misreport size 0 for readable files. Canonical
// wording lives in __emptyFileMsg (core); this dict is the offline fallback.
var REJECT_EMPTY_L = {
    en: 'File is empty or unreadable (0 bytes). If it lives in cloud storage, download it to your device first, then pick it again.',
    ru: 'Файл пустой или не читается (0 байт). Если он хранится в облаке — сначала скачайте его на устройство и выберите заново.',
    ko: '파일이 비어 있거나 읽을 수 없습니다(0바이트). 클라우드에 있는 파일이라면 먼저 기기에 다운로드한 후 다시 선택하세요.',
    ar: 'الملف فارغ أو تعذّرت قراءته (0 بايت). إذا كان محفوظًا في السحابة، نزّله إلى جهازك أولًا ثم اختره مجددًا.',
    id: 'File kosong atau tidak dapat dibaca (0 byte). Jika file ada di cloud, unduh dulu ke perangkat Anda lalu pilih lagi.',
    pt: 'O arquivo está vazio ou ilegível (0 bytes). Se estiver na nuvem, baixe-o para o dispositivo primeiro e selecione novamente.',
    de: 'Die Datei ist leer oder nicht lesbar (0 Bytes). Liegt sie in der Cloud, laden Sie sie zuerst auf Ihr Gerät herunter und wählen Sie sie erneut.',
    es: 'El archivo está vacío o no se puede leer (0 bytes). Si está en la nube, descárgalo primero a tu dispositivo y vuelve a elegirlo.',
    fr: 'Le fichier est vide ou illisible (0 octet). S’il est stocké dans le cloud, téléchargez-le d’abord sur votre appareil puis re-sélectionnez-le.',
    uk: 'Файл порожній або не читається (0 байт). Якщо він зберігається у хмарі, спершу завантажте його на пристрій і виберіть знову.',
    tr: 'Dosya boş veya okunamıyor (0 bayt). Bulut depolamada duruyorsa önce cihazınıza indirin, sonra tekrar seçin.',
};
// Older single-string fallback. Kept for tools that show this message via
// other code paths (decode-fail, codec-unsupported) and pass no file context.
var WRONG_FORMAT_L = {
    en: 'Unsupported file format — try one of the listed formats.',
    ru: 'Формат файла не поддерживается — выберите один из указанных.',
    ko: '지원되지 않는 파일 형식입니다 — 나열된 형식 중 하나를 선택하세요.',
    ar: 'تنسيق الملف غير مدعوم — جرّب أحد التنسيقات المدرجة.',
    id: 'Format file tidak didukung — gunakan salah satu format yang terdaftar.',
    pt: 'Formato de arquivo não suportado — escolha um dos formatos listados.',
    de: 'Dateiformat nicht unterstützt — wähle eines der aufgeführten Formate.',
    es: 'Formato de archivo no compatible — prueba uno de los formatos indicados.',
    fr: 'Format de fichier non pris en charge — essayez l\'un des formats indiqués.',
    uk: 'Формат файлу не підтримується — спробуйте один із указаних форматів.',
    tr: 'Desteklenmeyen dosya biçimi — listelenen biçimlerden birini deneyin.',
};

// MIDI signposting reject — .mid/.midi is "instructions, not sound" and will
// silently fail every browser decoder (decodeAudioData/FFmpeg/WebCodecs). Users
// who drop a MIDI on an audio tool see "Could not decode" and give up; the few
// who keep looking find /midi (MIDI→MP3 converter) and self-rescue (Dinara
// 06-24, comment #820: 4 tools tried before discovering /midi). 30-day
// telemetry: 45 .mid + 1 .midi file_picked events. Catch BEFORE the decoder so
// we can hand the user a clickable shortcut. NOTE: rendered as innerHTML — keep
// to a single trusted <a href='/midi'> tag, no interpolated user content.
var REJECT_MIDI_L = {
    en: "This is a MIDI file (instructions, not sound). First convert it to MP3 with the <a href='/midi'>MIDI converter</a>, then upload it here.",
    ru: "Это MIDI-файл (инструкции, не звук). Сначала превратите его в MP3 через <a href='/midi'>конвертер MIDI</a>, потом загрузите сюда.",
    ko: "이 파일은 MIDI 파일(소리가 아닌 명령)입니다. 먼저 <a href='/midi'>MIDI 변환기</a>로 MP3로 변환한 다음 여기에 업로드하세요.",
    ar: "هذا ملف MIDI (تعليمات، وليس صوتًا). حوّله أولًا إلى MP3 عبر <a href='/midi'>محوّل MIDI</a>، ثم ارفعه هنا.",
    id: "Ini berkas MIDI (instruksi, bukan suara). Konversikan dulu ke MP3 dengan <a href='/midi'>konverter MIDI</a>, lalu unggah di sini.",
    pt: "Este é um arquivo MIDI (instruções, não som). Converta-o primeiro para MP3 com o <a href='/midi'>conversor MIDI</a> e depois envie aqui.",
    de: "Das ist eine MIDI-Datei (Anweisungen, kein Ton). Wandle sie zuerst mit dem <a href='/midi'>MIDI-Konverter</a> in MP3 um und lade sie dann hier hoch.",
    es: "Este es un archivo MIDI (instrucciones, no sonido). Conviértelo primero a MP3 con el <a href='/midi'>conversor MIDI</a> y súbelo aquí después.",
    fr: "C'est un fichier MIDI (des instructions, pas du son). Convertis-le d'abord en MP3 avec le <a href='/midi'>convertisseur MIDI</a>, puis envoie-le ici.", uk: "Це MIDI-файл (інструкції, не звук). Спочатку перетворіть його на MP3 через <a href='/midi'>конвертер MIDI</a>, потім завантажте сюди.",
    tr: "Bu bir MIDI dosyasıdır (ses değil, talimat içerir). Önce <a href='/midi'>MIDI dönüştürücü</a> ile MP3'e çevirin, sonra buraya yükleyin.",
};
// True if `file` looks like MIDI by extension OR MIME. Browsers vary: Chrome
// gives audio/midi for .mid, Firefox often '' (empty), Safari audio/mid.
// We accept any of these + the rare .midi extension.
function isMidiFile(file){
    if (!file) return false;
    var name = (file.name || '').toLowerCase();
    if (/\.midi?$/.test(name)) return true;
    var type = (file.type || '').toLowerCase();
    if (type === 'audio/midi' || type === 'audio/x-midi' || type === 'audio/mid' || type === 'audio/x-mid') return true;
    return false;
}

// Shown WHILE a picked file is being decoded (decode-audio tools). Before this,
// the zone sat on its default prompt during the (sometimes multi-second) decode
// — on slow paths (in-app WebViews fall to the FFmpeg-WASM tier, ~tens of
// seconds) the tool looked frozen and users abandoned thinking nothing happened
// (daria.m999 bug-report 2026-06-20, iOS Yandex in-app, 9 MB m4a → no WebCodecs
// AudioDecoder in the WKWebView → slow FFmpeg tier, zero feedback).
var DECODING_L = {
    en: 'Processing your file…',
    ru: 'Обрабатываю файл…',
    ko: '파일을 처리하는 중…',
    ar: 'جارٍ معالجة الملف…',
    id: 'Memproses file Anda…',
    pt: 'Processando seu arquivo…',
    de: 'Datei wird verarbeitet…',
    es: 'Procesando tu archivo…',
    fr: 'Traitement de votre fichier…', uk: 'Обробляю файл…',
    tr: 'Dosyanız işleniyor…',
};
// Terminal message when decode never produces a usable buffer — a real failure
// OR the stall-watchdog firing (robust() neither resolved nor rejected in time,
// e.g. iOS throttled its internal setTimeout). Either way the user gets an
// actionable verdict instead of a permanently frozen, silent tool.
var DECODE_FAIL_L = {
    en: 'Could not process this file. Try a different format or a smaller file.',
    ru: 'Не удалось обработать этот файл. Попробуйте другой формат или файл поменьше.',
    ko: '이 파일을 처리할 수 없습니다. 다른 형식이나 더 작은 파일을 사용해 보세요.',
    ar: 'تعذّرت معالجة هذا الملف. جرّب تنسيقًا آخر أو ملفًا أصغر.',
    id: 'Tidak dapat memproses file ini. Coba format lain atau file yang lebih kecil.',
    pt: 'Não foi possível processar este arquivo. Tente outro formato ou um arquivo menor.',
    de: 'Diese Datei konnte nicht verarbeitet werden. Versuche ein anderes Format oder eine kleinere Datei.',
    es: 'No se pudo procesar este archivo. Prueba con otro formato o un archivo más pequeño.',
    fr: 'Impossible de traiter ce fichier. Essayez un autre format ou un fichier plus petit.', uk: 'Не вдалося обробити цей файл. Спробуйте інший формат або менший файл.',
    tr: 'Bu dosya işlenemedi. Farklı bir biçim veya daha küçük bir dosya deneyin.',
};
// A video whose container has NO audio stream (the decoder's Tier-0 probe says so in ~150 ms,
// NoAudioStreamError). «Try another format or a smaller file» sent people to re-encode a clip
// that has nothing to decode (QA /mashup-maker 2026-09-24: a silent screen capture).
var NO_AUDIO_L = {
    en: 'This video has no audio track — choose a file with sound.',
    ru: 'В этом видео нет звуковой дорожки — выберите файл со звуком.',
    ko: '이 동영상에는 오디오 트랙이 없습니다. 소리가 있는 파일을 선택하세요.',
    ar: 'لا يحتوي هذا الفيديو على مسار صوتي — اختر ملفًا فيه صوت.',
    id: 'Video ini tidak memiliki trek audio — pilih file yang ada suaranya.',
    pt: 'Este vídeo não tem faixa de áudio — escolha um arquivo com som.',
    de: 'Dieses Video hat keine Tonspur — wähle eine Datei mit Ton.',
    es: 'Este video no tiene pista de audio: elige un archivo con sonido.',
    fr: 'Cette vidéo n’a pas de piste audio — choisissez un fichier avec du son.', uk: 'У цьому відео немає звукової доріжки — виберіть файл зі звуком.',
    tr: 'Bu videoda ses parçası yok — sesli bir dosya seçin.',
};

// Concrete reject message per locale × needed-kind × given-kind. The previous
// generic "Unsupported file format" left users staring at the upload zone
// confused: a Hong-Kong user uploaded a JPG to /voice-changer, hit reject,
// rage-clicked 4× across 30 min, and reported "drop box not working". Saying
// what the tool needs and what the user gave fixes the diagnosis instantly.
// Russian needs INSTRUMENTAL case (the template's preposition "с" requires it):
// "работает с аудиофайлОМ" not "с аудиофайл". Pre-2026-06 strings used
// nominative — every RU WRONG_FORMAT toast was ungrammatical. Indeclinable
// nouns (аудио, видео) stay as-is. image_audio is a NEW bucket added for
// uploaders that accept BOTH image and audio (slideshow-maker / cover-art /
// photo-album — describeNeededKind previously folded these into 'audio' and
// silently dropped the image side: feedback 2026-06-06).
var WRONG_FORMAT_NEEDS = {
    en: { media: 'an image, video or audio file', av: 'an audio or video file', audio: 'an audio file', video: 'a video file', image: 'an image file', image_audio: 'an image or audio file', model: 'a 3D model file', subtitle: 'a subtitle file', file: 'a compatible file' },
    ru: { media: 'изображениями, видео и аудио', av: 'аудио или видео', audio: 'аудиофайлом', video: 'видеофайлом', image: 'изображением', image_audio: 'изображением или аудио', model: '3D-моделью', subtitle: 'файлом субтитров', file: 'подходящим файлом' },
    ko: { media: '이미지, 비디오 또는 오디오 파일', av: '오디오 또는 비디오 파일', audio: '오디오 파일', video: '비디오 파일', image: '이미지 파일', image_audio: '이미지 또는 오디오 파일', model: '3D 모델 파일', subtitle: '자막 파일', file: '호환되는 파일' },
    ar: { media: 'ملف صورة أو فيديو أو صوت', av: 'ملف صوتي أو فيديو', audio: 'ملف صوتي', video: 'ملف فيديو', image: 'ملف صورة', image_audio: 'ملف صورة أو صوت', model: 'ملف نموذج 3D', subtitle: 'ملف ترجمة', file: 'ملفًا متوافقًا' },
    id: { media: 'file gambar, video, atau audio', av: 'file audio atau video', audio: 'file audio', video: 'file video', image: 'file gambar', image_audio: 'file gambar atau audio', model: 'file model 3D', subtitle: 'berkas takarir', file: 'file yang kompatibel' },
    pt: { media: 'um arquivo de imagem, vídeo ou áudio', av: 'um arquivo de áudio ou vídeo', audio: 'um arquivo de áudio', video: 'um arquivo de vídeo', image: 'um arquivo de imagem', image_audio: 'um arquivo de imagem ou áudio', model: 'um arquivo de modelo 3D', subtitle: 'um ficheiro de legendas', file: 'um arquivo compatível' },
    de: { media: 'eine Bild-, Video- oder Audiodatei', av: 'eine Audio- oder Videodatei', audio: 'eine Audiodatei', video: 'eine Videodatei', image: 'eine Bilddatei', image_audio: 'eine Bild- oder Audiodatei', model: 'eine 3D-Modelldatei', subtitle: 'eine Untertiteldatei', file: 'eine passende Datei' },
    es: { media: 'un archivo de imagen, video o audio', av: 'un archivo de audio o vídeo', audio: 'un archivo de audio', video: 'un archivo de vídeo', image: 'un archivo de imagen', image_audio: 'un archivo de imagen o audio', model: 'un archivo de modelo 3D', subtitle: 'un archivo de subtítulos', file: 'un archivo compatible' },
    // French: leading "d'" because the template phrase "Cet outil a besoin"
    // takes "de", and "de + un …" elides to "d'un …" (no other native phrasing
    // keeps it natural). Keeping the elision inside the noun phrase lets the
    // template stay a plain concatenation.
    fr: { media: "d'un fichier image, vidéo ou audio", av: "d'un fichier audio ou vidéo", audio: "d'un fichier audio", video: "d'un fichier vidéo", image: "d'un fichier image", image_audio: "d'un fichier image ou audio", model: "d'un fichier de modèle 3D", subtitle: "d'un fichier de sous-titres", file: "d'un fichier compatible" },
    uk: { media: 'файл зображення, відео або аудіо', av: 'аудіо- або відеофайл', audio: 'аудіофайл', video: 'відеофайл', image: 'файл зображення', image_audio: 'файл зображення або аудіо', model: 'файл 3D-моделі', subtitle: 'файл субтитрів', file: 'сумісний файл' },
    tr: { media: 'bir görsel, video veya ses dosyası', av: 'bir ses veya video dosyası', audio: 'bir ses dosyası', video: 'bir video dosyası', image: 'bir görsel dosyası', image_audio: 'bir görsel veya ses dosyası', model: 'bir 3D model dosyası', subtitle: 'bir altyazı dosyası', file: 'uyumlu bir dosya' },
};
var WRONG_FORMAT_GAVE = {
    en: { image: 'an image', video: 'a video', audio: 'an audio file', pdf: 'a PDF', text: 'a text file', archive: 'an archive', generic: function(ext){ return 'a ' + ext + ' file'; }, unknown: 'a file with an unsupported type' },
    ru: { image: 'изображение', video: 'видео', audio: 'аудиофайл', pdf: 'PDF', text: 'текстовый файл', archive: 'архив', generic: function(ext){ return 'файл ' + ext; }, unknown: 'файл неподдерживаемого типа' },
    ko: { image: '이미지', video: '비디오', audio: '오디오 파일', pdf: 'PDF', text: '텍스트 파일', archive: '압축 파일', generic: function(ext){ return ext + ' 파일'; }, unknown: '지원되지 않는 형식의 파일' },
    ar: { image: 'صورة', video: 'فيديو', audio: 'ملفًا صوتيًا', pdf: 'PDF', text: 'ملفًا نصيًا', archive: 'أرشيفًا', generic: function(ext){ return 'ملف ' + ext; }, unknown: 'ملفًا بتنسيق غير مدعوم' },
    id: { image: 'gambar', video: 'video', audio: 'file audio', pdf: 'PDF', text: 'file teks', archive: 'arsip', generic: function(ext){ return 'file ' + ext; }, unknown: 'file dengan format tidak didukung' },
    pt: { image: 'uma imagem', video: 'um vídeo', audio: 'um arquivo de áudio', pdf: 'um PDF', text: 'um arquivo de texto', archive: 'um arquivo compactado', generic: function(ext){ return 'um arquivo ' + ext; }, unknown: 'um arquivo de tipo não suportado' },
    de: { image: 'ein Bild', video: 'ein Video', audio: 'eine Audiodatei', pdf: 'ein PDF', text: 'eine Textdatei', archive: 'ein Archiv', generic: function(ext){ return 'eine ' + ext + '-Datei'; }, unknown: 'eine Datei mit nicht unterstütztem Typ' },
    es: { image: 'una imagen', video: 'un vídeo', audio: 'un archivo de audio', pdf: 'un PDF', text: 'un archivo de texto', archive: 'un archivo comprimido', generic: function(ext){ return 'un archivo ' + ext; }, unknown: 'un archivo con un tipo no compatible' },
    fr: { image: 'une image', video: 'une vidéo', audio: 'un fichier audio', pdf: 'un PDF', text: 'un fichier texte', archive: 'une archive', generic: function(ext){ return 'un fichier ' + ext; }, unknown: 'un fichier d\'un type non pris en charge' },
    uk: { image: 'зображення', video: 'відео', audio: 'аудіофайл', pdf: 'PDF', text: 'текстовий файл', archive: 'архів', generic: function(ext){ return 'файл ' + ext; }, unknown: 'файл невідомого типу' },
    tr: { image: 'bir görsel', video: 'bir video', audio: 'bir ses dosyası', pdf: 'bir PDF', text: 'bir metin dosyası', archive: 'bir arşiv', generic: function(ext){ return 'bir ' + ext + ' dosyası'; }, unknown: 'desteklenmeyen türde bir dosya' },
};
var WRONG_FORMAT_TPL = {
    en: function(needs, gave){ return 'This tool needs ' + needs + '. You uploaded ' + gave + '.'; },
    ru: function(needs, gave){ return 'Этот инструмент работает с ' + needs + '. Вы загрузили ' + gave + '.'; },
    ko: function(needs, gave){ return '이 도구는 ' + needs + '이 필요합니다. ' + gave + '을(를) 업로드했습니다.'; },
    ar: function(needs, gave){ return 'تحتاج هذه الأداة إلى ' + needs + '. لقد رفعت ' + gave + '.'; },
    id: function(needs, gave){ return 'Alat ini memerlukan ' + needs + '. Anda mengunggah ' + gave + '.'; },
    pt: function(needs, gave){ return 'Esta ferramenta precisa de ' + needs + '. Você enviou ' + gave + '.'; },
    de: function(needs, gave){ return 'Dieses Tool braucht ' + needs + '. Du hast ' + gave + ' hochgeladen.'; },
    es: function(needs, gave){ return 'Esta herramienta necesita ' + needs + '. Has subido ' + gave + '.'; },
    fr: function(needs, gave){ return 'Cet outil a besoin ' + needs + '. Vous avez envoyé ' + gave + '.'; },
    uk: function(needs, gave){ return 'Цьому інструменту потрібно: ' + needs + '. Ви надіслали: ' + gave + '.'; },
    tr: function(needs, gave){ return 'Bu araç ' + needs + ' gerektirir. Siz ' + gave + ' yüklediniz.'; },
};

function describeNeededKind(accept){
    if (!accept) return 'file';
    var a = accept.toLowerCase();
    var hasA = a.indexOf('audio') !== -1 || /\.(mp3|wav|ogg|flac|aac|m4a|wma|aiff|opus|amr|caf|oga)\b/.test(a);
    var hasV = a.indexOf('video') !== -1 || /\.(mp4|mov|mkv|avi|webm|flv|wmv|m4v|3gp|ts)\b/.test(a);
    var hasI = a.indexOf('image') !== -1 || /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif|tiff|avif)\b/.test(a);
    var hasM = /\.(stl|obj|3mf|ply|glb|gltf|fbx|dae|gcode|gco)\b/.test(a);
    /* `.stl` is TWO formats: stereolithography and EBU STL, the broadcast
       subtitle container — and the 3D test above matches both. /subtitle-
       converter accepts EBU STL, so it used to answer every wrong file with
       "this tool needs a 3D model file". Subtitles are checked first and, when
       an accept list carries genuine subtitle formats, they win the `.stl`
       tie: no 3D tool on the site also accepts .srt/.vtt/.ass. */
    var hasS = /\.(srt|vtt|ass|ssa|sbv|ttml|dfxp|itt|sami|smi|lrc|sub)\b/.test(a)
            || a.indexOf('x-subrip') !== -1;
    if (hasS && !hasA && !hasV && !hasI) return 'subtitle';
    // A zone that takes pictures, video AND sound (/ai-label) used to answer
    // a GIF with «this tool works with audio or video» — the image side was
    // silently dropped, exactly as image_audio below once did.
    if (hasA && hasV && hasI) return 'media';
    if (hasA && hasV) return 'av';
    if (hasV) return 'video';
    // image+audio combos (slideshow-maker / cover-art / photo-album) used
    // to fall through to 'audio' because the audio branch fired first,
    // silently dropping the image side. See WRONG_FORMAT_NEEDS.image_audio.
    if (hasI && hasA) return 'image_audio';
    if (hasA) return 'audio';
    if (hasI) return 'image';
    if (hasM) return 'model';
    return 'file';
}
// True when an uploader wants a displayable raster image (accepts image/* or a
// concrete non-HEIC format). An iPhone HEIC dropped on such an uploader is
// decoded to JPEG at the boundary (emit). Pure HEIC/HEIF accepts — e.g.
// /heic-converter, which converts the raw bytes itself — return false and keep
// the original file untouched.
function wantsDecodedImage(accept){
    if (!accept) return false;
    var a = accept.toLowerCase();
    if (a.indexOf('image/*') !== -1) return true;
    if (/\b(jpe?g|png|webp|gif|bmp|tiff?|avif)\b/.test(a)) return true;
    if (/image\/(jpeg|png|webp|gif|bmp|tiff|avif|svg|x-icon)/.test(a)) return true;
    return false;
}
function describeGivenFile(file, locale){
    var labels = WRONG_FORMAT_GAVE[locale] || WRONG_FORMAT_GAVE.en;
    if (!file) return labels.unknown;
    var t = (file.type || '').toLowerCase();
    if (t.indexOf('image/') === 0) return labels.image;
    if (t.indexOf('video/') === 0) return labels.video;
    if (t.indexOf('audio/') === 0) return labels.audio;
    if (t === 'application/pdf') return labels.pdf;
    if (t.indexOf('text/') === 0 || t === 'application/json' || t === 'application/xml') return labels.text;
    if (/zip|rar|7z|tar|gzip|x-compressed/.test(t)) return labels.archive;
    var name = (file.name || '');
    var ext = name.indexOf('.') !== -1 ? name.split('.').pop().toUpperCase().slice(0, 6) : '';
    return ext ? labels.generic(ext) : labels.unknown;
}
// Same bucket on both sides — "this tool needs an image, you uploaded an
// image" — is what a JPG dropped on /heic-converter produced (3 of 7 rejects
// there in 12 days, 2026-09-05): the zone accepts one concrete FORMAT of a
// kind, so the useful sentence names the formats, not the kind.
var WRONG_FORMAT_EXT_TPL = {
    en: function(exts, gave){ return 'This tool accepts ' + exts + ' files. You uploaded ' + gave + '.'; },
    ru: function(exts, gave){ return 'Этот инструмент принимает файлы ' + exts + '. Вы загрузили ' + gave + '.'; },
    ko: function(exts, gave){ return '이 도구는 ' + exts + ' 파일만 받습니다. ' + gave + '을(를) 업로드했습니다.'; },
    ar: function(exts, gave){ return 'تقبل هذه الأداة ملفات ' + exts + ' فقط. لقد رفعت ' + gave + '.'; },
    id: function(exts, gave){ return 'Alat ini menerima file ' + exts + '. Anda mengunggah ' + gave + '.'; },
    pt: function(exts, gave){ return 'Esta ferramenta aceita arquivos ' + exts + '. Você enviou ' + gave + '.'; },
    de: function(exts, gave){ return 'Dieses Tool nimmt ' + exts + '-Dateien an. Du hast ' + gave + ' hochgeladen.'; },
    es: function(exts, gave){ return 'Esta herramienta acepta archivos ' + exts + '. Has subido ' + gave + '.'; },
    fr: function(exts, gave){ return 'Cet outil accepte les fichiers ' + exts + '. Vous avez envoyé ' + gave + '.'; },
    uk: function(exts, gave){ return 'Цей інструмент приймає файли ' + exts + '. Ви надіслали ' + gave + '.'; },
    tr: function(exts, gave){ return 'Bu araç ' + exts + ' dosyalarını kabul eder. Siz ' + gave + ' yüklediniz.'; },
};
function acceptedExtensions(accept){
    var out = [], seen = {};
    (accept || '').split(',').forEach(function(tok){
        tok = tok.trim().toLowerCase();
        if (tok.charAt(0) !== '.' || tok.length < 2 || tok.length > 6) return;
        var e = tok.slice(1).toUpperCase();
        if (!seen[e]) { seen[e] = 1; out.push(e); }
    });
    return out;
}
function givenKindBucket(file){
    var t = ((file && file.type) || '').toLowerCase();
    if (t.indexOf('image/') === 0) return 'image';
    if (t.indexOf('video/') === 0) return 'video';
    if (t.indexOf('audio/') === 0) return 'audio';
    var ext = ((file && file.name) || '').split('.').pop().toLowerCase();
    if (/^(jpe?g|png|gif|webp|bmp|heic|heif|tiff?|avif)$/.test(ext)) return 'image';
    if (/^(mp4|mov|mkv|avi|webm|m4v|3gp)$/.test(ext)) return 'video';
    if (/^(mp3|wav|ogg|flac|aac|m4a|wma|aiff|opus)$/.test(ext)) return 'audio';
    return '';
}
// Does a zone that needs `needKind` take files of `bucket` at all? Then the
// file was turned away for its FORMAT, and the sentence names the formats.
function kindCovers(needKind, bucket){
    if (!bucket) return false;
    if (needKind === bucket) return true;
    if (needKind === 'media') return bucket === 'image' || bucket === 'video' || bucket === 'audio';
    if (needKind === 'av') return bucket === 'audio' || bucket === 'video';
    if (needKind === 'image_audio') return bucket === 'image' || bucket === 'audio';
    return false;
}
function buildWrongFormatMsg(el, file, allRejected){
    var locale = (window.__LOCALE || 'en').substring(0, 2);
    // No-file path: legacy code paths (decode-fail, manual __reject calls)
    // hand us nothing to describe — fall back to the single-string message.
    if (!file) return WRONG_FORMAT_L[locale] || WRONG_FORMAT_L.en;
    var needsMap = WRONG_FORMAT_NEEDS[locale] || WRONG_FORMAT_NEEDS.en;
    var tpl = WRONG_FORMAT_TPL[locale] || WRONG_FORMAT_TPL.en;
    var needKind = describeNeededKind(el.dataset.accept || '');
    var needs = needsMap[needKind] || needsMap.file;
    var gave = describeGivenFile(file, locale);
    var base = tpl(needs, gave);
    if (kindCovers(needKind, givenKindBucket(file))) {
        var exts = acceptedExtensions(el.dataset.accept || '');
        var gaveExt = ((file.name || '').indexOf('.') !== -1 ? file.name.split('.').pop().toUpperCase().slice(0, 6) : '');
        if (exts.length && gaveExt && exts.indexOf(gaveExt) === -1) {
            var gaveLabels = WRONG_FORMAT_GAVE[locale] || WRONG_FORMAT_GAVE.en;
            base = (WRONG_FORMAT_EXT_TPL[locale] || WRONG_FORMAT_EXT_TPL.en)(exts.join(', '), gaveLabels.generic(gaveExt));
        }
    }
    // Multi-file rejection: append the rejected file names so the user sees
    // ALL skipped files, not just the first one. Audit wf_9d2ce135 #27.
    if (allRejected && allRejected.length > 1) {
        var names = [];
        for (var i = 0; i < allRejected.length && i < 4; i++) names.push(allRejected[i].name || '?');
        var more = allRejected.length > 4 ? ' (+' + (allRejected.length - 4) + ')' : '';
        base += ' — ' + names.join(', ') + more;
    }
    return base;
}

function showReject(el, msg, announce){
    var banner = el.querySelector('.tu-reject');
    if (banner) {
        var msgEl = banner.querySelector('.tu-reject-msg');
        if (msgEl) msgEl.textContent = msg;
        banner.classList.remove('tu-hint');   // a refusal, not the «file goes here» hint
        banner.hidden = false;
        // On small mobile viewports (e.g. iPhone 430×739 portrait, landscape-
        // rotated phones, narrow split-screen) the reject banner can sit
        // below the visible area — the upload tile + intro + how-to push
        // the banner off-screen. Without this scroll, the user drops the
        // wrong file type and sees NOTHING happen (the shake is on the
        // upload zone, not the banner). Bug 2026-06-10 03:46:53 (iPhone
        // Safari 18, came from /ru/denoise with an AUDIO file dropped on
        // extract-audio): repro confirmed `rejectInViewport: false`.
        // scrollIntoView({block:'nearest'}) is a no-op on desktops where
        // the banner is already in view — matches the CLAUDE.md global
        // auto-scroll rule (only block:'nearest' allowed).
        try { banner.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch(_){}
    }
    // Shake — clearing the class between triggers restarts the animation.
    el.classList.remove('tu-shaking');
    // eslint-disable-next-line no-unused-expressions
    el.offsetWidth;
    el.classList.add('tu-shaking');
    setTimeout(function(){ el.classList.remove('tu-shaking'); }, 450);
    // Screen reader announcement (role=alert on banner already does this; this
    // line is a fallback for browsers that throttle aria-live on rapid updates).
    if (announce !== false && typeof window.__announce === 'function') window.__announce(msg);
}
function hideReject(el){
    var banner = el.querySelector('.tu-reject');
    if (banner) banner.hidden = true;
}
// HTML-aware reject — same banner + shake as showReject but assigns innerHTML
// so trusted markup (the /midi link in the MIDI signposting message, etc.)
// renders as real anchors. The plain-text fallback to __announce strips tags
// so a screen reader hears the prose without "less-than a href slash midi".
function showRejectHtml(el, html, announce){
    var banner = el.querySelector('.tu-reject');
    if (banner) {
        var msgEl = banner.querySelector('.tu-reject-msg');
        if (msgEl) msgEl.innerHTML = html;
        banner.hidden = false;
        try { banner.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch(_){}
    }
    el.classList.remove('tu-shaking');
    // eslint-disable-next-line no-unused-expressions
    el.offsetWidth;
    el.classList.add('tu-shaking');
    setTimeout(function(){ el.classList.remove('tu-shaking'); }, 450);
    if (announce !== false && typeof window.__announce === 'function') {
        window.__announce(String(html).replace(/<[^>]+>/g, ''));
    }
}
function announcePicked(el, file){
    var polite = el.querySelector('.tu-announce');
    if (polite) polite.textContent = (file.name || 'file') + ' — ' + fmtSize(file.size);
}

// ───── Picked-file row (data-collapse="1") ─────
//
// Once a file is accepted the prompt stops being true, so the zone collapses to
// "<icon> name · size  [Another file] [×]". Before this the component left the
// post-pick state entirely to each tool: of the 254 tools on the shared uploader,
// 111 hid the zone themselves and 143 left the invitation sitting there under a
// loaded file (measured 2026-08-04). Owning it here is what makes the answer the
// same on every tool instead of depending on who remembered.
//
// Deliberately NOT tied to the tool's processing state: the row appears the
// moment the file is ACCEPTED, not when the tool finishes with it. A tool that
// takes 40 s to decode still shows which file it is working on.

/* The row's element, cached: once it has been re-homed (see keepRowReachable)
   it is no longer a descendant of the zone, and el.querySelector('.tu-picked')
   would answer null — i.e. the file name would silently stop updating. */
function pickedRow(el){
    if (!el.__tuRow || !el.__tuRow.isConnected) el.__tuRow = el.querySelector('.tu-picked') || el.__tuRow;
    return el.__tuRow;
}

function boxed(node){
    if (!node) return false;
    var r = node.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
}

/* ── The row has to survive a tool that hides the zone ─────────────────────
   Measured 2026-08-04 and written into the header above: 111 of the 254 tools
   on the shared uploader hide the zone themselves once a file is in. Hiding it
   takes THIS row down with it, and the row is the only «Another file» / «×» a
   tool gets for free. Browser sweep of the 47 tools that still do it
   (2026-09-06, tests/playwright/uploader-second-file-sweep.mjs): 20 left the
   person with NO way at all to reach a second file before the tool produced a
   result — the bug report that started this was one of them
   (/en/video-filter, «Another video hides», 2026-09-02).

   So the row moves out of the zone instead of dying with it: same place on
   screen (immediately after the hidden zone), same buttons, same handlers.
   The zone stays hidden exactly as the tool asked — the row is the component's
   property, not the tool's.

   Front door for new code is still «don't hide the zone»
   (UploaderLoadedStateConventionTest). This is the safety net for the ones
   already written, the same shape as __downloadFile's click interceptor.

   Opt out with data-no-rehome="1" on the zone if a tool ever needs the row to
   disappear with it. */
function keepRowReachable(el){
    if (el.dataset.noRehome === '1') return;
    var row = pickedRow(el);
    if (!row || row.hidden) return;
    var away = row.parentNode !== el;

    if (!boxed(el) && !away) {
        var anchor = hiddenAnchor(el);
        if (!anchor || !anchor.parentNode) return;
        el.__tuRowNext = row.nextSibling;
        anchor.parentNode.insertBefore(row, anchor.nextSibling);
        row.classList.add('tu-picked-detached');
        /* Still no box? Then nothing here can hold it — an invisible node
           parked outside its component would be pure litter. Put it back and
           say so out loud, so the case is a number instead of another silent
           year. */
        if (!boxed(row)) {
            homeRow(el);
            if (!el.__tuSaidUnreachable) {
                el.__tuSaidUnreachable = 1;
                tuEvent('uploader_row_unreachable', { zone: el.id || '' });
            }
        } else if (!el.__tuSaidRehomed) {
            el.__tuSaidRehomed = 1;
            tuEvent('uploader_row_rehomed', { zone: el.id || '', anchor: anchor === el ? 'zone' : (anchor.id || 'wrapper') });
        }
        return;
    }
    if (boxed(el) && away) homeRow(el);
}

/* Where to park the row. Half the tools hide the zone itself, the other half
   hide a card around it (#bsUploadWrap, #vs-analyze-intro, an "upload screen"),
   and inserting the row next to a zone inside a hidden card would just make it
   invisible one level down. So climb to the outermost element that has no box
   while its parent still has one — that node is what the tool took off the
   page, and the row belongs immediately after it, which is exactly where the
   zone used to be on screen. Stops at the tool's content area: the row must
   never end up outside the page's own body. */
function hiddenAnchor(el){
    var node = el, anchor = null, hops = 0;
    while (node && node.parentNode && node !== document.body && hops++ < 12) {
        if (!boxed(node) && boxed(node.parentNode)) anchor = node;
        node = node.parentNode;
    }
    return anchor;
}

function homeRow(el){
    var row = el.__tuRow;
    if (!row || row.parentNode === el) return;
    row.classList.remove('tu-picked-detached');
    el.insertBefore(row, el.__tuRowNext && el.__tuRowNext.parentNode === el ? el.__tuRowNext : null);
}

/* Everything the zone and its ancestors looked like WHILE THE ZONE WAS STILL
   VISIBLE. Taken at collapse time, which is the last moment that is true: the
   tool hides the zone right after, and by then there is nothing left to
   remember. */
function snapVisibility(el){
    if (el.__tuVisSnap) return;
    var snap = [], node = el, hops = 0;
    while (node && node !== document.body && hops++ < 12) {
        snap.push({ node: node, display: node.style.display, hid: node.hidden, cls: node.className });
        node = node.parentNode;
    }
    el.__tuVisSnap = snap;
}

/* × means "I do not want this file any more", and the honest answer to that is
   the empty upload zone — but the zone is hidden by the TOOL, which has no idea
   the file is gone (most of these tools do not listen for tool-uploader:clear).
   Before this the point was moot: the × was inside the hidden zone and could
   not be pressed. Making the row reachable makes it pressable, so the primitive
   now has to finish the job, or it hands the person a page with no file, no
   zone and no way back — which is worse than the bug it fixes.
   (Caught by uploader-row-survives-a-hidden-zone.mjs case B, not by reasoning.)

   Undo exactly what was done since the snapshot, no more: the inline display,
   the hidden attribute, and classes that were not there before. */
function reopenZone(el){
    if (boxed(el) || !el.__tuVisSnap) return;
    el.__tuVisSnap.forEach(function(s){
        var n = s.node;
        if (!n.isConnected) return;
        if (n.hidden && !s.hid) n.hidden = false;
        if (n.style.display === 'none' && s.display !== 'none') n.style.display = s.display || '';
        if (n.className !== s.cls) {
            var before = String(s.cls).split(/\s+/);
            [].slice.call(n.classList).forEach(function(c){
                if (c && c !== 'tu-collapsed' && before.indexOf(c) === -1) n.classList.remove(c);
            });
        }
    });
    tuEvent('uploader_zone_reopened', { zone: el.id || '', ok: boxed(el) ? 1 : 0 });
}

function tuEvent(name, data){
    try {
        if (typeof window.__ae === 'function') {
            window.__ae('feature_use', Object.assign({ f: name }, data || {}));
        }
    } catch (_) {}
}

/* A tool can hide the zone at any moment — video-filter did it seconds later,
   from the <video>'s loadedmetadata handler — so one check after collapsing
   would miss most of them. A ResizeObserver answers exactly the question that
   matters (does this element still occupy a box), display:none included, and
   costs nothing while nothing changes. */
function watchZoneBox(el){
    if (el.__tuBoxWatch || typeof ResizeObserver !== 'function') return;
    el.__tuBoxWatch = new ResizeObserver(function(){ keepRowReachable(el); });
    try { el.__tuBoxWatch.observe(el); } catch (_) { el.__tuBoxWatch = null; }
}

/* ── перенос тегов звука: загрузить политику, когда пришёл звук ──────────────
   Сама политика — audio-source-tags.js (что описательно, откуда источник, в
   какие контейнеры можно). Здесь только «когда»: модуль нужен, как только на
   странице появился аудиофайл, и не нужен до этого. Свой обработчик события
   модуль пропустит (он ещё грузится), поэтому первый разбор запускаем явно. */
function looksLikeAudioForTags(f){
    var n = String((f && f.name) || '').toLowerCase();
    var t = String((f && f.type) || '').toLowerCase();
    return /\.(mp3|m4a|m4b|m4r|mp4|m4v|mov|flac|ogg|oga|opus|wav|aiff|aif|wma)$/.test(n) ||
        t.indexOf('audio/') === 0 || t === 'video/mp4' || t === 'video/quicktime';
}
var __astLoading = false;
function ensureAudioSourceTags(){
    if (window.__audioSourceTags) { try { window.__audioSourceTags.noteDelivered(); } catch (_) {} return; }
    if (__astLoading) return;
    __astLoading = true;
    var rel = '/js/audio-source-tags.js';
    var s = document.createElement('script');
    s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc(rel) : rel;
    s.onload = function(){ try { window.__audioSourceTags.noteDelivered(); } catch (_) {} };
    s.onerror = function(){ __astLoading = false; };
    document.head.appendChild(s);
}

/* ── Office lane: an OLD office file on a zone that reads its modern twin ──────
   `legacy-office="upgrade"` on the component (data-legacy-office + the families
   the zone's accept takes as a twin, data-legacy-families). A .doc/.ppt/.xls/
   .odt/.pages… dropped here is not this tool's format and never was — the
   browser cannot read it. public/js/office-lane.js decides by the BYTES:
   legacy → the door under the zone (Premium converts on our server and the
   twin comes back into THIS zone through __uploaderInjectFile, as if picked;
   everyone else reads what we do, why it is part of the plan, and how to do it
   without us); a modern file wearing an old name → delivered under its real
   extension; anything else → the zone's ordinary refusal, as before the
   attribute. Loaded lazily: the script costs nothing until such a file comes. */
var __olLoading = null;
function ensureOfficeLane(){
    if (window.OfficeLane) return Promise.resolve(window.OfficeLane);
    if (__olLoading) return __olLoading;
    __olLoading = new Promise(function(res){
        var rel = '/js/office-lane.js';
        var s = document.createElement('script');
        var done = false;
        var fin = function(v){ if (!done) { done = true; res(v); } };
        s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc(rel) : rel;
        s.onload = function(){ fin(window.OfficeLane || null); };
        s.onerror = function(){ __olLoading = null; fin(null); };
        setTimeout(function(){ if (!done) { __olLoading = null; fin(window.OfficeLane || null); } }, 15000);
        document.head.appendChild(s);
    });
    return __olLoading;
}
var OFFICE_LEGACY_EXT = {
    word: ['doc', 'dot', 'wps', 'wpd', 'odt', 'ott', 'pages'],
    slides: ['ppt', 'pps', 'pot', 'odp', 'otp', 'key'],
    sheets: ['xls', 'xlt', 'ods', 'ots', 'numbers']
};
var OFFICE_TWIN_MIME = {
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};
function officeFamilies(el){
    return String((el.dataset && el.dataset.legacyFamilies) || '').split(',').filter(Boolean);
}
function officeExt(name){
    var m = /\.([A-Za-z0-9]{1,8})$/.exec(String(name || ''));
    return m ? m[1].toLowerCase() : '';   // nameExt() stops at five letters: «numbers»
}
function officeLegacyByName(el, file){
    var ext = officeExt(file && file.name);
    if (!ext) return false;
    // Only what the lane ADDED to the picker (the component lists it): an old format
    // the zone's own accept names is the tool's to read — /text-to-handwriting reads
    // .doc in the browser, and the door offered 27 people a server conversion for it.
    var added = el.dataset && el.dataset.legacyExts;
    if (typeof added === 'string') return added.split(',').indexOf(ext) >= 0;
    return officeFamilies(el).some(function(f){ return (OFFICE_LEGACY_EXT[f] || []).indexOf(ext) >= 0; });
}
function closeOfficeDoor(el){
    if (el.__officeDoor) { try { el.__officeDoor.destroy(); } catch (_) {} el.__officeDoor = null; }
    if (el.__officeDoorHost && el.__officeDoorHost.parentNode) el.__officeDoorHost.parentNode.removeChild(el.__officeDoorHost);
    el.__officeDoorHost = null;
}
/* deliver(file) — hand a file to the zone's normal pipeline; refuse(file) — the
   zone's own wrong-format answer. */
function officeUpgrade(el, file, deliver, refuse){
    var slug = (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
    ensureOfficeLane().then(function(OL){
        if (!OL || !OL.available()) { refuse(file); return; }
        return OL.classify(file).then(function(info){
            var fams = officeFamilies(el);
            if (info.status === 'modern' && !info.encrypted && OFFICE_TWIN_MIME[info.format]) {
                // A DOCX named .doc: the bytes are already what the tool reads.
                var base = String(file.name || 'document').replace(/\.[A-Za-z0-9]{1,8}$/, '');
                var fixed;
                try { fixed = new File([file], base + '.' + info.format, { type: OFFICE_TWIN_MIME[info.format], lastModified: file.lastModified }); }
                catch (_) { fixed = file; }
                deliver(fixed);
                return;
            }
            if (info.encrypted && (info.status === 'legacy' || info.status === 'modern')) {
                // Nobody can convert a file only its password opens — say what to do, in the zone.
                closeOfficeDoor(el);
                showReject(el, OL.text('err_encrypted'), true);
                try { window.__ae && window.__ae('feature_use', { slug: slug || null, f: 'office_lane_door', fam: info.family || '', ext: info.format || '', pro: 'enc', mode: 'upgrade' }); } catch (_) {}
                return;
            }
            if (info.status !== 'legacy' || fams.indexOf(info.family) < 0) {
                refuse(file);
                return;
            }
            hideReject(el);
            closeOfficeDoor(el);
            var host = document.createElement('div');
            host.className = 'tu-office-door';
            el.insertAdjacentElement('afterend', host);
            el.__officeDoorHost = host;
            el.__officeDoor = OL.door(host, {
                file: file, info: info, tool: slug, mode: 'upgrade',
                onResult: function(out, target){
                    closeOfficeDoor(el);
                    if (typeof el.__uploaderInjectFile === 'function') el.__uploaderInjectFile(out, 'office_upgrade');
                    try {
                        if (typeof window.__toast === 'function') window.__toast(OL.text('delivered', { fmt: String(target || '').toUpperCase() }), 'success');
                    } catch (_) {}
                }
            });
        });
    }).catch(function(){ refuse(file); });
}

function collapseZone(el, list){
    if (el.dataset.collapse !== '1') return;
    var row = pickedRow(el);
    if (!row || !list || !list.length) return;

    var nameEl = row.querySelector('.tu-picked-name');
    var sizeEl = row.querySelector('.tu-picked-size');
    var total = 0;
    for (var i = 0; i < list.length; i++) total += (list[i].size || 0);

    if (nameEl) {
        // textContent, never innerHTML — a filename is user-controlled input and
        // "<img onerror=…>.mp3" is a perfectly legal name on every OS.
        nameEl.textContent = (list[0].name || '') + (list.length > 1 ? ' +' + (list.length - 1) : '');
        nameEl.title = list.length > 1
            ? Array.prototype.map.call(list, function(f){ return f.name; }).join(', ')
            : (list[0].name || '');
    }
    if (sizeEl) sizeEl.textContent = fmtSize(total);
    // Audio+video zones render both icons; the picked file says which is true.
    // MIME first — an .mp4 of type audio/mp4 is sound — the extension only when
    // the browser gave no type (Android pickers often do not).
    var ics = row.querySelectorAll('[data-tu-ic]');
    if (ics.length > 1) {
        var f0 = list[0] || {};
        var ft = String(f0.type || '').toLowerCase();
        var isVid = ft ? ft.indexOf('video/') === 0
            : /\.(mp4|mov|mkv|avi|webm|flv|wmv|m4v|3gp|ts|mts|m2ts)$/i.test(f0.name || '');
        var isImg = ft ? ft.indexOf('image/') === 0
            : /\.(jpe?g|png|gif|webp|bmp|tiff?|avif|heic|heif)$/i.test(f0.name || '');
        var want = isImg && row.querySelector('[data-tu-ic="image"]') ? 'image' : (isVid ? 'video' : 'audio');
        for (var k = 0; k < ics.length; k++) {
            ics[k].hidden = ics[k].getAttribute('data-tu-ic') !== want;
        }
    }

    row.hidden = false;
    el.classList.add('tu-collapsed');
    // role=button + tabindex on the zone made sense while the zone WAS the
    // button. Collapsed it is a status row whose controls carry their own
    // semantics, so the outer role goes away instead of announcing a button
    // that no longer does anything.
    if (!el.__tuRoleBefore) el.__tuRoleBefore = el.getAttribute('role') || '';
    el.removeAttribute('role');
    el.setAttribute('tabindex', '-1');

    snapVisibility(el);
    watchZoneBox(el);
    // The tool's own «hide the zone» usually runs in its handler for this same
    // file, i.e. after us in the same task — measure on the next frame, and
    // again once decoding has had a moment (video-filter hides it from
    // loadedmetadata). The observer above covers everything later than that.
    requestAnimationFrame(function(){ keepRowReachable(el); });
    setTimeout(function(){ keepRowReachable(el); }, 1200);
}

// Put the zone back into its "waiting for a file" state. `silent` suppresses the
// tool-uploader:clear event — used when the user is REPLACING a file (a new pick
// is already inbound), so tools don't tear their workspace down and rebuild it.
function restoreZone(el, silent){
    var row = pickedRow(el);
    // Back inside the zone first: the DOM must return to the shape the tool
    // (and its CSS) was written against, whether or not it was ever re-homed.
    homeRow(el);
    // …and the zone has to be on screen to receive the next file — see
    // reopenZone(): a tool that hid it is not going to un-hide it for us.
    reopenZone(el);
    if (row) row.hidden = true;
    el.classList.remove('tu-collapsed');
    if (el.__tuRoleBefore) el.setAttribute('role', el.__tuRoleBefore);
    el.setAttribute('tabindex', '0');
    hideReject(el);
    el.__pendingFile = null;
    el.__pendingTs = 0;
    forgetPick(el);
    el.__deliveredFiles = null;      /* файла на странице больше нет — и памяти о нём тоже */
    if (!silent) {
        // Tools listen for this to reset their workspace. A tool that does NOT
        // listen simply keeps its last result on screen while the zone reopens —
        // exactly what every tool did before this row existed, so ignoring the
        // event is never worse than the old behaviour.
        el.dispatchEvent(new CustomEvent('tool-uploader:clear', { bubbles: true }));
    }
}

// Feedback-widget context for EVERY uploader-layer reject. Historically each
// tool's own handleFile recorded __LAST_FILE/__LAST_TOOL_ERROR before its
// gates, so bug reports carried what the user tried. After tools migrated to
// the shared uploader, the component's gates fire FIRST — a reject here meant
// the tool never saw the file and feedback arrived with last_file/last_error
// null (the exact diagnostic that cracked the 2026-07-02 /ru/join "1697"
// report). Record centrally so all reject paths stay uniform; a tool-layer
// reject later simply overwrites with more specific data (last-write-wins).
function recordRejectContext(file, reason, msg){
    try {
        var slug = (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
        var ext = ((file && file.name) || '').split('.').pop().toLowerCase();
        window.__LAST_FILE = { name: (file && file.name) || '', size: (file && file.size) || 0, type: (file && file.type) || '', ext: ext, src: 'picker', ts: Date.now() };
        // Сам File, а не его паспорт: отпечаток (public/js/file-fingerprint.js)
        // разбирает СТРУКТУРУ файла, и без ссылки на объект жалоба «видео не
        // загружается» приходит с теми же тремя полями, что и текст жалобы.
        window.__LAST_FILE_REF = file || null;
        window.__LAST_TOOL_ERROR = { tool: slug, stage: 'pick_reject', reason: reason, msg: String(msg).replace(/<[^>]+>/g, '').slice(0, 200), fileSize: (file && file.size) || 0, fileExt: ext, fileType: (file && file.type) || '', ts: Date.now() };
    } catch (_) {}
}

/* The zone's verdict travels WITH the file (a non-enumerable property on the
   File object) rather than in a registry keyed by name/size: the same File
   reference is what the tool receives in tool-uploader:file, and nothing else
   on the page can mistake a different file for it. The layout's
   __validateFileSize reads it; FileBytes.settle() carries it onto the durable
   File it hands back. */
/* Per FILE, not per batch: on a zone with `data-kind-video` a clip and a photo
   in the same pick were judged by different numbers, and each file carries
   the verdict that was actually applied to it. */
function stampAccepted(list, el, maxMb, capFor){
    var zoneKind = (el.dataset && el.dataset.kind) || '';
    var kindVideo = (el.dataset && el.dataset.kindVideo) || '';
    var kindAudio = (el.dataset && el.dataset.kindAudio) || '';
    for (var i = 0; i < list.length; i++) {
        var f = list[i];
        var cap = capFor ? capFor(f) : maxMb;
        if (!(cap > 0)) continue;
        var gate = { kind: (kindVideo && isVideoLike(f)) ? kindVideo : ((kindAudio && isAudioLike(f)) ? kindAudio : zoneKind), maxMb: cap, zone: el.id || '' };
        try { Object.defineProperty(f, '__tuGate', { value: gate, configurable: true }); } catch(_){}
    }
}

/* Video by type or by extension — phone files often carry no MIME. Same
   reading file-buffer-cache.js uses for its own video cap. */
var VIDEO_LIKE_EXT_RE = /\.(mp4|m4v|mov|webm|mkv|avi|wmv|flv|3gp|3gpp|ts|mts|m2ts|mpg|mpeg|ogv)$/i;
function isVideoLike(file){
    var t = String((file && file.type) || '').toLowerCase();
    if (t.indexOf('video/') === 0) return true;
    if (t) return false;
    return VIDEO_LIKE_EXT_RE.test(String((file && file.name) || ''));
}
/* Sound by type or by extension — the same reading, for a zone that also
   names an audio kind (data-kind-audio, /video-editor's music and voice). */
var AUDIO_LIKE_EXT_RE = /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac|weba|aif|aiff|amr|wma|caf)$/i;
function isAudioLike(file){
    var t = String((file && file.type) || '').toLowerCase();
    if (t.indexOf('audio/') === 0) return true;
    if (t) return false;
    return AUDIO_LIKE_EXT_RE.test(String((file && file.name) || ''));
}

function rejectTooLarge(el, file, maxMb, partial){
    var locale = (window.__LOCALE || 'en').substring(0, 2);
    var label = (REJECT_L[locale] || REJECT_L.en)(fmtSize(file.size), maxMb);
    // no-SIMD device: the cap came from the compatibility-engine clamp — tell
    // the user the RIGHT next step (update browser vs honest device limit).
    if (window.__noSimd && typeof window.__noSimdAdvice === 'function') {
        var _adv = window.__noSimdAdvice();
        if (_adv) label += ' ' + _adv;
    }
    showReject(el, label);
    recordRejectContext(file, 'file_too_large', label);
    // Typed error path — still route to tool_blocked so telemetry has reason=FILE_TOO_LARGE.
    if (typeof window.__reject === 'function' && window.ToolErrors && window.ToolErrors.REASONS) {
        window.__reject(label, window.ToolErrors.REASONS.FILE_TOO_LARGE, file);
    }
    // Symmetric tool-uploader:reject event so host tools can layer a
    // contextual CTA (e.g. "split your file with /audio-splitter first").
    // Previously file_too_large was the only reject path that DIDN'T fire
    // the event — asymmetric with rejectWrongType / rejectEmpty. Audit
    // wf_9d2ce135 finding #23. Backward compatible: existing listeners
    // already filter by detail.reason / detail.gotKind, so the new branch
    // is opt-in for any tool that wants to handle it.
    try {
        el.dispatchEvent(new CustomEvent('tool-uploader:reject', {
            detail: { reason: 'file_too_large', file: file, maxMb: maxMb, msg: label, partial: !!partial },
            bubbles: false
        }));
    } catch (_) {}
}
function rejectEmpty(el, file, kind){
    var locale = (window.__LOCALE || 'en').substring(0, 2);
    var label = (typeof window.__emptyFileMsg === 'function' && window.__emptyFileMsg(kind))
        || REJECT_EMPTY_L[locale] || REJECT_EMPTY_L.en;
    showReject(el, label);
    recordRejectContext(file, 'empty_file', label);
    if (typeof window.__reject === 'function' && window.ToolErrors && window.ToolErrors.REASONS) {
        window.__reject(label, window.ToolErrors.REASONS.EMPTY_FILE || 'empty_file', file);
    }
    try {
        el.dispatchEvent(new CustomEvent('tool-uploader:reject', {
            detail: { reason: 'empty_file', file: file, msg: label },
            bubbles: false
        }));
    } catch (_) {}
}
// Offline fallback for the unreadable-file verdict. Canonical wording lives in
// __unreadableFileMsg (core) so there is ONE copy to translate; this dict is
// what shows if timbrica-core.js was blocked (ad-blocker / CDN double-miss) —
// the same shape REJECT_EMPTY_L uses for __emptyFileMsg.
var REJECT_UNREADABLE_L = {
    read_error: {
        en: 'This file could not be read. If it is stored in the cloud (Google Photos, Google Drive, iCloud, OneDrive), download it to this device first, then choose it again.',
        ru: 'Файл не удалось прочитать. Если он хранится в облаке (Google Фото, Google Диск, Яндекс Диск, iCloud, OneDrive) — сначала скачайте его на это устройство, затем выберите заново.',
        ko: '이 파일을 읽을 수 없습니다. 클라우드(Google 포토, Google 드라이브, iCloud, OneDrive)에 있다면 먼저 이 기기에 다운로드한 뒤 다시 선택하세요.',
        ar: 'تعذّرت قراءة هذا الملف. إذا كان مخزّنًا في السحابة (صور Google أو Google Drive أو iCloud أو OneDrive) فنزّله إلى هذا الجهاز أولًا ثم اختره مجددًا.',
        id: 'File ini tidak dapat dibaca. Jika tersimpan di cloud (Google Photos, Google Drive, iCloud, OneDrive), unduh dulu ke perangkat ini lalu pilih lagi.',
        pt: 'Não foi possível ler este arquivo. Se ele estiver na nuvem (Google Fotos, Google Drive, iCloud, OneDrive), baixe-o para este dispositivo e selecione novamente.',
        de: 'Diese Datei konnte nicht gelesen werden. Liegt sie in der Cloud (Google Fotos, Google Drive, iCloud, OneDrive), laden Sie sie zuerst auf dieses Gerät herunter und wählen Sie sie erneut.',
        es: 'No se pudo leer este archivo. Si está en la nube (Google Fotos, Google Drive, iCloud, OneDrive), descárgalo primero a este dispositivo y vuelve a seleccionarlo.',
        fr: 'Impossible de lire ce fichier. S’il est stocké dans le cloud (Google Photos, Google Drive, iCloud, OneDrive), téléchargez-le d’abord sur cet appareil puis re-sélectionnez-le.',
        uk: 'Не вдалося прочитати цей файл. Якщо він зберігається в хмарі (Google Фото, Google Диск, iCloud, OneDrive), спершу завантажте його на цей пристрій — або виберіть інший файл.'
    },
    not_found: {
        en: 'The file moved, was renamed or was deleted after you picked it. Pick it again.',
        ru: 'Файл переместили, переименовали или удалили после выбора. Выберите его заново.',
        ko: '선택한 뒤 파일이 이동·이름 변경·삭제되었습니다. 다시 선택하세요.',
        ar: 'تم نقل الملف أو إعادة تسميته أو حذفه بعد اختياره. اختره مجددًا.',
        id: 'File dipindahkan, diganti nama, atau dihapus setelah Anda memilihnya. Pilih lagi.',
        pt: 'O arquivo foi movido, renomeado ou excluído depois de você selecioná-lo. Selecione novamente.',
        de: 'Die Datei wurde nach der Auswahl verschoben, umbenannt oder gelöscht. Wählen Sie sie erneut.',
        es: 'El archivo se movió, se renombró o se eliminó después de elegirlo. Vuelve a elegirlo.',
        fr: 'Le fichier a été déplacé, renommé ou supprimé après la sélection. Re-sélectionnez-le.',
        uk: 'Файл перемістили, перейменували або видалили після вибору. Виберіть його ще раз.'
    },
    // The browser stopped reading, not the file (FileBytes evidence — chat 714).
    browser: {
        en: 'The browser could not read this file. This usually means the browser itself has stalled, not the file. Quit the browser completely (on a Mac: ⌘Q) and open this page again — or open it in another browser.',
        ru: 'Браузер не смог прочитать файл — обычно так бывает, когда сбоит сам браузер, а не файл. Закройте браузер полностью (на Mac — ⌘Q) и откройте страницу снова — или откройте её в другом браузере.',
        ko: '브라우저가 이 파일을 읽지 못했습니다. 보통 파일이 아니라 브라우저 자체의 문제입니다. 브라우저를 완전히 종료한 뒤(Mac: ⌘Q) 이 페이지를 다시 열거나 다른 브라우저에서 여세요.',
        ar: 'تعذّر على المتصفح قراءة هذا الملف. عادةً تكون المشكلة في المتصفح نفسه لا في الملف. أغلق المتصفح تمامًا (على Mac: ⌘Q) ثم افتح هذه الصفحة من جديد — أو افتحها في متصفح آخر.',
        id: 'Browser tidak dapat membaca file ini. Biasanya yang bermasalah adalah browsernya, bukan file-nya. Tutup browser sepenuhnya (di Mac: ⌘Q) lalu buka halaman ini lagi — atau buka di browser lain.',
        pt: 'O navegador não conseguiu ler este arquivo. Normalmente o problema é do próprio navegador, não do arquivo. Feche o navegador por completo (no Mac: ⌘Q) e abra esta página de novo — ou abra-a em outro navegador.',
        de: 'Der Browser konnte diese Datei nicht lesen. Meist liegt das am Browser selbst, nicht an der Datei. Beenden Sie den Browser vollständig (auf dem Mac: ⌘Q) und öffnen Sie diese Seite erneut — oder öffnen Sie sie in einem anderen Browser.',
        es: 'El navegador no pudo leer este archivo. Normalmente la falla es del navegador, no del archivo. Cierra el navegador por completo (en Mac: ⌘Q) y vuelve a abrir esta página, o ábrela en otro navegador.',
        fr: 'Le navigateur n’a pas pu lire ce fichier. En général, c’est le navigateur qui est bloqué, pas le fichier. Quittez complètement le navigateur (sur Mac : ⌘Q) puis rouvrez cette page — ou ouvrez-la dans un autre navigateur.',
        uk: 'Браузер не зміг прочитати файл — зазвичай так буває, коли збоїть сам браузер, а не файл. Закрийте браузер повністю (на Mac — ⌘Q) і відкрийте сторінку знову — або відкрийте її в іншому браузері.'
    },
};

// The tool got there first and guessed. Retire the guess.
//
// The tool's own decode fails at the same instant the first prime read does -
// in the #1492 trace the toast and the prime failure are in the SAME telemetry
// batch - but the ladder needs ~1 s before it can be sure, and an error toast
// lives 6 s. So the two sit side by side for ~5 s saying opposite things:
// "try a different file" (wrong, and he did) against "download it to this
// device first" (right). Only toasts raised since THIS pick are touched, so an
// unrelated warning already on screen is left alone.
function retireGuessToasts(el){
    var since = el.__tuPickAt || 0;
    if (!since) return;
    try {
        var nodes = document.querySelectorAll('.toast-msg--error[data-ts-ms]');
        for (var i = 0; i < nodes.length; i++) {
            var t = nodes[i];
            var ts = parseInt((t.dataset && t.dataset.tsMs) || '0', 10);
            if (!ts || ts < since) continue;
            var x = t.querySelector('.toast-close');
            if (x && typeof x.onclick === 'function') x.onclick();   // its own dismiss: clears the timer, animates out
        }
    } catch (_) {}
}

// The bytes of an ACCEPTED file turned out to be unreachable. Everything the
// other reject paths do, plus two things only this one needs:
//   1. restoreZone() — the file was already accepted, so the zone had collapsed
//      into the "picked" row. Leaving it collapsed would show the user a file
//      that is loaded and a banner saying it is not, with no way to pick again.
//      restoreZone also fires tool-uploader:clear, which is how a tool tears
//      down the half-built workspace it started on the dispatch a second ago.
//      SINGLE-file zones only — see below.
//   2. tool-uploader:file-unreadable — a tool that wants to say something
//      contextual can, but nothing has to listen for the user to be told.
function rejectUnreadable(el, file, kind, err){
    // The verdict below IS the sentence for this error: a tool that catches the
    // same error from its own read (FileBytes.whenUnreadable hands everyone the
    // one object) must not toast it again (__handleError honours the mark).
    try { if (err && typeof err === 'object') err.__userNotified = true; } catch (_) {}
    if (el.__tuUnreadableShown === file) return;   // one verdict per file
    el.__tuUnreadableShown = file;
    var locale = (window.__LOCALE || 'en').substring(0, 2);
    var table = REJECT_UNREADABLE_L[kind] || REJECT_UNREADABLE_L.read_error;
    var label = (typeof window.__unreadableFileMsg === 'function' && window.__unreadableFileMsg(kind))
        || table[locale] || table.en;
    // On a multi-file zone the other files are fine and the user picked them on
    // purpose. Tearing the batch down over one dead item is the mistake
    // rejectWrongType/rejectEmpty already learned not to make (they partition and
    // fire tool-uploader:partial-reject) - a nine-track pick with one cloud photo
    // in it must not cost the user the other eight. Name the file in the banner
    // instead, and let the tool drop just that one off detail.file.
    var multi = el.dataset && el.dataset.multiple === '1';
    if (multi) {
        var name = (file && file.name) || '';
        if (name) label += ' — ' + name;
        hideReject(el);
    } else {
        restoreZone(el);      // reopen the picker BEFORE the banner...
    }
    showReject(el, label);    // ...because restoreZone() calls hideReject()
    retireGuessToasts(el);
    recordRejectContext(file, 'file_lost', label);
    if (typeof window.__reject === 'function' && window.ToolErrors && window.ToolErrors.REASONS) {
        window.__reject(label, window.ToolErrors.REASONS.FILE_LOST, file);
    }
    try {
        if (typeof window.__ae === 'function') window.__ae('feature_use', {
            slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
            f: 'file_unreadable_surfaced',
            kind: kind,
            why: (err && err.browserWhy) || undefined,
            errName: (err && err.fileLostName) || (err && err.name) || '',
            ext: ((file && file.name) || '').split('.').pop().toLowerCase().slice(0, 8),
            fileMB: Math.round(((file && file.size) || 0) / 1048576),
        });
    } catch (_) {}
    try {
        el.dispatchEvent(new CustomEvent('tool-uploader:file-unreadable', {
            detail: { reason: 'file_lost', kind: kind, file: file, msg: label, error: err },
            bubbles: true,
        }));
    } catch (_) {}
}

// A dropped FOLDER is not an empty file, and saying so is the whole fix: it
// arrives in dataTransfer.files as a 0-byte File carrying the folder's name,
// so before this it fell into rejectEmpty and the person was advised to
// «download it from the cloud». Kind comes from what the walk actually found,
// so the sentence is never a guess.
function rejectFolder(el, kind, n, names){
    var label = (window.__folderDrop && window.__folderDrop.msg(kind, n)) || 'That is a folder, not a file.';
    showReject(el, label);
    recordRejectContext({ name: (names && names[0]) || 'folder', size: 0, type: '' }, 'folder_drop', label);
    if (typeof window.__reject === 'function' && window.ToolErrors && window.ToolErrors.REASONS) {
        window.__reject(label, window.ToolErrors.REASONS.INVALID_INPUT || 'invalid_input');
    }
    try {
        el.dispatchEvent(new CustomEvent('tool-uploader:reject', {
            detail: { reason: 'folder_drop', kind: kind, count: n, msg: label },
            bubbles: false
        }));
    } catch (_) {}
}
function rejectMidi(el, file){
    var locale = (window.__LOCALE || 'en').substring(0, 2);
    var html = REJECT_MIDI_L[locale] || REJECT_MIDI_L.en;
    showRejectHtml(el, html);
    recordRejectContext(file, 'wrong_format', String(html).replace(/<[^>]+>/g, ''));
    if (typeof window.__reject === 'function' && window.ToolErrors && window.ToolErrors.REASONS) {
        // Telemetry as a plain string; reuse WRONG_FORMAT reason since there's
        // no dedicated MIDI bucket — the message + reach to /midi is what
        // distinguishes the event in pino.
        var plain = String(html).replace(/<[^>]+>/g, '');
        window.__reject(plain, window.ToolErrors.REASONS.WRONG_FORMAT || 'wrong_format', file);
    }
    try {
        el.dispatchEvent(new CustomEvent('tool-uploader:reject', {
            detail: { reason: 'midi_unsupported', file: file, msg: html },
            bubbles: false
        }));
    } catch (_) {}
}
// ── Video signposting ────────────────────────────────────────────────
// A zone that takes SOUND only answers a video with the way to the tool that
// pulls the sound out — and carries the file there (tool-handoff.js carry()),
// so the person does not pick it a second time. 90 days to 12.10.2026: about
// 790 sessions brought a video to a sound-only zone (vocal-remover 271,
// voice-enhancer 104, audio-to-notes 79 …) and read «this tool needs an audio
// file». Report of 05.10.2026 (iPhone, audiobook narrator, Premium bought that
// day): /acx-mastering answered two .MOV takes with «No audio files found».
//
// A video reaches a sound-only tool by two doors, and both end here:
//   • the zone turns it away itself (not in `accept`) — rejectWrongType;
//   • on an iPhone the picker's accept is widened with .mp4/.mov (see wire()),
//     the zone lets the file through and the TOOL's own type check refuses it —
//     the tool calls zone.__uploaderVideoSignpost(files) instead of a toast.
// The target tool, its title and its address come from the component
// (data-video-to-*): a title typed here would drift from the tool's own, and
// the address has to carry the page's locale.
function videoSignpostReady(el){
    return !!(el && el.dataset && el.dataset.videoToUrl && el.dataset.videoMsg);
}
// Which of these files are videos this zone's tool did not ask for. Judged by
// the accept the BLADE declared, not the one widened for the iPhone picker:
// the widening exists so the picker does not grey a file out, it says nothing
// about what the tool can use. A format the blade named itself is never ours.
function videosForSignpost(files, declaredAccept){
    var out = [];
    for (var i = 0; files && i < files.length; i++) {
        var f = files[i];
        if (!f || givenKindBucket(f) !== 'video') continue;
        if (declaredAccept && matchesAccept(f, declaredAccept)) continue;
        out.push(f);
    }
    return out;
}
function rejectVideo(el, videos, via, partial){
    var d = el.dataset, first = videos[0];
    var msg = d.videoMsg, title = d.videoToTitle || '';
    if (videos.length > 1) {
        var names = [];
        for (var i = 0; i < videos.length && i < 4; i++) names.push(videos[i].name || '?');
        msg += ' — ' + names.join(', ') + (videos.length > 4 ? ' (+' + (videos.length - 4) + ')' : '');
    }
    var plain = msg + ' ' + title;
    // A tool that refused a file the zone had already taken leaves a single-file
    // zone folded into its «file · Another file · ×» row for a file that is not
    // going to be used. Reopen it first: restoreZone() hides the banner.
    if (via === 'tool' && !partial && !(d.multiple === '1')) restoreZone(el);
    showReject(el, plain);      // banner on screen, shake, screen-reader line
    // One file rides along; several would need the target's batch, and the
    // carrier holds one — then the link is a plain link and says nothing more.
    var carries = false;
    var msgEl = el.querySelector('.tu-reject .tu-reject-msg');
    if (msgEl) {
        msgEl.textContent = msg;
        var link = document.createElement('a');
        link.className = 'tu-reject-link';
        link.href = d.videoToUrl;
        link.setAttribute('data-slug', d.videoTo || '');
        link.textContent = title;
        msgEl.appendChild(link);
        // The carrier (tool-handoff.js) is a deferred script: a file picked in
        // the page's first second gets here before it. Without the wait such a
        // link stayed a plain link for good — the person arrived at an empty
        // tool. The promise «the file goes along» is printed only once the
        // carrier is really on the link.
        var armCarry = function(tries){
            if (!link.isConnected) return false;
            var H = window.__toolHandoff;
            if (!H || typeof H.carry !== 'function') {
                if (tries > 0) setTimeout(function(){ armCarry(tries - 1); }, 250);
                return false;
            }
            if (typeof H.available === 'function' && !H.available()) return false;
            H.carry(link, function(){ return { blob: first, filename: first.name || 'video' }; });
            if (d.videoNote) {
                var note = document.createElement('span');
                note.className = 'tu-reject-note';
                note.textContent = d.videoNote;
                msgEl.appendChild(note);
            }
            return true;
        };
        if (videos.length === 1) carries = armCarry(60);
    }
    recordRejectContext(first, 'wrong_format', plain);
    // The banner IS the message: a toast with the same words lands on top of
    // its button on a phone (seen on the stand, 375 px). The event is still
    // recorded as a blocked file. The toast stays for the pick the tool goes
    // on with — it folds the zone away, and the banner with it.
    if (typeof window.__toast === 'function') {
        var why = (window.ToolErrors && window.ToolErrors.REASONS && window.ToolErrors.REASONS.WRONG_FORMAT) || 'wrong_format';
        var told = !partial && boxed(el.querySelector('.tu-reject'));
        window.__toast(plain, 'error', told ? { reason: why, file: first, noUi: 1, ui: 'told' } : { reason: why, file: first });
    }
    try {
        if (typeof window.__ae === 'function') window.__ae('feature_use', {
            slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
            f: 'video_signposted', to: d.videoTo || '', via: via, n: videos.length, carry: carries ? 1 : 0,
            ext: (first.name || '').split('.').pop().toLowerCase().slice(0, 8),
            mime: (first.type || '').slice(0, 40),
            fileMB: Math.round((first.size || 0) / 1048576),
        });
    } catch (_) {}
    try {
        el.dispatchEvent(new CustomEvent('tool-uploader:reject', {
            // msg is the sentence alone: a tool that holds the reason on its own
            // line (/music-mastering) prints it, and the tool's title is not
            // part of a sentence. Where the way leads is in `to`.
            detail: { reason: 'video_unsupported', file: first, files: videos, gotKind: 'video', partial: !!partial, msg: msg,
                      to: { slug: d.videoTo || '', url: d.videoToUrl, title: title } },
            bubbles: false
        }));
    } catch (_) {}
}
function _classifyFileKind(file){
    if (!file) return 'unknown';
    var t = (file.type || '').toLowerCase();
    if (t.indexOf('image/') === 0) return 'image';
    if (t.indexOf('video/') === 0) return 'video';
    if (t.indexOf('audio/') === 0) return 'audio';
    if (t === 'application/pdf') return 'pdf';
    if (t.indexOf('text/') === 0) return 'text';
    if (/zip|rar|7z|tar|gzip|x-compressed/.test(t)) return 'archive';
    return 'unknown';
}
function rejectWrongType(el, fileOrFiles, keepPartial){
    // Backward-compat: single-file (the original signature) AND array of files
    // (new — used by the partition path so all rejected names appear in one
    // banner instead of just the first one, audit wf_9d2ce135 #27).
    var arr = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    // Nothing but videos on a zone that knows where a video's sound is pulled
    // out: say that, with the way there, instead of «needs an audio file».
    if (videoSignpostReady(el) && arr.length && videosForSignpost(arr, el.dataset.accept || '').length === arr.length) {
        rejectVideo(el, arr, 'zone', keepPartial);
        return;
    }
    var first = arr[0];
    var msg = buildWrongFormatMsg(el, first, arr.length > 1 ? arr : null);
    showReject(el, msg);
    recordRejectContext(first, 'wrong_format', msg);
    if (typeof window.__reject === 'function' && window.ToolErrors && window.ToolErrors.REASONS) {
        window.__reject(msg, window.ToolErrors.REASONS.WRONG_FORMAT || 'wrong_format', first);
    }
    // Tools listen for this to bolt on context-specific CTAs (e.g.
    // voice-changer pushing image-rejecting users toward the Microphone tab).
    // detail.gotKind is the FIRST rejected file's kind (image / video /
    // audio / pdf / text / archive / generic / unknown). For full visibility
    // on partial-reject the host should listen to tool-uploader:partial-reject
    // (fires alongside this one, contains the full rejected array).
    try {
        el.dispatchEvent(new CustomEvent('tool-uploader:reject', {
            detail: {
                reason: 'wrong_format',
                file: first,
                files: arr,
                gotKind: _classifyFileKind(first),
                partial: !!keepPartial,
                msg: msg,
            },
            bubbles: false
        }));
    } catch (_) {}
}
// Extension fallback per wildcard category — used when file.type is empty
// (Windows + YandexBrowser drag-and-drop often drops the MIME hint, and some
// archived/network-streamed files have no MIME). Matches the kinds we accept
// across the project so a "video/*" wildcard still admits .mov / .mkv / .3gp
// when MIME is missing. Caught 2026-05-09 from a YandexBrowser report
// "видео скидывается" — `last_file:null` because rejectWrongType fired silently.
var WILDCARD_EXT_FALLBACK = {
    'video/': /\.(mp4|mov|webm|mkv|avi|wmv|flv|3gp|m4v|ts|mpg|mpeg|ogv|m2ts|mts|vob)$/i,
    'audio/': /\.(mp3|wav|ogg|oga|m4a|aac|flac|opus|amr|aiff|aif|wma|mp2|mpga|alac|caf|au|spx|ra|ac3|dts|tta|wv)$/i,
    'image/': /\.(jpg|jpeg|png|webp|gif|bmp|heic|heif|avif|tiff|tif|svg)$/i,
};

function matchesAccept(file, accept){
    if (!accept || accept === '*/*') return true;
    var tokens = accept.split(',').map(function(t){ return t.trim().toLowerCase(); });
    var name = (file.name || '').toLowerCase();
    var type = (file.type || '').toLowerCase();
    for (var i = 0; i < tokens.length; i++) {
        var t = tokens[i];
        if (!t) continue;
        // «Any file», spelled the way a blade spells it. The whole-string `*/*`
        // above was the only form understood, so a zone declared `accept="*"`
        // turned every file away as the wrong format — /corrupted-file-generator
        // took nothing from its publication (21.05.2026) to 07.10.2026.
        if (t === '*' || t === '*/*') return true;
        if (t.charAt(0) === '.') {
            if (name.endsWith(t)) return true;
        } else if (t.endsWith('/*')) {
            var prefix = t.slice(0, -1); // 'video/*' → 'video/'
            if (type && type.indexOf(prefix) === 0) return true;
            // file.type empty → trust the extension if it matches the wildcard kind.
            // OS picker already filtered by `accept` in the input element, so a file
            // with the right extension reaching us here was user-selected on purpose.
            if (!type && WILDCARD_EXT_FALLBACK[prefix] && WILDCARD_EXT_FALLBACK[prefix].test(name)) return true;
        } else if (type === t) {
            return true;
        }
    }
    return false;
}

// ── Content sniffing ─────────────────────────────────────────────────
// Last-resort gate when a file matches the `accept` neither by extension
// nor by MIME — e.g. a downloaded backing track whose name has no dot
// ("ANGELS — Backing Track") AND whose MIME the OS dropped. Without this
// a perfectly valid MP3 is rejected as "unsupported type" (telemetry:
// recording-studio tool_blocked, 2026-05-17). We read the first bytes and
// match magic numbers. This can only turn a reject INTO an accept — the
// tool's own decoder still validates the file for real downstream.
// classifyBytes() answers the ACCEPT question ("is this the kind of file this
// zone wants"). identifyBytes() answers the IDENTITY question ("what exactly is
// this file") and is what repairIdentity() writes back onto the File — so the
// two must never disagree. One table, two readers: classifyBytes delegates.
function classifyBytes(b){
    var id = identifyBytes(b);
    return id ? id.kind : null;
}
/* identifyBytes:begin */
function identifyBytes(b){
    if (!b || b.length < 12) return null;
    function ascii(o, n){ var s = ''; for (var i = 0; i < n; i++) s += String.fromCharCode(b[o + i]); return s; }
    function F(kind, ext, mime){ return { kind: kind, ext: ext, mime: mime }; }
    var h4 = ascii(0, 4);
    // audio
    if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) return F('audio', 'mp3', 'audio/mpeg');      // 'ID3' — MP3 w/ ID3v2
    if (b[0] === 0xFF && (b[1] & 0xE0) === 0xE0) return F('audio', 'mp3', 'audio/mpeg');              // MPEG audio / AAC-ADTS frame sync
    if (h4 === 'fLaC') return F('audio', 'flac', 'audio/flac');                                       // FLAC
    if (h4 === 'OggS') return F('audio', 'ogg', 'audio/ogg');                                         // Ogg (Vorbis/Opus)
    if (b[0] === 0x23 && b[1] === 0x21 && b[2] === 0x41 && b[3] === 0x4D && b[4] === 0x52) return F('audio', 'amr', 'audio/amr'); // '#!AMR'
    if (b[0] === 0x30 && b[1] === 0x26 && b[2] === 0xB2 && b[3] === 0x75) return F('audio', 'wma', 'audio/x-ms-wma'); // ASF/WMA
    if (h4 === 'RIFF'){ var rf = ascii(8, 4);
        if (rf === 'WAVE') return F('audio', 'wav', 'audio/wav');
        if (rf === 'AVI ') return F('video', 'avi', 'video/x-msvideo');
        if (rf === 'WEBP') return F('image', 'webp', 'image/webp');
    }
    if (h4 === 'FORM'){ var ff = ascii(8, 4); if (ff === 'AIFF' || ff === 'AIFC') return F('audio', 'aiff', 'audio/aiff'); }
    // MP4 family ('ftyp'). audio vs video can't be told from one brand —
    // an iOS .m4a often carries a generic major brand (isom/mp42) with
    // 'M4A '/'M4B ' only in the *compatible-brands* list (bytes 16+).
    // Scan the whole brand area: any M4A/M4B/M4P brand ⇒ audio (video
    // mp4s never carry it), otherwise treat as video.
    if (ascii(4, 4) === 'ftyp'){
        var brands = ascii(8, Math.min(b.length, 32) - 8).toLowerCase();
        // HEIC/HEIF/AVIF/HEIF-sequence are ALSO ftyp-boxed (ISO-BMFF). They
        // share the same container shape as MP4 but carry image-class
        // brands (heic/heix/heim/heis/mif1/msf1/avif/avis). Without this
        // check they previously fell through to 'video' — so a HEIC drag-
        // dropped with empty MIME and no .heic extension (Yandex Browser
        // sometimes strips both) was rejected with "you uploaded a video"
        // (feedback 2026-06-06, Анна's 15:54:21 tool_blocked event).
        if (/avif|avis/.test(brands)) return F('image', 'avif', 'image/avif');
        if (/heic|heix|heim|heis|mif1|msf1/.test(brands)) return F('image', 'heic', 'image/heic');
        if (/m4a|m4b|m4p/.test(brands)) return F('audio', 'm4a', 'audio/mp4');
        // 'qt  ' is QuickTime .mov — same box shape, different container.
        if (/qt  |qt$|^qt/.test(brands)) return F('video', 'mov', 'video/quicktime');
        return F('video', 'mp4', 'video/mp4');
    }
    // Matroska/WebM. One magic number, two containers — the DocType string
    // sits in the EBML header a few bytes in, so read it rather than guess:
    // an appended '.webm' on an .mkv would be a name that lies about the bytes.
    if (b[0] === 0x1A && b[1] === 0x45 && b[2] === 0xDF && b[3] === 0xA3){
        var head = ascii(4, Math.min(b.length, 80) - 4).toLowerCase();
        return head.indexOf('webm') !== -1
            ? F('video', 'webm', 'video/webm')
            : F('video', 'mkv', 'video/x-matroska');
    }
    // MPEG program stream (.mpg/.mpeg/.vob) — pack header start code.
    if (b[0] === 0x00 && b[1] === 0x00 && b[2] === 0x01 && (b[3] === 0xBA || b[3] === 0xB3)) return F('video', 'mpg', 'video/mpeg');
    // MPEG transport stream. There is no header — the format is a run of
    // fixed-size packets each starting with the 0x47 sync byte, so the only
    // honest test is "does the sync repeat at the right stride". Two strides
    // exist: 188 bytes (broadcast .ts) and 192 (BDAV/AVCHD .m2ts/.mts, which
    // prefixes every packet with a 4-byte arrival timecode).
    //
    // This matters more than it looks: Chromium reports .mts as
    // `model/vnd.mts` and .m2ts as `application/octet-stream` (measured
    // 2026-08-09) — neither is empty, so the extension fallback in
    // matchesAccept never fired and every camcorder file was turned away with
    // "Вы загрузили файл MTS" on ALL video tools, though FFmpeg reads them fine.
    if (b.length >= 377){
        if (b[0] === 0x47 && b[188] === 0x47 && b[376] === 0x47) return F('video', 'ts', 'video/mp2t');   // 188-byte TS
        if (b.length >= 389 && b[4] === 0x47 && b[196] === 0x47 && b[388] === 0x47) return F('video', 'm2ts', 'video/mp2t'); // 192-byte M2TS
    }
    // images
    if (b[0] === 0xFF && b[1] === 0xD8) return F('image', 'jpg', 'image/jpeg');                       // JPEG
    if (b[0] === 0x89 && ascii(1, 3) === 'PNG') return F('image', 'png', 'image/png');                // PNG
    if (h4 === 'GIF8') return F('image', 'gif', 'image/gif');
    if (b[0] === 0x42 && b[1] === 0x4D) return F('image', 'bmp', 'image/bmp');                        // BMP
    if (h4 === '%PDF') return F('pdf', 'pdf', 'application/pdf');
    return null;
}
/* identifyBytes:end */
function sniffMediaKind(file){
    return new Promise(function(resolve){
        try {
            var fr = new FileReader();
            fr.onload = function(){
                var id = null;
                try { id = identifyBytes(new Uint8Array(fr.result)); } catch(_){}
                // Keep the full descriptor on the file: repairIdentity() below
                // needs the FORMAT (mp3 vs flac), not just the kind, and re-
                // reading the head a second time to learn what we already read
                // is the kind of waste that turns into a second owner.
                if (id) { try { Object.defineProperty(file, '__tuSniff', { value: id, configurable: true }); } catch(_){} }
                resolve(id ? id.kind : null);
            };
            fr.onerror = function(){ resolve(null); };
            // 32 bytes cover every magic-number check; the MPEG-TS stride test
            // needs three sync bytes 192 apart, so read 389. slice() is lazy —
            // this still reads one disk block either way.
            fr.readAsArrayBuffer(file.slice(0, 389));
        } catch(_){ resolve(null); }
    });
}
// True if `accept` admits files of `kind` — by ANY of the three ways a
// tool can express it: a `kind/*` wildcard, an explicit MIME
// (`audio/mpeg`, `application/pdf`, …) or a known extension token
// (`.mp3`, `.pdf`, …). Earlier this only checked `kind/*`, which meant a
// correctly-sniffed PDF was still rejected on a PDF tool — PDF has no
// `pdf/*` wildcard, it's `application/pdf` + `.pdf` — and audio tools
// that list only extensions got no benefit either.
function acceptAdmitsKind(accept, kind){
    if (!accept || !kind) return false;
    var mimePrefix = (kind === 'pdf') ? 'application/pdf' : kind + '/';
    var extRe = (kind === 'pdf') ? /^\.pdf$/ : WILDCARD_EXT_FALLBACK[kind + '/'];
    var tokens = accept.toLowerCase().split(',');
    for (var i = 0; i < tokens.length; i++){
        var t = tokens[i].trim();
        if (!t) continue;
        if (t.charAt(0) === '.'){
            if (extRe && extRe.test(t)) return true;            // extension token (.pdf, .mp3)
        } else if (t === kind + '/*'){
            return true;                                         // wildcard (audio/*)
        } else if (t.indexOf('/') !== -1 && t.indexOf(mimePrefix) === 0){
            return true;                                         // explicit MIME (application/pdf)
        }
    }
    return false;
}
// A file whose NAME already says what it is does not get the sniff rescue.
// The rescue exists for a dotless "ANGELS — Backing Track" or a .dat whose MIME
// the OS dropped; a photo.jpg on a zone that accepts .heic/.heif is not an
// unidentified file, it is a different format — and sniffing it as "image"
// admitted it to /heic-converter, where the page then dropped it in silence
// (found 2026-09-05 while testing the refusal wording). With the rescue
// withheld, the normal reject path names the accepted formats instead.
var KNOWN_MEDIA_EXT = /^(jpe?g|jpe|png|gif|webp|bmp|tiff?|avif|heic|heif|svg|ico|psd|raw|dng|cr2|nef|arw|mp4|mov|mkv|avi|webm|flv|wmv|m4v|3gp|ts|mts|mp3|wav|ogg|oga|opus|flac|aac|m4a|m4b|wma|aiff?|amr|caf|pdf|zip|rar|7z|gz|txt|json|xml|csv|docx?|xlsx?|pptx?)$/;
// Spellings of one format: a zone that lists `.jpg` also means `.jpeg`.
var EXT_ALIAS = { jpeg: 'jpg', jpe: 'jpg', tiff: 'tif', aif: 'aiff', htm: 'html', mpeg: 'mpg', yml: 'yaml' };
function canonicalExt(e){ e = (e || '').toLowerCase(); return EXT_ALIAS[e] || e; }
function sniffMatches(file, accept){
    var name = (file && file.name || '').toLowerCase();
    var dot = name.lastIndexOf('.');
    var ext = dot > 0 ? name.slice(dot + 1) : '';
    var tokens = (accept || '').split(',').map(function(t){ return t.trim().toLowerCase(); });
    var listed = tokens.filter(function(t){ return t.charAt(0) === '.'; }).map(function(t){ return canonicalExt(t.slice(1)); });
    if (ext && listed.length) {
        if (listed.indexOf(canonicalExt(ext)) !== -1) return Promise.resolve(true);   // .jpeg on a .jpg zone
        // A zone that takes a whole KIND (`video/*`) still wants the bytes to
        // decide — test-with-audio-4s.mp4 declared as image/jpeg is a video and
        // belongs on /add-music-to-video. Only a zone that names specific
        // formats and no wildcard for this file's kind refuses on the name.
        var kindOfExt = /^(jpe?g|jpe|png|gif|webp|bmp|tiff?|avif|heic|heif|svg|ico|psd|raw|dng|cr2|nef|arw)$/.test(ext) ? 'image/'
            : /^(mp4|mov|mkv|avi|webm|flv|wmv|m4v|3gp|ts|mts|mpe?g)$/.test(ext) ? 'video/'
            : /^(mp3|wav|ogg|oga|opus|flac|aac|m4a|m4b|wma|aiff?|aif|amr|caf)$/.test(ext) ? 'audio/' : '';
        var wildcardForKind = kindOfExt && tokens.indexOf(kindOfExt + '*') !== -1;
        if (!wildcardForKind && tokens.indexOf('*/*') === -1 && KNOWN_MEDIA_EXT.test(ext)) return Promise.resolve(false);   // a different, known format
    }
    return sniffMediaKind(file).then(function(kind){
        return acceptAdmitsKind(accept, kind);
    });
}

// ── Identity repair ──────────────────────────────────────────────────
//
// The zone learned to say YES to a file the OS described badly — twice, in
// fact: the empty-MIME extension fallback (2026-05-09) and the content sniff
// (2026-05-18). Both taught the ZONE. Neither told the TOOL. So a dotless
// "Biarkan Aku" with no MIME passed the zone on its bytes and was then turned
// away by the tool's own `file.type.startsWith('audio/') || EXT.test(file.name)`
// with «Format audio tidak didukung» — measured on ~60 tools that keep such a
// line, and reported from tembrica.com/id on 2026-08-11 by a reader who hit it
// on /audio-metadata AND /audio-watermark five minutes apart.
//
// Rather than rewrite sixty hand-written conditions (and the decoder/naming
// heuristics behind them, which read file.name too), the zone now hands the
// tool a file that DESCRIBES ITSELF: a usable `type`, and a `name` that ends in
// the extension its bytes call for. Every existing gate then evaluates
// correctly without being touched.
//
// Two rules, both deliberately narrow — the whole point is to add information,
// never to overrule the OS:
//   · `type` is filled only when it is absent or the catch-all octet-stream.
//     A MIME the OS actually stated is left alone.
//   · an extension is APPENDED only when the name has none. A present-but-wrong
//     extension is a different class and is left alone (the same restraint
//     __downloadFile keeps: rewriting the other direction is what broke .m4r).
//
// Zero-copy and identity-preserving: an own property shadows the Blob.prototype
// accessor, so the File the tool receives is the SAME object the size stamp,
// FileBytes' cache and the caps' file key are keyed on. Measured in Chrome:
// 0 ms, 0 bytes, and FormData carries the repaired name to server lanes too.
/* repairIdentity:begin */
var VAGUE_MIME = /^$|^application\/(octet-stream|unknown|force-download|binary)$|^binary\/octet-stream$/;
// Extension → what the file is, used when the name says what the MIME did not.
// Only formats the sniffer itself can produce, so the two readers agree.
var EXT_IDENTITY = {
    mp3: ['audio', 'audio/mpeg'], wav: ['audio', 'audio/wav'], flac: ['audio', 'audio/flac'],
    ogg: ['audio', 'audio/ogg'], oga: ['audio', 'audio/ogg'], opus: ['audio', 'audio/ogg'],
    m4a: ['audio', 'audio/mp4'], m4b: ['audio', 'audio/mp4'], aac: ['audio', 'audio/aac'],
    wma: ['audio', 'audio/x-ms-wma'], aiff: ['audio', 'audio/aiff'], aif: ['audio', 'audio/aiff'],
    amr: ['audio', 'audio/amr'], caf: ['audio', 'audio/x-caf'],
    mp4: ['video', 'video/mp4'], m4v: ['video', 'video/mp4'], mov: ['video', 'video/quicktime'],
    webm: ['video', 'video/webm'], mkv: ['video', 'video/x-matroska'], avi: ['video', 'video/x-msvideo'],
    wmv: ['video', 'video/x-ms-wmv'], flv: ['video', 'video/x-flv'], '3gp': ['video', 'video/3gpp'],
    ts: ['video', 'video/mp2t'], mts: ['video', 'video/mp2t'], m2ts: ['video', 'video/mp2t'],
    mpg: ['video', 'video/mpeg'], mpeg: ['video', 'video/mpeg'], vob: ['video', 'video/mpeg'],
    ogv: ['video', 'video/ogg'],
    jpg: ['image', 'image/jpeg'], jpeg: ['image', 'image/jpeg'], png: ['image', 'image/png'],
    gif: ['image', 'image/gif'], webp: ['image', 'image/webp'], bmp: ['image', 'image/bmp'],
    heic: ['image', 'image/heic'], heif: ['image', 'image/heif'], avif: ['image', 'image/avif'],
    tif: ['image', 'image/tiff'], tiff: ['image', 'image/tiff'], svg: ['image', 'image/svg+xml'],
    pdf: ['pdf', 'application/pdf'],
};
// MIME → the extension to append when the OS gave a type but no name to match.
var MIME_EXT = {
    'audio/mpeg': 'mp3', 'audio/mp3': 'mp3', 'audio/wav': 'wav', 'audio/x-wav': 'wav',
    'audio/wave': 'wav', 'audio/flac': 'flac', 'audio/x-flac': 'flac', 'audio/ogg': 'ogg',
    'audio/opus': 'opus', 'audio/mp4': 'm4a', 'audio/x-m4a': 'm4a', 'audio/aac': 'aac',
    'audio/aiff': 'aiff', 'audio/x-aiff': 'aiff', 'audio/amr': 'amr', 'audio/x-ms-wma': 'wma',
    'video/mp4': 'mp4', 'video/quicktime': 'mov', 'video/webm': 'webm', 'video/x-matroska': 'mkv',
    'video/x-msvideo': 'avi', 'video/avi': 'avi', 'video/mpeg': 'mpg', 'video/3gpp': '3gp',
    'video/mp2t': 'ts', 'video/x-ms-wmv': 'wmv',
    'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/webp': 'webp',
    'image/bmp': 'bmp', 'image/heic': 'heic', 'image/heif': 'heif', 'image/avif': 'avif',
    'image/tiff': 'tif', 'application/pdf': 'pdf',
};
// The name's extension, but only when it looks like one: a dotless
// "Biarkan Aku" and a dotted "Track 3. Final mix" must both read as «none».
function nameExt(name){
    var dot = String(name || '').lastIndexOf('.');
    if (dot <= 0) return '';
    var e = String(name).slice(dot + 1).toLowerCase();
    return /^[a-z0-9]{1,5}$/.test(e) ? e : '';
}
function shadow(file, prop, value){
    try { Object.defineProperty(file, prop, { value: value, configurable: true, enumerable: false }); return true; }
    catch(_){ return false; }
}
function repairIdentity(file){
    if (!file) return null;
    var sniff = file.__tuSniff || null;               // set by sniffMediaKind when we read the head
    var ext   = nameExt(file.name);
    var mime  = String(file.type || '').toLowerCase();
    var vague = VAGUE_MIME.test(mime);

    // What this file is, best evidence first: its own bytes, then the name,
    // then the MIME the OS stated.
    var kind = sniff && sniff.kind, wantMime = sniff && sniff.mime, wantExt = sniff && sniff.ext;
    if (!kind && ext && EXT_IDENTITY[ext]) { kind = EXT_IDENTITY[ext][0]; wantMime = EXT_IDENTITY[ext][1]; }
    if (!kind && !vague && MIME_EXT[mime]) { kind = mime.split('/')[0]; wantExt = MIME_EXT[mime]; }
    if (!kind) return null;                            // nothing to say — leave the file exactly as it came

    var changed = null;
    if (vague && wantMime && shadow(file, 'type', wantMime)) {
        changed = changed || {}; changed.type = wantMime;
    }
    if (!ext && wantExt && shadow(file, 'name', String(file.name || 'file') + '.' + wantExt)) {
        changed = changed || {}; changed.ext = wantExt;
    }
    if (changed) { changed.kind = kind; changed.via = sniff ? 'sniff' : (ext ? 'ext' : 'mime'); }
    return changed;
}
// The zone's answer to «what is this file», carried on the file itself so a
// tool can ask instead of re-deriving it from the name. '' when the file did
// not come through a zone. Deliberately NOT folded into __tuGate: that stamp
// means «the zone gated the size at this number» and the layout skips its own
// size check for stamped files — a zone with max-mb="0" gates nothing and must
// not start looking like it did.
function stampKind(file){
    var id = file && file.__tuSniff;
    var kind = (id && id.kind) || '';
    if (!kind) {
        var e = nameExt(file && file.name);
        if (e && EXT_IDENTITY[e]) kind = EXT_IDENTITY[e][0];
        else {
            var m = String((file && file.type) || '').toLowerCase();
            if (!VAGUE_MIME.test(m)) kind = m.split('/')[0] === 'application' ? (m === 'application/pdf' ? 'pdf' : '') : m.split('/')[0];
        }
    }
    if (kind) shadow(file, '__tuKind', kind);
    return kind;
}
/* repairIdentity:end */
function zoneKind(file){
    return (file && file.__tuKind) || '';
}

// Snapshot of the most recently picked file across all uploaders. Read by the
// feedback form's client-state collector (resources/views/components/tool-feedback.blade.php),
// so a "не работает" report carries enough context to diagnose without analytics.
// Kept on window because the feedback form lives outside the tool-uploader DOM tree.
function recordLastFile(file, source){
    if (!file) return;
    window.__LAST_FILE = {
        name: (file.name || '').slice(0, 120),
        size: file.size || 0,
        type: (file.type || '').slice(0, 60),
        ext: (file.name || '').split('.').pop().toLowerCase().slice(0, 8),
        source: source,  // 'click' | 'drop'
        ts: Date.now(),
    };
    // См. комментарий у recordRejectContext: отпечатку нужен сам File.
    window.__LAST_FILE_REF = file;
}

/**
 * @param batch  the whole picked list, so the event carries HOW MANY files came
 *               in one gesture. Without it `file_picked` counted gestures, not
 *               files, and batch size was unmeasurable across every tool that
 *               uses this uploader — a question that came up while sizing
 *               photo-crop's batch limits (2026-07-30) and could only be
 *               answered for the three tools that happen to self-report a count.
 */
function emitTelemetry(file, source, uploaderEl, batch, pickS){
    if (typeof window.__ae !== 'function' || !file) return;
    var slug = (document.querySelector('meta[name="tool-slug"]') || {}).content || null;
    // Carry device context so we can later correlate upload → OOM:
    // dm   = navigator.deviceMemory (GB)
    // hint = __effectiveFileLimit(kind) in MB at pick time
    // Compared against the resulting success/error for the same session,
    // this lets us validate (or tighten) the 250 MB/GB formula.
    var kind = uploaderEl && uploaderEl.dataset ? (uploaderEl.dataset.kind || '') : '';
    // A zone that takes photos AND clips judges a clip by its VIDEO kind
    // (data-kind-video — capMbFor in wire()). The hint must be the number the
    // gate used, or overHint marks every clip it accepted as «over»: on
    // /slideshow-maker a phone clip read hintMB 100 (the photo ceiling) beside
    // the «видео до 4 ГБ» the zone printed and enforced (30.09.2026).
    var kindVideo = uploaderEl && uploaderEl.dataset ? (uploaderEl.dataset.kindVideo || '') : '';
    var kindAudio = uploaderEl && uploaderEl.dataset ? (uploaderEl.dataset.kindAudio || '') : '';
    if (kindVideo && isVideoLike(file)) kind = kindVideo;
    else if (kindAudio && isAudioLike(file)) kind = kindAudio;
    var hintMB = 0;
    if (kind && typeof window.__effectiveFileLimit === 'function') {
        hintMB = Math.round(window.__effectiveFileLimit(kind) / 1048576);
    }
    var fileMB = Math.round((file.size || 0) / 1048576);
    var n = (batch && batch.length) || 1;
    var batchMB = 0;
    if (n > 1) {
        for (var bi = 0; bi < batch.length; bi++) batchMB += (batch[bi].size || 0);
        batchMB = Math.round(batchMB / 1048576);
    }
    var ev = {
        slug: slug,
        f: 'file_picked',
        src: source,             // 'click' | 'drop'
        n: n,                    // files in THIS pick (1 unless a batch)
        batchMB: batchMB || fileMB,
        ext: (file.name || '').split('.').pop().toLowerCase().slice(0, 8),
        sz: file.size || 0,      // bytes
        mime: (file.type || '').slice(0, 40),
        dm: navigator.deviceMemory || 0,
        hintMB: hintMB,
        fileMB: fileMB,
        // Flag when user picked a file larger than our adaptive hint — if
        // this correlates with OOM we know the hint is too optimistic;
        // if these succeed often the hint is too conservative.
        overHint: (hintMB && fileMB > hintMB) ? 1 : 0,
    };
    // Seconds from the tap that opened the picker to the file (null when the
    // file came another way). Read against fileMB on iOS it is the phone's
    // preparation time — the number PICK_PREP_WAIT_S should be tuned from.
    if (typeof pickS === 'number') ev.pick_s = pickS;
    // Is this file's identity stable across picks? The day's free repeat of a file
    // is keyed by name|size|lastModified (tool-usage-limit.js rawKey), and a picker
    // that stamps the pick time, or hands the same video over under another name,
    // makes every re-pick a NEW file — billed again. Support mail 16.10.2026
    // (/enhance-video, Android 15, Yandex Browser): the same 243 926 981-byte video
    // picked on three page loads of one day, a wall each time. RU, 7 days: a re-pick
    // on a new page is recognised 83–87 % of the time — mobile video only 75 %.
    //   lm_s — age of lastModified at the pick (exact under an hour, hours above,
    //          −1 when none): ≈ pick_s on a re-pick means the picker stamps the pick;
    //   nh   — the name folded to 12 bits (never the name): differs between two
    //          picks of one size → the name changed.
    try {
        var lm = +file.lastModified || 0, age = lm > 0 ? Math.max(0, Math.round((Date.now() - lm) / 1000)) : -1;
        ev.lm_s = age < 3600 ? age : Math.min(31536000, Math.round(age / 3600) * 3600);
        var nm = String(file.name || ''), h = 0;
        for (var ci = 0; ci < nm.length; ci++) h = (h * 31 + nm.charCodeAt(ci)) >>> 0;
        ev.nh = h & 4095;
    } catch (_) {}
    window.__ae('feature_use', ev);
}

// Above this cut-off we skip FileBytes.prime — pulling 200 MB+ into JS heap at pick
// time just to cache it is pointless when WORKERFS streaming will read the same bytes
// lazily from disk during processing. Memory guard + streaming together handle huge
// files without the eager load.
var PRIME_MAX_BYTES = 100 * 1024 * 1024;
// Video files on memory-constrained devices RAM-prime only up to 32 MB; the
// 32–100 MB band routes to the OPFS spill instead. Video pipelines read via
// WORKERFS mounts / object URLs — the RAM copy is pure handle-death insurance,
// and readBytes()/asFile() consult the spill tier for exactly that rescue, so
// the FILE_LOST guard survives the reroute at zero heap cost. The batch math
// that forced this (bug-report 02.08, iPhone/YaBrowser): one pick of three
// MOVs (454+122+58 MB) — 58 MB primed into heap + concurrent spill streams +
// three <video> probes killed the WKWebView tab, wiping the user's project.
// deviceMemory is undefined on Safari/Firefox: treat as constrained only when
// the UA is actually iOS (desktop Safari/Firefox keep the 100 MB behaviour).
var PRIME_VIDEO_CONSTRAINED_BYTES = 32 * 1024 * 1024;
function constrainedMemoryDevice(){
    /* Владелец числа — window.__memBudget (layouts/app.blade.php): своя копия
       фолбэка здесь была ещё одним тихим решением о бюджете, а в WebKit
       deviceMemory нет вовсе. gb() возвращает 2 для iOS-WebKit, поэтому старая
       UA-ветка ниже больше не нужна — она в него уже вшита. */
    var B = window.__memBudget;
    if (B && typeof B.gb === 'function') return B.gb() <= 4;
    var dm = navigator.deviceMemory;
    if (typeof dm === 'number') return dm <= 4;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (/Macintosh/i.test(navigator.userAgent) && 'ontouchend' in document);
}

// The one place that already knows a picked file cannot be read.
//
// Until 2026-08-26 this function threw that knowledge away
// (`.catch(function(){})`) and handed the tool a File it had just proved dead.
// ~380 tools then rediscovered it one at a time, each in its own words:
// /3d-photo-to-model told comment #1492 "could not read the image, try a
// different file", so he picked a second cloud photo and lost that one too,
// rated 1 star and left. Only 28 tool blades mention FILE_LOST at all, so for
// the rest the verdict was simply never spoken.
//
// Owning it here is what makes the answer the same everywhere, and it is where
// it belongs: the uploader is the front door for file intake, it already owns
// the too-large / empty / wrong-type / folder / MIDI verdicts, and it is the
// only party that primes the bytes at pick time.
//
// Scale (RU shard, 30 d to 26.08): 1681 sessions ended on a fatal prime; only
// 26% of them ever reached a tool_complete afterwards.
//
// NOT blocking: dispatch stays synchronous, so the happy path is byte-for-byte
// the old one and the read still starts in the same task as `change` (moving it
// later is itself a documented cause of NotReadableError). The verdict lands
// when the retry ladder finishes, ~1 s later, in a banner that persists — the
// tool's own transient toast has faded by then.
function reportUnreadable(zoneEl, file, err, seq){
    // A stale pick must never speak: by the time the ladder gives up the user
    // may already have picked a different file, and re-opening the zone under
    // a working file would destroy their state. Compare the pick sequence.
    if (!zoneEl || zoneEl.__tuPickSeq !== seq) return;
    var kind = (err && err.fileLostKind) || 'read_error';
    if (kind === 'oom') return;   // not about the file; the tool's own OOM path owns this
    if (kind === 'aborted') return;   // a cancelled read (page left, pick replaced) — nothing to tell (27.09.2026)
    rejectUnreadable(zoneEl, file, kind, err);
}

function primeBytes(files, zoneEl, seq){
    if (!window.FileBytes || typeof window.FileBytes.prime !== 'function') return;
    // Read-once tools opt out (data-no-prime): they consume the bytes in the
    // same gesture, so the FILE_LOST insurance can never pay out — while the
    // OPFS spill would copy multi-GB picks to disk for nothing.
    if (zoneEl && zoneEl.dataset && zoneEl.dataset.noPrime === '1') return;
    var kind = (zoneEl && zoneEl.dataset && zoneEl.dataset.kind) || '';
    // Any video-family kind, not just the literal 'video': the size-only kinds
    // ('videoStream', 'videoDemux') describe the same MOV/MP4 picks and must not
    // slip out of the iOS 32 MB prime cap just because they carry a bigger
    // ceiling. Before this, repair-video's 'videoStream' zone silently primed
    // up to 100 MB on the very devices the cap exists for.
    var ramCap = (/^video/.test(kind) && constrainedMemoryDevice())
        ? PRIME_VIDEO_CONSTRAINED_BYTES : PRIME_MAX_BYTES;
    /* The per-file cap above answers "is THIS file too big for RAM". It cannot
       answer "is this PICK too big", and a multi-file zone is where that
       question is asked: 94 photos of 7 MB each are 94 small files and 637 MB
       of heap. 111 tool pages mount a `multiple` uploader, so this is a class,
       not one tool. Beyond the aggregate budget we do not drop the insurance —
       we take the same file down the OPFS spill tier, which readBytes()/asFile()
       already consult for the FILE_LOST rescue. */
    var aggBudget = (typeof window.FileBytes.ramBudget === 'function')
        ? window.FileBytes.ramBudget() : Infinity;
    var aggUsed = (typeof window.FileBytes.ramUsed === 'function')
        ? window.FileBytes.ramUsed() : 0;
    for (var i = 0; i < files.length; i++) {
        var overAgg = (aggUsed + (files[i].size || 0)) > aggBudget;
        if (files[i].size > ramCap || overAgg) {
            /* Above the RAM cap — spill to the OPFS disk tier instead of
               skipping entirely. Streamed copy (no heap spike); gives the
               WORKERFS mount rescue a byte-identical disk fallback when
               the OS revokes the picker handle. 26% of the FILE_LOST
               cohort (104/407 events / 14 d, telemetry 2026-07-03) was
               files above this cap with zero survivability before. */
            if (typeof window.FileBytes.spill === 'function') {
                window.FileBytes.spill(files[i]);
            }
            continue;
        }
        aggUsed += (files[i].size || 0);
        (function(f){
            window.FileBytes.prime(f).catch(function(err){ reportUnreadable(zoneEl, f, err, seq); });
        })(files[i]);
    }
}

// ── Second slot for a file of another kind ──────────────────────────────
// `data-reroute="video/*=a2vBgVideoInput;image/*=a2vMediaInput"` on a zone:
// a file that does NOT match the zone's own accept but matches a rule goes
// to that target instead of being rejected. A raw <input type=file> target
// gets a synthetic change — its own handler runs exactly as if the user had
// used it; a shared-zone target gets inject(). The zone fires
// `tool-uploader:rerouted` {files, target, accept, source} BEFORE delivery so
// the host can open the right panel and set its telemetry source. The
// primitive itself says nothing to the user: the wording belongs to the tool
// that owns the slot.
// Why: /audio-to-video has a looping-video background and a photo strip, yet
// a video dropped on its (audio) zone was rejected and bridged to
// /add-music-to-video — a tool that cuts the output at the VIDEO's length, so
// it cannot loop a 10-second clip under a 3-minute track (63 such drops in
// 90 days, 18 followed the link). The slot was on the same page all along.
function parseRerouteRules(spec){
    var rules = [];
    String(spec || '').split(';').forEach(function(part){
        var i = part.lastIndexOf('=');
        if (i <= 0) return;
        var acc = part.slice(0, i).trim(), id = part.slice(i + 1).trim();
        if (acc && id) rules.push({ accept: acc, target: id });
    });
    return rules;
}
function rerouteOtherKinds(el, fileList, zoneAccept, source){
    var files = Array.prototype.slice.call(fileList || []);
    var rules = parseRerouteRules(el.dataset && el.dataset.reroute);
    if (!rules.length) return { moved: 0, left: files };
    var left = [], groups = {}, order = [];
    files.forEach(function(f){
        if (matchesAccept(f, zoneAccept)) { left.push(f); return; }   // the zone's own kind — normal path
        var rule = null;
        for (var i = 0; i < rules.length; i++) { if (matchesAccept(f, rules[i].accept)) { rule = rules[i]; break; } }
        if (!rule) { left.push(f); return; }                           // no slot for it — the normal reject path
        if (!groups[rule.target]) { groups[rule.target] = { rule: rule, files: [] }; order.push(rule.target); }
        groups[rule.target].files.push(f);
    });
    var moved = 0;
    order.forEach(function(id){
        var g = groups[id], target = document.getElementById(id);
        if (!target) { left = left.concat(g.files); return; }
        var isZone = !!(target.classList && target.classList.contains('tool-uploader'));
        var takesMany = isZone ? !!(target.dataset && target.dataset.multiple === '1') : !!target.multiple;
        var take = takesMany ? g.files : g.files.slice(0, 1);
        if (!takesMany && g.files.length > 1) left = left.concat(g.files.slice(1));
        try {
            el.dispatchEvent(new CustomEvent('tool-uploader:rerouted', {
                detail: { files: take, target: target, accept: g.rule.accept, source: source },
                bubbles: false
            }));
        } catch (_) {}
        var delivered = false;
        if (isZone) {
            if (!target.__toolUploaderWired) wire(target);
            if (typeof target.__uploaderInjectFile === 'function') {
                take.forEach(function(f){ target.__uploaderInjectFile(f, 'rerouted'); });
                delivered = true;
            }
        } else if (target.tagName === 'INPUT' && target.type === 'file') {
            try {
                // A secondary picker owned by the tool: the page-level rescue
                // keeps its hands off the synthetic change below.
                target.setAttribute('data-aux-input', '');
                var dt = new DataTransfer();
                take.forEach(function(f){ dt.items.add(f); });
                target.files = dt.files;
                target.dispatchEvent(new Event('change', { bubbles: true }));
                delivered = true;
            } catch (_) {}
        }
        if (!delivered) { left = left.concat(take); return; }
        moved += take.length;
        try {
            if (typeof window.__ae === 'function') {
                window.__ae('feature_use', {
                    slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
                    f: 'file_rerouted', to: id, n: take.length, src: source,
                    kind: _classifyFileKind(take[0]),
                    ext: (take[0].name || '').split('.').pop().toLowerCase().slice(0, 8)
                });
            }
        } catch (_) {}
    });
    return { moved: moved, left: left };
}

// ONE decode at a time on a PAGE (every decode-audio zone shares this tail — it lives outside
// wire(), which runs once per zone). A decode holds the file's sound about twice while it runs (the
// decoder's output and the AudioBuffer made of it), and a page with several decode-audio zones —
// /mashup-maker's three decks — started one per pick: two 5-min songs decoding side by side were
// ~400 MB at once on a 4 GB phone whose tab then died (bug report 2026-09-28, renderer_crash oom ×6).
// The next file waits for the TERMINAL event of the one before (decoded, decode-error or the stall
// verdict — a decode that never settles cannot hold the queue past STALL_MS), under its zone's
// overlay; its stall watchdog starts with ITS decode. A file its zone no longer holds when its turn
// comes (replaced, cleared) is not decoded.
var decodeTail = Promise.resolve();

/* lateListeners:begin */
// ── A pick made while the page is still loading reaches a listener that binds late ──
// The zone takes files from the moment it is wired; the tool that listens to it is
// usually a deferred script or a module further down the page, so for a while the
// zone hands its file to nobody. `__bindUploaderFile` (below) closes that for the
// tools that bind through it. A tool that listens with a plain
// `zone.addEventListener('tool-uploader:file', …)` had nothing: the pick was gone,
// the zone showed the file as loaded, the tool stood still.
//
// Measured on production 07.10.2026, 356 zones on 312 pages: 114 zones have such
// a listener bound AFTER the zone is wired (112 by DOMContentLoaded, 2 by load,
// none later; median 1.3 s on a desktop), and a file picked in that window was
// lost on 115 of them, plus 4 zones that wait for `decoded`. In the field the
// window is seconds long: the tools that DO bind through the helper were
// replayed an early pick 1027 times in 30 days (RU), mean age 5–7 s, up to 29 s.
//
// So the zone remembers what it dispatched for the standing pick, and a listener
// for these events added to it while the page is still loading is handed, once,
// what it missed — as if the pick had been made after everything was in place.
// The listener is called with a real CustomEvent carrying the SAME detail.
//   • until `load` (+ a tick, so `load` handlers that bind are in) — afterwards a
//     listener that appears is somebody's lazy part and an old pick is not news to
//     it. NO age limit before that: while the page loads, the zone is showing
//     that file as loaded, and the slower the connection the longer the tool's
//     script takes — a limit would lose the pick for exactly the people this is
//     for (a loaded test box, 07.10.2026: scripts 38–96 s behind the pick; in the
//     field the helper's own 30 s limit dropped 24 picks in 30 days, mean age of
//     the dropped 30–75 s). The helper's limit now applies after `load` only;
//   • one listener, one delivery; a new pick or a clear forgets the old one;
//   • `__bindUploaderFile` adds its own listener past this door and keeps its own
//     replay — «one consumer takes the pick» is its rule, not this one's.
// What it does not see: a listener on an ANCESTOR of the zone or on `document`
// (the live event bubbles to them; this door is the zone's). One tool listened so
// and was moved to the helper; UploaderListenerRaceTest holds that line.
// Why not hold the dispatch until the page has loaded instead: 63 zones have their
// listener bound before the zone is even wired, and they would wait for scripts
// that have nothing to do with them — for seconds, on exactly the slow loads.
var REDELIVERED = { 'tool-uploader:file': 1, 'tool-uploader:decoded': 1, 'tool-uploader:decode-error': 1 };
var pageSettled = document.readyState === 'complete';
var lateListenerZones = [];
function settlePage(){
    pageSettled = true;
    for (var i = 0; i < lateListenerZones.length; i++) {
        var z = lateListenerZones[i];
        z.__tuPick = null;
        // Ours to remove only while it is still ours: somebody may have wrapped it.
        try { if (z.addEventListener === z.__tuLateDoor) delete z.addEventListener; } catch (_) {}
        z.__tuLateDoor = null;
    }
    lateListenerZones = [];
}
if (!pageSettled) window.addEventListener('load', function(){ setTimeout(settlePage, 0); }, { once: true });

function watchLateListeners(el){
    if (pageSettled || el.__tuLateDoor) return;
    var door = function(type, fn){
        // The prototype's method is looked up at call time, so whatever wraps it
        // (error reporters do) still applies to listeners added through here.
        var r = EventTarget.prototype.addEventListener.apply(this, arguments);
        if (!pageSettled && fn && REDELIVERED[type] === 1 && this === el) redeliverTo(el, type, fn, arguments[2]);
        return r;
    };
    try { el.addEventListener = door; el.__tuLateDoor = door; lateListenerZones.push(el); } catch (_) {}
}

// What the zone has dispatched for the pick it is holding. Forgotten BEFORE a new
// pick is dispatched and written AFTER: a listener added from inside another
// listener, in answer to this very pick, is that tool's own doing, not a latecomer.
function forgetPick(el){ el.__tuPick = null; }
function notePick(el, type, detail){
    if (pageSettled) return;
    var p = el.__tuPick;
    if (!p) { if (type !== 'tool-uploader:file') return; p = el.__tuPick = { at: Date.now(), ev: {}, told: {}, said: {} }; }
    p.ev[type] = detail;
}

function redeliverTo(el, type, fn, opts){
    var p = el.__tuPick;
    if (!p || !Object.prototype.hasOwnProperty.call(p.ev, type)) return;
    var ageMs = Date.now() - p.at;
    var told = p.told[type] || (p.told[type] = []);
    if (told.indexOf(fn) !== -1) return;          // the same function added twice is one listener
    told.push(fn);
    var o = (opts && typeof opts === 'object') ? opts : null;
    // A microtask, like the helper's replay and for its reason: the caller is in
    // the middle of its own setup, and what its handler closes over is not all
    // assigned yet (Sentry TIMBRICA-FRONTEND-3Z/47).
    var defer = (typeof queueMicrotask === 'function') ? queueMicrotask : function(f){ Promise.resolve().then(f); };
    defer(function(){
        if (el.__tuPick !== p) return;            // replaced or cleared in between
        if (o && o.signal && o.signal.aborted) return;
        var ev = new CustomEvent(type, { detail: p.ev[type], bubbles: true });
        try {
            Object.defineProperty(ev, 'target', { value: el });
            Object.defineProperty(ev, 'currentTarget', { value: el });
            Object.defineProperty(ev, 'srcElement', { value: el });
        } catch (_) {}
        ev.__tuRedelivered = true;
        if (o && o.once) { try { el.removeEventListener(type, fn, opts); } catch (_) {} }
        try {
            if (!p.said[type]) {
                p.said[type] = 1;
                if (typeof window.__ae === 'function') {
                    window.__ae('feature_use', {
                        slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || '',
                        f: 'uploader_redelivered', ev: type.slice(14), age_ms: ageMs, zone: el.id || ''
                    });
                }
            }
        } catch (_) {}
        try {
            if (typeof fn === 'function') fn.call(el, ev);
            else if (fn && typeof fn.handleEvent === 'function') fn.handleEvent(ev);
        } catch (e) {
            // A listener that throws is its page's error, as it is on a live event.
            if (typeof window.reportError === 'function') window.reportError(e);
            else setTimeout(function(){ throw e; }, 0);
        }
    });
}
// For a module of this file that must listen PAST the door (the helper below).
function listenPastLateDoor(el, type, fn){
    if (el.__tuLateDoor) EventTarget.prototype.addEventListener.call(el, type, fn);
    else el.addEventListener(type, fn);
}
/* lateListeners:end */
/* pendingPick:begin */
// The files of a pick the layout stashed before this zone wired. The stash
// carries files[0] as `pending` and, since 30.10.2026, the whole list beside it;
// a stash from an older layout still has its list in the zone's own input (a
// click). Either list counts only while it starts with that same File — a list
// left from an earlier pick is not this one.
function pendingPickList(pending, stashed, inputFiles){
    if (stashed && stashed.length > 1 && stashed[0] === pending) return Array.prototype.slice.call(stashed);
    if (inputFiles && inputFiles.length > 1 && inputFiles[0] === pending) return Array.prototype.slice.call(inputFiles);
    return [pending];
}
/* pendingPick:end */

function wire(el){
    if (el.__toolUploaderWired) return;
    el.__toolUploaderWired = true;
    watchLateListeners(el);

    var accept    = el.dataset.accept || '*/*';
    // Tools where a 0-byte file is legitimate content (archivers) opt out of
    // the empty-file rejection below. Default stays on for everyone else.
    var allowEmpty = el.dataset.allowEmpty === '1';
    // iOS quirk: Safari's file picker filters by UTI, and bare "audio/*" /
    // "video/*" / "image/*" maps to a narrow subset of Apple-native types —
    // .ogg / .flac / .opus / .wma / .aiff / .m4b audio files become greyed
    // out in Files.app even when they exist. Explicit .ext tokens in the
    // accept attribute widen the filter. Tools that still pass a bare
    // wildcard (there are ~60 of them, migrating incrementally) get this
    // widening for free without editing the blade. Harmless on non-iOS
    // because appending already-present extensions just re-lists them.
    // Desktop needs one widening of its own, for a different reason: the OS
    // picker filters `video/*` through the browser's MIME table, and Chromium
    // maps .mts to `model/vnd.mts` and .m2ts to `application/octet-stream`
    // (measured 2026-08-09). Under a bare wildcard an AVCHD camcorder file is
    // greyed out and the user has to know to switch the picker to "All files".
    // These are the only video containers whose desktop MIME is not video/*;
    // .mpg/.vob/.m4v already map correctly and need nothing.
    // ⚠️⚠️ ТОЛЬКО НЕ Android — и это не вкусовщина, а условие системного
    // фотопикера. Chromium открывает ACTION_PICK_IMAGES (единственный пикер
    // Android, где мультивыбор есть ВСЕГДА) лишь когда accept сводится к
    // картинкам и видео; здесь же дописывается .mts, который тот же Chromium
    // маппит в `model/vnd.mts` (замер 09.08.2026, две строки выше) — тип не
    // видео, и зона молча падает в старый чузер. Там множественный выбор
    // зависит от оболочки браузера, а у встроенного браузера Xiaomi его нет
    // вовсе: обращение 19.09.2026, Премиум, «по несколько фотографий не
    // цепляет, только по одной». Сама причина расширений — десктопная
    // (AVCHD-файлы камкордера серели в пикере ОС), на телефоне AVCHD не
    // снимают, поэтому потеря здесь теоретическая, а выигрыш измерен.
    if (/\bvideo\/\*/.test(accept) && !/Android/i.test(navigator.userAgent)) accept += ',.mts,.m2ts,.ts';
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent) || /Macintosh/i.test(navigator.userAgent) && 'ontouchend' in document) {
        if (/\baudio\/\*/.test(accept)) accept += ',.mp3,.wav,.ogg,.flac,.aac,.m4a,.m4b,.wma,.aiff,.opus,.webm,.caf,.3gp,.mp4,.mov,.mka';
        if (/\bvideo\/\*/.test(accept)) accept += ',.mp4,.webm,.mov,.mkv,.avi,.wmv,.flv,.m4v,.3gp,.ts,.mts,.m2ts,.vob,.mpg,.mpeg,.ogv';
        if (/\bimage\/\*/.test(accept)) accept += ',.jpg,.jpeg,.png,.webp,.gif,.bmp,.heic,.heif,.avif,.tiff,.tif';
    }
    var rawMaxMb  = el.dataset.maxMb || '0';
    // Three cap modes, picked per tool:
    //   "0"     — no upload cap. Best for tools that route everything through
    //             FFmpegProcessor: FFmpegMemoryGuard already refuses operations
    //             that won't fit on the user's device (device-adaptive, operation-aware).
    //   "auto"  — adaptive cap based on navigator.deviceMemory. For tools that
    //             decode into a Web Audio PCM buffer (3d-audio, audio-degrader etc.)
    //             where a single full-file RAM load happens; FFmpegMemoryGuard
    //             doesn't cover those paths.
    //   "200"   — hard static cap (MB). For tools with a known codec-specific
    //             upper bound.
    // 60 MB per GB of deviceMemory roughly tracks what a single-shot
    // decodeAudioData + waveform render fits in without OOM across 2–16 GB machines.
    var maxMb;
    if (rawMaxMb === 'auto') {
        // Single source of truth — same number the user sees in the size
        // hint (__effectiveFileLimit). Previously the display and the
        // reject gate used different formulas (dm × 250 MB capped vs.
        // dm × 60 MB clamped 80–800), so a user saw "до 500 МБ" and got
        // rejected at 480 MB. Now both agree.
        var kind = el.dataset.kind || 'audio';
        if (typeof window.__effectiveFileLimit === 'function') {
            maxMb = Math.round(window.__effectiveFileLimit(kind) / 1048576);
        } else {
            var devMem = navigator.deviceMemory || 4;
            maxMb = Math.max(80, Math.min(1024, Math.round(devMem * 250)));
        }
        // A tool may have a ceiling of its own that is LOWER than what the
        // device could technically hold — /join, for example, enforces a 500 MB
        // total, so a 1024 MB per-file allowance is a number no file can ever
        // use. Quoting the roomier one told a user «максимум 1024 МБ» right
        // under a hint reading «суммарно до 500 МБ» (bug-report 2026-07-27).
        // Tools publish their own cap as __TOOL_MAX_MB; the stricter wins.
        if (typeof window.__TOOL_MAX_MB === 'number' && window.__TOOL_MAX_MB > 0) {
            maxMb = Math.min(maxMb, window.__TOOL_MAX_MB);
        }
    } else {
        maxMb = parseInt(rawMaxMb, 10) || 0;
    }
    /* A zone that takes photos AND clips has two kinds of file, and one kind
       per zone cannot describe both. `data-kind-video` names the size kind the
       VIDEO files are judged by — the clip lane's shape (videoWebCodecs for a
       lane that reads clips lazily and writes to OPFS); photos keep `kind`.
       Before this every clip dropped on a photo zone met the PHOTO ceiling:
       /slideshow-maker refused a 129 MB phone clip at «максимум 100 МБ» while
       its own «+» door took the same clip with no gate at all (support,
       22.09.2026). Only the adaptive mode: a hard max-mb is a server rule and
       stays one number for everything. */
    var kindVideo = el.dataset.kindVideo || '';
    var maxMbVideo = 0;
    if (rawMaxMb === 'auto' && kindVideo && typeof window.__effectiveFileLimit === 'function') {
        maxMbVideo = Math.round(window.__effectiveFileLimit(kindVideo) / 1048576);
        if (typeof window.__TOOL_MAX_MB === 'number' && window.__TOOL_MAX_MB > 0) {
            maxMbVideo = Math.min(maxMbVideo, window.__TOOL_MAX_MB);
        }
    }
    var kindAudio = el.dataset.kindAudio || '';
    var maxMbAudio = 0;
    if (rawMaxMb === 'auto' && kindAudio && typeof window.__effectiveFileLimit === 'function') {
        maxMbAudio = Math.round(window.__effectiveFileLimit(kindAudio) / 1048576);
        if (typeof window.__TOOL_MAX_MB === 'number' && window.__TOOL_MAX_MB > 0) {
            maxMbAudio = Math.min(maxMbAudio, window.__TOOL_MAX_MB);
        }
    }
    function capMbFor(file){
        if (maxMbVideo > 0 && isVideoLike(file)) return maxMbVideo;
        if (maxMbAudio > 0 && isAudioLike(file)) return maxMbAudio;
        return maxMb;
    }
    var multiple = el.dataset.multiple === '1';

    // Prefer the static <input> the blade component renders inside the zone
    // — its <label for=...> sibling makes the OS picker openable without JS,
    // which rescues users who clicked before this script finished loading.
    // Fall back to the legacy "create on body" path for any consumer that
    // mounts a .tool-uploader DOM dynamically without going through the
    // blade (rare; share-target receivers, etc.).
    var input = null;
    var staticId = el.dataset.staticInput;
    if (staticId) input = document.getElementById(staticId);
    if (!input) {
        input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.multiple = multiple;
        input.style.display = 'none';
        // Mount on body rather than inside the zone — tools sometimes rewrite
        // their zone's innerHTML on file load, which would silently detach an
        // in-zone input and break subsequent "Change file" clicks.
        document.body.appendChild(input);
    } else {
        // Static input came from blade. Keep its accept/multiple in lockstep
        // with the zone's data-attrs in case JS-side overrides differ.
        input.accept = accept;
        input.multiple = multiple;
    }
    // data-uploader-for ties the input back to its zone's id. Lets E2E tests
    // and inter-tool routing target the right input when the page has multiple
    // <x-tool-uploader> instances: page.locator('[data-uploader-for=ID]').
    if (el.id && !input.getAttribute('data-uploader-for')) input.setAttribute('data-uploader-for', el.id);
    // Unconditional wired-marker on the ZONE itself. data-uploader-for above
    // exists only for zones WITH an id, which made id-less zones (e.g.
    // ocr-text-extractor) look "dead" to the audit sweep and any E2E health
    // check — the 2026-07-10 audit spent a prod investigation on exactly that
    // false alarm. A probe can now assert '.tool-uploader[data-tu-wired]'.
    el.setAttribute('data-tu-wired', '1');

    // ── Overlay retirement at wire-time (structural, 2026-08-13) ──
    // The transparent native label overlay exists ONLY for the JS-free boot
    // window (click-before-wire on slow links opens the OS picker natively).
    // From this point on it must stop catching pointers: it spans the whole
    // zone at z-index:1, so any interactive child a custom slot rendered
    // WITHOUT its own stacking sat underneath and never received a click —
    // the tap opened the OS picker instead. Measured 2026-08-13: five tools
    // had hand-rolled per-button z-index patches for exactly this (analyze,
    // gif-audio, karaoke, voice-enhancer ×2), /background-music's «Убрать»
    // and /video-overlay's slot-remove × were silently dead. Retiring the
    // overlay here fixes the CLASS: slot buttons get their clicks, plain
    // zone clicks fall through to the el click handler below (hit == null →
    // input.click()), and the pre-wire native fallback is untouched.
    // Telemetry note: upload_zone_clicked src shifts 'label' → 'js' from
    // this date — both values arm the no-pick followup, so cohorts hold.
    var lblOv = el.querySelector(':scope > .tu-label-overlay');
    if (lblOv) lblOv.style.pointerEvents = 'none';

    // Last interaction source — set by the click and drop handlers when the
    // emit() call comes back via the hidden input's change event (which has
    // no concept of "drag" vs "click"). Programmatic / share-target / paste /
    // keyboard callers pass the source explicitly to emit() instead.
    var lastSource = 'click';

    function emit(fileList, sourceOverride, _heicDone, _emptyDone, _officeDone){
        if (!fileList || !fileList.length) return;
        var source = sourceOverride || lastSource;
        // Any new pick retires an office-lane door this zone was showing.
        if (el.__officeDoor || el.__officeDoorHost) closeOfficeDoor(el);
        // Second slot for a file of ANOTHER kind — data-reroute, see
        // rerouteOtherKinds. Runs on the untruncated batch so a single-file
        // zone can still hand three photos to a `multiple` photo input.
        if (el.dataset && el.dataset.reroute) {
            var routed = rerouteOtherKinds(el, fileList, accept, source);
            if (routed.moved) {
                if (!routed.left.length) { input.value = ''; return; }
                fileList = routed.left;
            }
        }
        var list = multiple ? Array.prototype.slice.call(fileList) : [fileList[0]];

        // The pick is now in the real pipeline — remove the layout's inline
        // "file accepted, module loading…" pre-wire note if one is showing.
        try { var pw = el.querySelector('.tu-prewire-note'); if (pw) pw.remove(); } catch(_){}

        // Zero-size probe — FIRST, before the MIDI/HEIC/accept gates, because
        // every one of those reads file bytes or metadata that a size-lying
        // File breaks (slice() clamps to the recorded size, so content-sniff
        // on a "0-byte" file reads nothing and rejects as wrong_format).
        // __probeEmptyFile (timbrica-core.js) re-reads the blob; when the size
        // metadata lied (Android SAF providers, some drag sources) the rebuilt
        // File — real .size, same name/type — re-enters the pipeline and the
        // pick just works. Only confirmed-unreadable files reach rejectEmpty.
        // The synchronous fast path (all sizes > 0) is unchanged.
        if (!_emptyDone && !allowEmpty) {
            var hasZero = false;
            for (var zi = 0; zi < list.length; zi++) { if (!list[zi].size) { hasZero = true; break; } }
            if (hasZero && typeof window.__probeEmptyFile === 'function') {
                // Keep each file's probe outcome alongside it: 'empty' (read
                // returned 0 bytes — the file really is empty) and 'read_error'
                // (the read threw — the handle is dead) need different advice,
                // and one message for both sent people to look in the cloud for
                // a file that was sitting empty on their own disk.
                var outcomes = [];
                Promise.all(list.map(function(f, fi){
                    if (f.size) return Promise.resolve(f);
                    var po = { src: source };
                    return window.__probeEmptyFile(f, po).then(function(rec){ outcomes[fi] = po.out; return rec || f; });
                })).then(function(resolved){
                    var kept = [], empties = [], firstEmptyKind = null;
                    for (var ri = 0; ri < resolved.length; ri++) {
                        if (resolved[ri] && resolved[ri].size) kept.push(resolved[ri]);
                        else { if (!empties.length) firstEmptyKind = outcomes[ri]; empties.push(resolved[ri]); }
                    }
                    if (empties.length) rejectEmpty(el, empties[0], firstEmptyKind);
                    if (!kept.length) { input.value = ''; return; }
                    // Partition (mirrors the wrong-format partial path): keep
                    // the readable files so a multi-track pick with one dead
                    // placeholder doesn't drop the user's whole selection.
                    emit(kept, source, _heicDone, true);
                });
                return;
            }
            // Probe helper unavailable (core script blocked) — finishEmit's
            // synchronous 0-byte gate still catches these; nothing to do here.
        }

        // Office lane (legacy-office="upgrade", see officeUpgrade above): an old
        // office file is answered by the door, not by this zone's tool. One file
        // at a time — every zone that opts in is a single-file zone.
        if (!_officeDone && el.dataset && el.dataset.legacyOffice === 'upgrade' && list.length === 1
            && officeLegacyByName(el, list[0])) {
            var olFile = list[0];
            input.value = '';
            // The pick DID arrive — settle the no-pick timer and the lost-pick record.
            disarmUploadNoPickFollowup(el);
            clearPickerOpen('file');
            officeUpgrade(el, olFile,
                function(f){ emit([f], source, _heicDone, true, true); },
                function(f){ rejectWrongType(el, [f]); });
            return;
        }

        // MIDI signposting — short-circuit BEFORE any decoder runs. A .mid file
        // tech-matches `accept="audio/*"` (audio/midi MIME) so without this gate
        // it would sail through, hit decodeAudioData/FFmpeg, fail with a
        // generic "could not decode" toast, and the user would give up. Send
        // them to /midi (MIDI→MP3 converter) instead. Only active on uploaders
        // whose accept covers audio — image/video/document uploaders that
        // happen to receive a .mid still fall through to the normal wrong-type
        // path (where "this tool needs an image, you uploaded a MIDI file" is
        // already correct). See Dinara bug-report 2026-06-24 comment #820.
        // A tool that parses MIDI itself (eight-bit-converter: MIDI → chiptune
        // directly) opts out of the signpost via data-accept-midi, so a .mid
        // reaches its own handler instead of being turned away.
        var acceptsMidi = el.dataset && el.dataset.acceptMidi === '1';
        var acceptsAudio = !acceptsMidi && (/audio/.test(accept) || /\.(mp3|wav|ogg|flac|aac|m4a|wma|aiff|opus|amr|caf|oga)\b/i.test(accept));
        if (acceptsAudio) {
            for (var mi = 0; mi < list.length; mi++) {
                if (isMidiFile(list[mi])) {
                    rejectMidi(el, list[mi]);
                    input.value = '';
                    try {
                        if (typeof window.__ae === 'function') {
                            var slugM = (document.querySelector('meta[name="tool-slug"]') || {}).content || null;
                            window.__ae('feature_use', {
                                slug: slugM, f: 'midi_signposted',
                                ext: (list[mi].name || '').split('.').pop().toLowerCase().slice(0, 8),
                                mime: (list[mi].type || '').slice(0, 40),
                            });
                        }
                    } catch (_) {}
                    return;
                }
            }
        }

        // iPhone HEIC/HEIF → JPEG at the upload boundary. <img>/createImageBitmap
        // can't decode HEIC off Safari, so without this every image tool would turn
        // iPhone photos away. Decode once here (shared heic-to helper) and re-enter
        // with the JPEG. Gated to uploaders that want a displayable image (HEIC-only
        // tools like /heic-converter opt out via wantsDecodedImage) and to actual
        // HEIC files — non-HEIC uploads never enter this path. _heicDone guards a
        // re-decode loop if a file fails to convert.
        // data-heic="keep": the tool converts the ORIGINAL itself (window.HeicConvert —
        // Exif, P3 → sRGB, GPU) because the JPEG made here carries no Exif. Since
        // 22.10.2026 /timestamp-camera: the Exif date and GPS of an iPhone photo are
        // the «from the file» time and place on its stamp.
        if (!_heicDone && window.HeicDecode && wantsDecodedImage(accept) &&
            !(el.dataset && el.dataset.heic === 'keep') &&
            list.some(function (f) { return window.HeicDecode.isHeic(f); })) {
            Promise.all(list.map(function (f) {
                return window.HeicDecode.isHeic(f)
                    ? window.HeicDecode.normalize(f, window.__ae || null).catch(function () { return f; })
                    : Promise.resolve(f);
            })).then(function (decoded) { emit(decoded, source, true); });
            return;
        }

        // Accept gate — paste/drag/programmatic can hand us anything regardless
        // of <input accept>. Clicked-input picks are pre-filtered by the OS, but
        // we re-check so all entry paths share one gate. Files that match by
        // extension/MIME pass synchronously (the 99.9% path — unchanged). Any
        // that don't get one content-sniff before rejection, so a valid media
        // file with a dotless name + dropped MIME isn't turned away.
        var pending = [];
        for (var j = 0; j < list.length; j++) {
            if (!matchesAccept(list[j], accept)) pending.push(list[j]);
        }
        if (!pending.length) { finishEmit(list, source); return; }
        // Partition the batch instead of all-or-nothing reject. Previously a
        // single sniff-failure dropped the entire batch silently — a user
        // dropping [photo1.jpg, readme.txt, photo2.jpg] saw a banner naming
        // only readme.txt and lost both photos. After: dispatch the accepted
        // subset via finishEmit and surface ALL rejected names in the banner
        // + a one-shot tool-uploader:partial-reject event for host tools
        // that want to layer their own message. Audit wf_9d2ce135 #25 + #27.
        Promise.all(pending.map(function(f){
            return sniffMatches(f, accept).then(function(ok){ return ok ? null : f; });
        })).then(function(results){
            var rejected = [];
            for (var r = 0; r < results.length; r++) {
                if (results[r]) rejected.push(results[r]);
            }
            if (rejected.length === 0) { finishEmit(list, source); return; }
            // Build the accepted subset by removing rejected files from the list.
            var rejectedSet = new Set(rejected);
            var accepted = [];
            for (var i = 0; i < list.length; i++) {
                if (!rejectedSet.has(list[i])) accepted.push(list[i]);
            }
            // No accepted files at all — fall through to the legacy reject
            // path (one banner naming all rejected files).
            if (accepted.length === 0) {
                rejectWrongType(el, rejected);
                input.value = '';
                return;
            }
            // Partial — keep accepted, surface rejected. Banner names all
            // rejected files (rejectWrongType handles arrays now).
            rejectWrongType(el, rejected, /* keepPartial */ true);
            try {
                el.dispatchEvent(new CustomEvent('tool-uploader:partial-reject', {
                    detail: { accepted: accepted, rejected: rejected },
                    bubbles: false
                }));
            } catch (_) {}
            finishEmit(accepted, source, { keepBanner: true });
        });
    }

    // Post-accept-gate pipeline: size/empty checks, then dispatch the file.
    function finishEmit(list, source, opts){
        var keepBanner = !!(opts && opts.keepBanner);
        // 0-byte gate. decodeAudioData / FFmpeg WASM hang on empty input in
        // some browsers (Chrome 119+ specifically), so catch this here once
        // for every uploader instead of forcing each tool to re-implement.
        //
        // allowEmpty opts out, because "0 bytes" is not universally broken —
        // for an archiver an empty file is ordinary content. Folder pick on
        // /archive-creator used to lose `.gitkeep` and empty logs with no
        // banner and no toast: a project template archived here came back
        // missing exactly the file whose absence breaks it.
        if (!allowEmpty) {
            for (var k = 0; k < list.length; k++) {
                if (!list[k].size) {
                    rejectEmpty(el, list[k]);
                    input.value = '';
                    return;
                }
            }
        }

        /* One oversize file no longer sinks the whole pick on a multi-file
           zone: the rest go through and the banner names the one that did
           not fit. Before, «5 clips + a 120 MB WAV» on /video-editor refused
           all six at the first oversize file (audit 30.09.2026). A single
           file — or a single-file zone — is refused exactly as before. */
        var tooBig = [], fits = [];
        for (var i = 0; i < list.length; i++) {
            var capMb = capMbFor(list[i]);   // the zone's number for THIS file's kind
            if (capMb > 0 && list[i].size > capMb * 1024 * 1024) tooBig.push({ file: list[i], cap: capMb });
            else fits.push(list[i]);
        }
        if (tooBig.length && (!multiple || !fits.length)) {
            rejectTooLarge(el, tooBig[0].file, tooBig[0].cap);
            input.value = '';
            return;
        }
        if (tooBig.length) {
            rejectTooLarge(el, tooBig[0].file, tooBig[0].cap, /* partial */ true);
            keepBanner = true;
            list = fits;
        }

        // Accepted. Before anything downstream looks at the file, give it a
        // name and a type that match its bytes — the tool's own gates, its
        // decoder heuristics and its output naming all read those two strings,
        // and the OS does not always fill them in. See repairIdentity().
        for (var ri = 0; ri < list.length; ri++) {
            var fixed = repairIdentity(list[ri]);
            stampKind(list[ri]);
            if (!fixed) continue;
            try {
                if (typeof window.__ae === 'function') {
                    window.__ae('feature_use', {
                        slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
                        f: 'file_identity_repaired',
                        kind: fixed.kind, via: fixed.via,
                        gave: fixed.ext || '', mime: fixed.type || '',
                        mob: /Mobi|Android/i.test(navigator.userAgent) ? 1 : 0,
                    });
                }
            } catch (_) {}
        }

        // Accepted — clear any prior reject banner (unless this very pick
        // refused some of its files: that banner is the only place that says
        // which), announce to screen reader.
        if (!keepBanner) hideReject(el);
        announcePicked(el, list[0]);
        // The zone has answered the size question for these files, at maxMb —
        // the number its own size line printed. Stamp them so the layout's
        // __validateFileSize can never contradict that answer with a kind of
        // its own: /video-trim promised «до 4 ГБ» through a videoDemux zone and
        // then refused at its April-era 'video' re-check, «максимум 1024 МБ»
        // (two paying users on 2026-09-13; ~37 sessions/day since 24.08).
        // Only when the zone actually gated: a max-mb="0" zone enforces
        // nothing, and a tool behind it keeps its own gate.
        if (maxMb > 0 || maxMbVideo > 0 || maxMbAudio > 0) stampAccepted(list, el, maxMb, capMbFor);

        // Pick sequence — the token a late prime failure has to still match
        // before it is allowed to speak. Without it, the verdict for the file
        // the user ALREADY replaced would tear down the workspace of the file
        // that is working.
        el.__tuPickSeq = (el.__tuPickSeq || 0) + 1;
        el.__tuPickAt = Date.now();
        el.__tuUnreadableShown = null;
        primeBytes(list, el, el.__tuPickSeq);
        recordLastFile(list[0], source);
        // How long the system picker held the person before the file came —
        // on an iPhone that is mostly the phone preparing a video (see
        // PICK_PREP_WAIT_S). Only for a pick through a picker this zone opened.
        var pickS = (source === 'drop' || source === 'paste') ? null : pickerWaitS(el);
        el.__pickOpenAt = 0;
        emitTelemetry(list[0], source, el, list, pickS);

        var detail = multiple ? { files: list, source: source } : { file: list[0], files: list, source: source };
        // A tool that ALREADY holds this file's decoded sound (the same song in another of its
        // zones — /mashup-maker's deck C taking deck B's song) hands it back from its
        // `tool-uploader:file` listener, and a `decode-audio` zone skips the decode: the second
        // decode was a whole second copy of the song for nothing (bug report 2026-09-28). Opt-in by
        // construction — only the tool knows its buffers are read-only; it must call this
        // synchronously, in the listener.
        detail.reuseAudio = function (buffer) { if (buffer && typeof buffer.getChannelData === 'function') detail.__reuseAudio = buffer; };
        // Stash on element BEFORE dispatching — so a tool listener that
        // attaches LATE (defer-script race, content-blocker delay,
        // DOMContentLoaded-wrapped IIFE on slow 4G) can retroactively
        // catch a file the user already picked. Surfaced 2026-05-08 from
        // vladimir_xakim@mail.ru bug-report — first file_picked at t=11s
        // never reached the blade listener; processFile sat dead for 4 min
        // until user re-picked. Tools opt into recovery by checking
        // `el.__pendingFile` after attaching their listener (the listener
        // itself is idempotent for the same File reference via __consumed).
        el.__pendingFile = detail;
        el.__pendingTs = Date.now();
        // Cancel any arm'd no-followup timer — a real file_picked event is
        // about to dispatch, so the click did lead to a real pick.
        disarmUploadNoPickFollowup(el);
        // The pick arrived, so the "picker is open" record is settled — and
        // if this file came through the lost-pick note's retry button, that
        // note has done its job.
        clearPickerOpen('file');
        if (el.__pickLostBox) {
            hidePickLost(el);
            try {
                if (window.__ae) window.__ae('feature_use', {
                    slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
                    f: 'pick_lost_rescued' });
            } catch (_) {}
        }
        // …and if we already reported no_open for this uploader, retract it:
        // the picker plainly did open, the user was just slower than the 30 s
        // window. 84% of no_open sessions end this way (measured 2026-08-13),
        // so without this the bucket reads as a bug list and is not one.
        if (el.__uploadNoOpenAt) {
            var waitedS = Math.round((Date.now() - el.__uploadNoOpenAt) / 1000);
            el.__uploadNoOpenAt = null;
            try {
                if (window.__ae) window.__ae('feature_use', {
                    slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
                    f: 'upload_pick_after_no_open',
                    waited_s: waitedS,          // seconds BEYOND the 30 s verdict
                });
            } catch (_) {}
        }
        // Collapse BEFORE dispatch: a tool listener that synchronously hides the
        // whole zone (the 111 tools that already roll their own) must find the
        // row already inside it, otherwise it hides an un-collapsed zone and the
        // row flashes on the next frame.
        collapseZone(el, list);

        /* Зона — владелец вопроса «какой файл сейчас загружен на страницу», и
           до сих пор она отвечала на него ТОЛЬКО событием: кто не слушал в
           нужный момент, ответа не получал никогда. Запоминаем выданное, чтобы
           общий примитив мог спросить постфактум (см. __mp4Tags.pageSource:
           теги источника обязаны переехать в выход, а экспортёр, собирающий
           контейнер, исходного файла на руках не держит). Ссылка, не копия. */
        try { el.__deliveredFiles = list && list.length ? list.slice() : (detail && detail.file ? [detail.file] : []); } catch (_) {}

        /* Пришёл ЗВУК — подготовить перенос его тегов (audio-source-tags.js).
           Грузим здесь, а не у кодировщика: кодировщиков у сайта три, и у
           /voice-gender нет ни FFmpeg, ни общего mp3-энкодера — загрузчик,
           живший в ffmpeg-processor, туда просто не доезжал. Зона же стоит на
           каждой странице с файлом. Модуль маленький и грузится один раз;
           фото, PDF и прочее его не будят. */
        /* Модуль уже здесь — сообщить ЛЮБУЮ выдачу: звук, заменённый фото,
           обязан сбросить теги прежнего трека, иначе следующий экспорт унесёт
           чужое название. Модуля нет — будить его стоит только ради звука. */
        try {
            if (window.__audioSourceTags) window.__audioSourceTags.noteDelivered();
            else if (el.__deliveredFiles.some(looksLikeAudioForTags)) ensureAudioSourceTags();
        } catch (_) {}

        forgetPick(el);
        el.dispatchEvent(new CustomEvent('tool-uploader:file', {
            detail: detail,
            bubbles: true,
        }));
        notePick(el, 'tool-uploader:file', detail);

        // Reset the input AFTER dispatch so picking the SAME file again fires
        // `change` (Chromium suppresses it when the value is unchanged). The
        // reject paths above already do this; the accepted path didn't — so a
        // user whose tool-level handler rejected the file (or who simply wanted
        // to reload the same file) re-picked it and got total silence: no event,
        // no toast, no workspace. Bug-report 2026-06-12 /ru/auto-captions
        // ("Не загружается!", 756 MB mp4): blade rejected > cap, user re-picked
        // the same file, change never fired, upload_no_pick_followup armed.
        // The File references in `detail` / `el.__pendingFile` stay readable
        // after the value reset — clearing only drops the <input>'s selection.
        input.value = '';

        // Opt-in robust decode. When the blade declared `decode-audio`, run
        // the file through AudioDecoderRobust.robust (native → FFmpeg WASM →
        // AudioRecover) and dispatch tool-uploader:decoded with the buffer
        // ready. Tools listen for the decoded event instead of (or in
        // addition to) the file event. Single point of integration so the
        // 30 audio tools that previously called raw decodeAudioData all
        // become silently more resilient.
        // Under `multiple` a ONE-file pick is still decoded here (the tool keeps
        // its single-file path untouched); several files → the tool decodes them
        // itself from detail.files[] (a queue is the tool's business, not ours).
        if (el.dataset.decodeAudio === '1' && list.length === 1) {
            var reused = detail.__reuseAudio;
            delete detail.__reuseAudio;            // the stashed detail must not pin the song
            if (reused) reuseAndDispatch(el, list[0], reused, source);
            else decodeAndDispatch(el, list[0], source);
        }

        // Opt-in engine pre-warming. The uploader attribute names what the
        // tool needs (warm-engines="ffmpeg,dfn3") and we trigger
        // EngineWarmer.warm(slug, [...]) on file-pick so heavy WASM/AI
        // assets start downloading while the user reads settings — by the
        // time they click the action button the engine is already loaded.
        //
        // Engine specs:
        //   • BUILTIN names (`ffmpeg`) are stock — register-if-absent here
        //     so 30+ FFmpeg-tools don't repeat the same init() boilerplate.
        //     EngineWarmer.isRegistered() guards against stomping a tool
        //     that already declared a richer spec (denoise sets
        //     required:true, audio-enhancer customises onProgress, etc.).
        //   • Custom names (`dfn3`, `vocal_remover_onnx`, …) MUST be
        //     EngineWarmer.register()'d by the tool's own script at init.
        var warmList = el.dataset.warmEngines;
        if (warmList && window.EngineWarmer && typeof window.EngineWarmer.warm === 'function') {
            var wSlug = ((document.querySelector('meta[name="tool-slug"]') || {}).content) || (window.__TOOL_SLUG || '');
            if (wSlug) {
                var names = warmList.split(',').map(function(s){ return s.trim(); }).filter(Boolean);
                if (typeof window.EngineWarmer.isRegistered === 'function') {
                    for (var bi = 0; bi < names.length; bi++) {
                        var bname = names[bi];
                        if (!BUILTIN_ENGINES[bname]) continue;
                        if (window.EngineWarmer.isRegistered(wSlug, bname)) continue;
                        try { window.EngineWarmer.register(wSlug, [BUILTIN_ENGINES[bname]]); } catch(_) {}
                    }
                }
                try { window.EngineWarmer.warm(wSlug, warmList); } catch(_) {}
            }
        }
    }

    // Decode a picked file via the global AudioDecoder, then dispatch
    // `tool-uploader:decoded` (success) or `tool-uploader:decode-error`
    // (final failure). Failure also surfaces via the inline reject banner
    // so users see something actionable instead of a frozen workspace.
    // One-time stylesheet for the in-zone "processing your file" overlay.
    function ensureDecodeStyles(){
        if (document.getElementById('tu-decode-styles')) return;
        var s = document.createElement('style');
        s.id = 'tu-decode-styles';
        s.textContent =
            '.tu-decoding{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;'
            + 'background:rgba(10,15,26,.74);backdrop-filter:blur(2px);-webkit-backdrop-filter:blur(2px);border-radius:inherit;z-index:6;'
            + 'font-size:14px;color:#fff;text-align:center;padding:14px;cursor:default}'
            + '[data-theme=light] .tu-decoding{background:rgba(255,255,255,.85);color:#1a1a2e}'
            + '.tu-decoding-spin{width:30px;height:30px;border:3px solid rgba(255,255,255,.22);border-top-color:#38bdf8;border-radius:50%;animation:tuDecSpin .8s linear infinite}'
            + '[data-theme=light] .tu-decoding-spin{border-color:rgba(0,0,0,.14);border-top-color:#2563eb}'
            + '@keyframes tuDecSpin{to{transform:rotate(360deg)}}'
            + '.tu-decoding-bar{width:72%;max-width:190px;height:5px;border-radius:3px;background:rgba(255,255,255,.22);overflow:hidden;display:none}'
            + '[data-theme=light] .tu-decoding-bar{background:rgba(0,0,0,.12)}'
            + '.tu-decoding-bar.on{display:block}'
            + '.tu-decoding-fill{height:100%;width:0;background:#38bdf8;transition:width .25s}';
        (document.head || document.documentElement).appendChild(s);
    }
    // Show a determinate-or-spinner "processing" overlay on the zone while a
    // picked file decodes. Returns {update(0..1), remove()}.
    function showDecoding(el){
        ensureDecodeStyles();
        try { if (getComputedStyle(el).position === 'static') el.style.position = 'relative'; } catch(_){}
        var loc = (window.__LOCALE || 'en').substring(0, 2);
        var ov = el.querySelector(':scope > .tu-decoding');
        if (!ov) {
            ov = document.createElement('div');
            ov.className = 'tu-decoding';
            ov.setAttribute('role', 'status');
            ov.setAttribute('aria-live', 'polite');
            ov.innerHTML = '<div class="tu-decoding-spin"></div><div class="tu-decoding-txt"></div>'
                + '<div class="tu-decoding-bar"><div class="tu-decoding-fill"></div></div>';
            el.appendChild(ov);
        }
        ov.querySelector('.tu-decoding-txt').textContent = (DECODING_L[loc] || DECODING_L.en);
        ov.style.display = 'flex';
        return {
            update: function(p){
                if (p == null || isNaN(p)) return;
                var bar = ov.querySelector('.tu-decoding-bar');
                if (bar) bar.classList.add('on');
                var fill = ov.querySelector('.tu-decoding-fill');
                if (fill) fill.style.width = Math.max(0, Math.min(100, Math.round(p * 100))) + '%';
            },
            remove: function(){
                try {
                    ov.style.display = 'none';
                    var bar = ov.querySelector('.tu-decoding-bar'); if (bar) bar.classList.remove('on');
                    var fill = ov.querySelector('.tu-decoding-fill'); if (fill) fill.style.width = '0%';
                } catch(_){}
            }
        };
    }

    // The tool handed this file's sound back (detail.reuseAudio): the decoded event, with no decode.
    // Asynchronous like a real decode, so a listener sees `file` before `decoded` as always.
    function reuseAndDispatch(zoneEl, file, buffer, source){
        setTimeout(function(){
            var reused = { file: file, buffer: buffer, tier: 'reused', ms: 0, source: source };
            zoneEl.dispatchEvent(new CustomEvent('tool-uploader:decoded', { detail: reused, bubbles: true }));
            if (zoneHolds(zoneEl, file)) notePick(zoneEl, 'tool-uploader:decoded', reused);
        }, 0);
    }

    // this file's turn in the page's ONE decode queue (decodeTail, above wire())
    function zoneHolds(zoneEl, file){ var d = zoneEl.__deliveredFiles; return !!(d && d.indexOf(file) >= 0); }

    function decodeAndDispatch(zoneEl, file, source){
        var slug = ((document.querySelector('meta[name="tool-slug"]') || {}).content) || (window.__TOOL_SLUG || '');
        var ind = showDecoding(zoneEl);
        var release = null;
        decodeTail = decodeTail.then(function(){
            return new Promise(function(res){
                release = res;
                try {
                    if (!zoneHolds(zoneEl, file)) { done = true; cleanup(); return; }
                    start();
                } catch (e) { onErr(e); }
            });
        });

        // ── Integration-boundary invariant ──────────────────────────────
        // decodeAndDispatch MUST resolve to exactly one terminal event
        // (decoded | decode-error) within a bounded no-progress window — no
        // matter what robust()/the fallback/the platform does. The decoder's
        // own per-tier timeouts are setTimeout-based and get THROTTLED on iOS
        // when the page is backgrounded / busy with ad iframes, so robust()
        // can silently never settle and the tool freezes with no feedback
        // (daria.m999 2026-06-20). NOTE on scope: this guard sits at the
        // uploader boundary and therefore protects ONLY the tools that route
        // their decode through decode-audio="1" — currently just 2
        // (background-music, voice-gender). The other ~30 audio tools call
        // AudioDecoderRobust.robust() DIRECTLY and bypass this entirely; the
        // durable fix is to relocate the guard INTO robust() so all callers
        // are covered (follow-up, see reference_decode_integration_watchdog).
        var done = false;
        var STALL_MS = (window.__decodeStallMs | 0) || 45000; // overridable for tests
        var stallTimer = null;
        function armStall(){ if (stallTimer) clearTimeout(stallTimer); stallTimer = setTimeout(onStall, STALL_MS); }
        // iOS throttles/pauses setTimeout while hidden; on return to foreground
        // give the (possibly merely-suspended) decode a fresh window before we
        // declare it stalled — avoids false positives on tab-switch, still
        // bounds a genuine hang to STALL_MS after the user is looking again.
        function onVis(){ if (document.visibilityState === 'visible' && !done) armStall(); }
        function cleanup(){
            if (stallTimer) clearTimeout(stallTimer); document.removeEventListener('visibilitychange', onVis); ind.remove();
            if (release) { var next = release; release = null; next(); }   // the page's next decode may start
        }
        function emitTerminal(kind, detail){
            if (done) return; done = true; cleanup();
            zoneEl.dispatchEvent(new CustomEvent('tool-uploader:' + kind, { detail: detail, bubbles: true }));
            if (zoneHolds(zoneEl, file)) notePick(zoneEl, 'tool-uploader:' + kind, detail);
        }
        function rejectBanner(err){
            try {
                var loc = (window.__LOCALE || 'en').substring(0, 2);
                var map = (err && err.name === 'NoAudioStreamError') ? NO_AUDIO_L : DECODE_FAIL_L;
                showReject(zoneEl, (map[loc] || map.en));
            } catch(_){}
        }
        function onStall(){
            if (done) return;
            var err = new Error('decode stalled — no progress for ' + STALL_MS + 'ms');
            err.name = 'DecodeTimeoutError'; err.reason = 'decode_stall';
            rejectBanner();
            try {
                if (window.__aeToolError) window.__aeToolError({
                    error_type: 'decode_stall_watchdog', tier: -1,
                    err_msg: 'no_progress_' + STALL_MS + 'ms',
                    file_ext: (((file && file.name) || 'f').split('.').pop() || '').toLowerCase(),
                    file_type: (file && file.type) || '', file_size: (file && file.size) || 0,
                    slug: slug, _no_auto_stage: 0
                });
            } catch(_){}
            emitTerminal('decode-error', { file: file, error: err, source: source });
        }
        function onOk(r){ emitTerminal('decoded', { file: file, buffer: r.buffer, tier: r.tier, ms: r.ms, source: source }); }
        function onErr(err){ rejectBanner(err); emitTerminal('decode-error', { file: file, error: err, source: source }); }
        // progress = proof of life → reset the stall window + drive the bar.
        var onProg = function(p){ try { ind.update(p); } catch(_){} armStall(); };

        // this file's turn in the page's decode queue (above)
        function start(){
            document.addEventListener('visibilitychange', onVis);
            armStall();
            if (!window.AudioDecoderRobust || typeof window.AudioDecoderRobust.robust !== 'function') {
                // Module didn't load — plain decode-attempt. Previously this path
                // had NO timeout at all; now it's covered by the stall-watchdog too.
                try {
                    var ctx = new (window.AudioContext || window.webkitAudioContext)();
                    file.arrayBuffer()
                        .then(function(ab){ return ctx.decodeAudioData(ab); })
                        .then(function(buffer){ onOk({ buffer: buffer, tier: 0, ms: 0 }); })
                        .catch(onErr)
                        .finally(function(){ try { ctx.close(); } catch(_){} });
                } catch (e){ onErr(e); }
                return;
            }
            window.AudioDecoderRobust.robust(file, null, { slug: slug, onProgress: onProg }).then(onOk).catch(onErr);
        }
    }

    // Click — but not when the user clicked an interactive child (button, link,
    // another input, label). Tools often put a "Replace file" button inside
    // the zone, and our own .tu-label-overlay opens the picker natively, so
    // the JS path would otherwise fire the picker twice.
    el.addEventListener('click', function(e){
        var hit = e.target.closest('button, a, input, select, textarea, label');
        // Source disambiguation — pre-2026-06-07 we only tagged 'label' / 'js'
        // / 'interactive' and the catch-all 'interactive' lumped the BENIGN
        // native-label-activation case (the synthetic click that <label for=>
        // dispatches on the hidden .tu-static-input bubbles up to el) with
        // GENUINE foreign-widget hits ("Replace file" buttons, paste-handler
        // anchors). 7d /trim SQL: interactive mob:1=1539 vs label mob:1=1422
        // — so HALF of mobile clicks fall in the opaque bucket, defeating
        // upload_no_pick triage. Now we split:
        //   'label'        — clicked the .tu-label-overlay directly
        //   'native_input' — synthetic click on the hidden tu-static-input
        //                    (native label-for activation; picker IS opening)
        //   'button'/'a'/'input'/'select'/'textarea'
        //                  — a real foreign widget actually intercepted
        //   'js'           — no interactive child; our input.click() fires
        // ⚠️ Read this breakdown from `upload_no_pick_followup`, NOT from the
        // event below: AnalyticsController::DROP_FEATURE_SUBTYPES discards
        // upload_zone_clicked as volume, so it has never reached prod
        // analytics (0 rows in 14 days, checked 2026-08-29). The followup
        // event carries the same `src` and is kept.
        var src = 'js';
        if (hit) {
            if (hit.classList && hit.classList.contains('tu-label-overlay')) {
                src = 'label';
            } else if (hit.classList && hit.classList.contains('tu-static-input')) {
                src = 'native_input';
            } else {
                src = (hit.tagName || 'interactive').toLowerCase();
            }
        }
        try {
            var slug = (document.querySelector('meta[name="tool-slug"]') || {}).content || null;
            if (window.__ae) window.__ae('feature_use', { slug: slug, f: 'upload_zone_clicked', src: src });
        } catch (_) {}
        // No-followup heuristic — armed on every click that PLAUSIBLY opened
        // a picker ('label', 'native_input', 'js'). Foreign-widget hits
        // ('button', 'a', etc.) don't arm because they handled the click
        // themselves (e.g. "Replace file" button). After 30 s with the page
        // still visible, no file_picked event, and no error, we emit
        // upload_no_pick_followup so /pino can size the silent-abandon
        // cohort instead of inferring it from the click→pick rate gap.
        // The arm timer is cleared the moment a file is picked
        // (tool-uploader:file dispatch) or the page goes hidden.
        if (src === 'label' || src === 'native_input' || src === 'js') {
            armUploadNoPickFollowup(el, src);
            // The one record of this tap that outlives the page — see
            // markPickerOpen. The timer above dies with a killed renderer.
            markPickerOpen(el, src);
        }
        if (hit) return;
        lastSource = 'click';
        input.click();
    });

    // Keyboard: Enter / Space activate the zone (role="button" + tabindex on the wrapper)
    el.addEventListener('keydown', function(e){
        if ((e.key === 'Enter' || e.key === ' ') && e.target === el) {
            e.preventDefault();
            lastSource = 'keyboard';
            markPickerOpen(el, 'keyboard');
            input.click();
        }
    });

    // ── Opt-in mic recording (blade prop record-audio) ─────────────────
    // Third hand-rolled copy was the trigger (voice-enhancer, gif-audio,
    // then background-music 2026-08-13) — the concern moves into the
    // component: the blade renders .tu-record-btn, this wires it. Record →
    // MediaRecorder → File → the SAME emit() pipeline as a click-pick
    // (gates, FileBytes priming, telemetry, decode-audio), source
    // 'mic_record'. Requires the mic-manager-loader component on the page —
    // MicManager owns permissions + the localized denied/busy recovery UI;
    // re-implementing that per-tool is the divergence this kills.
    (function wireRecord(){
        var btn = el.querySelector('.tu-record-btn');
        if (!btn) return;
        // Capability gate: stay hidden where recording can't work at all.
        // MicManager may legitimately not be DEFINED yet (both scripts are
        // deferred) — checked at click-time instead.
        if (!(window.MediaRecorder && navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) return;
        btn.hidden = false;
        var lbl = btn.querySelector('.tu-record-txt') || btn;
        var idleL = btn.getAttribute('data-record-label') || lbl.textContent;
        var stopL = btn.getAttribute('data-stop-label') || 'Stop';
        var recName = btn.getAttribute('data-recording-name') || 'recording';
        var unavailMsg = btn.getAttribute('data-unavailable-msg') || '';
        var st = { mr: null, mic: null, chunks: [], t0: 0, timer: 0, wl: null };
        function slugOf(){ return ((document.querySelector('meta[name="tool-slug"]') || {}).content) || ''; }
        function tfmt(s){ var m = Math.floor(s / 60), x = s % 60; return m + ':' + (x < 10 ? '0' : '') + x; }
        function recAe(f, extra){
            try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: slugOf(), f: f }, extra || {})); } catch(_){}
        }
        function pickMime(){
            var M = window.MediaRecorder;
            if (!M || !M.isTypeSupported) return '';
            // Chromium-family records webm/opus, Safari mp4/AAC. Constructing
            // with an unsupported mimeType THROWS — probe before use.
            var c = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/ogg;codecs=opus'];
            for (var i = 0; i < c.length; i++) { try { if (M.isTypeSupported(c[i])) return c[i]; } catch(_){} }
            return '';
        }
        function tick(){ lbl.textContent = stopL + ' · ' + tfmt(Math.floor((Date.now() - st.t0) / 1000)); }
        function reset(){
            if (st.timer) { clearInterval(st.timer); st.timer = 0; }
            if (st.wl) { try { st.wl.release(); } catch(_){} st.wl = null; }
            if (st.mic) { try { st.mic.stop(); } catch(_){} }
            btn.classList.remove('rec');
            lbl.textContent = idleL;
            st.mr = null;
        }
        function unavailable(msg){
            reset();
            try { if (unavailMsg && window.__toast) window.__toast(unavailMsg, 'error'); } catch(_){}
            recAe('tu_rec_error', { msg: String(msg || '').slice(0, 120) });
        }
        function start(){
            if (!window.MicManager) {
                try { console.warn('[tool-uploader] record-audio needs <x-mic-manager-loader /> on the page'); } catch(_){}
                unavailable('no_micmanager');
                return;
            }
            if (!st.mic) st.mic = new MicManager({ locale: window.__LOCALE });
            st.mic.start().then(function(stream){
                var mime = pickMime(), mr;
                try { mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
                catch(e){
                    try { mr = new MediaRecorder(stream); }
                    catch(e2){ unavailable(e2 && e2.message); return; }
                }
                st.mr = mr; st.chunks = [];
                mr.ondataavailable = function(ev){ if (ev.data && ev.data.size) st.chunks.push(ev.data); };
                mr.onstop = function(){
                    var type = (mr.mimeType || mime || 'audio/webm').split(';')[0];
                    var ext = /mp4/.test(type) ? 'm4a' : (/ogg/.test(type) ? 'ogg' : 'webm');
                    var sec = Math.round((Date.now() - st.t0) / 1000);
                    var blob = new Blob(st.chunks, { type: type });
                    reset();
                    if (!blob.size) { recAe('tu_rec_error', { msg: 'empty_blob' }); return; }
                    recAe('tu_rec_done', { sec: sec, mime: type });
                    emit([new File([blob], recName + '.' + ext, { type: type })], 'mic_record');
                };
                // 250 ms timeslice: a tab killed mid-take still leaves the
                // already-delivered chunks usable instead of zero bytes.
                mr.start(250);
                st.t0 = Date.now();
                btn.classList.add('rec');
                tick(); st.timer = setInterval(tick, 500);
                recAe('tu_rec_start');
                // Screen-off on phones kills the take; keep the screen awake
                // while recording when the shared helper is present.
                if (window.RecordingUtils && window.RecordingUtils.acquireWakeLock) {
                    try { window.RecordingUtils.acquireWakeLock(slugOf() || 'uploader').then(function(wl){ st.wl = wl; }).catch(function(){}); } catch(_){}
                }
            }).catch(function(e){
                // MicManager already showed its localized recovery modal for
                // the denied/busy/not-found classes — telemetry only here.
                recAe('tu_rec_error', { msg: String(e && (e.code || e.message) || e).slice(0, 120) });
            });
        }
        btn.addEventListener('click', function(ev){
            ev.stopPropagation();
            if (st.mr && st.mr.state === 'recording') { try { st.mr.stop(); } catch(_){ reset(); } }
            else start();
        });
    })();

    // ── «Take a photo»: the dedicated-camera door (blade prop take-photo, el.__uploaderOpenCamera) ──
    // The phone's own camera app, opened through a SEPARATE input: the zone's input
    // never carries `capture` (on iOS it hides the photo library — CaptureAttributeGuardTest).
    // Owned here like the mic-record button above: five tools had hand-rolled a
    // capture input that fed their own loader past the zone (no HEIC lane, no size
    // gate, no pick bookkeeping). Review #1960 (21.10.2026, /barcode-scanner, Samsung
    // Internet): the browser refused the live camera and the person went to the camera
    // app on their own — the tab came back reloaded and the photo had to be found in
    // the gallery. The camera app needs no site permission; opened from here, a tab
    // the system kills while it is open is explained on the next load (markPickerOpen).
    // Button only on a touch screen: a desktop browser ignores `capture` and would open
    // a file dialog under a camera label.
    var camInput = null;
    function openCamera(src){
        if (!camInput) {
            camInput = document.createElement('input');
            camInput.type = 'file';
            var imgAccept = String(input.getAttribute('accept') || '').split(',')
                .map(function(a){ return a.trim(); }).filter(function(a){ return /^image\//i.test(a); }).join(',');
            camInput.setAttribute('accept', imgAccept || 'image/*');
            camInput.setAttribute('capture', 'environment');
            camInput.hidden = true;
            camInput.tabIndex = -1;
            camInput.setAttribute('aria-hidden', 'true');
            camInput.addEventListener('change', function(){
                clearPickerOpen();
                var f = camInput.files && camInput.files[0];
                if (f) emit([f], 'camera');
                try { camInput.value = ''; } catch(_){}
            });
            camInput.addEventListener('cancel', function(){ clearPickerOpen(); });
            document.body.appendChild(camInput);
        }
        markPickerOpen(el, 'camera');
        try {
            if (window.__ae) window.__ae('feature_use', { slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null, f: 'tu_camera_open', src: src || 'tool' });
        } catch(_){}
        camInput.click();
    }
    el.__uploaderOpenCamera = function(src){ openCamera(src); };

    // ── «Pick through Files»: the document-picker door (el.__uploaderOpenFiles) ──
    // On Android a zone whose accept is image-only opens the system PHOTO picker,
    // and that picker hands the page a copy with the location zeroed — always:
    // AOSP MediaProvider forces redaction on picker URIs, and Chrome holds no
    // ACCESS_MEDIA_LOCATION to ask for the original. A tool whose whole job is
    // that location (/photo-location: camera originals carried GPS in 2–3 of 60–66
    // Android sessions against 21–38 of 32–77 on Windows, 30 days to 28.10.2026)
    // needs a way past it: the same zone opened with accept "*/*", which Chrome
    // routes to the ordinary chooser and the Files app — the document providers
    // open the file under their own identity. A SEPARATE input, like the camera
    // door, so the zone's own picker stays the photo grid; the file goes through
    // the same emit (accept check, size gate, priming) and the same "picker open"
    // record a reload is explained from.
    var filesInput = null;
    function openFiles(src){
        if (!filesInput) {
            filesInput = document.createElement('input');
            filesInput.type = 'file';
            filesInput.setAttribute('accept', '*/*');
            if (input.multiple) filesInput.multiple = true;
            filesInput.hidden = true;
            filesInput.tabIndex = -1;
            filesInput.setAttribute('aria-hidden', 'true');
            filesInput.addEventListener('change', function(){
                clearPickerOpen();
                var fl = filesInput.files;
                if (fl && fl.length) emit(Array.prototype.slice.call(fl), 'files');
                try { filesInput.value = ''; } catch(_){}
            });
            filesInput.addEventListener('cancel', function(){ clearPickerOpen(); });
            document.body.appendChild(filesInput);
        }
        markPickerOpen(el, 'files');
        try {
            if (window.__ae) window.__ae('feature_use', { slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null, f: 'tu_files_open', src: src || 'tool' });
        } catch(_){}
        filesInput.click();
    }
    el.__uploaderOpenFiles = function(src){ openFiles(src); };
    (function wirePhoto(){
        var btn = el.querySelector('.tu-photo-btn');
        if (!btn) return;
        var touch = false;
        try { touch = window.matchMedia('(pointer: coarse)').matches; } catch(_){}
        if (!touch) return;
        btn.hidden = false;
        btn.addEventListener('click', function(ev){
            ev.preventDefault();
            ev.stopPropagation();
            openCamera('zone_button');
        });
    })();

    // Dragenter/leave counter avoids flicker when the cursor crosses a child element
    var dragCounter = 0;
    el.addEventListener('dragenter', function(e){
        e.preventDefault();
        dragCounter++;
        el.classList.add('dragover');
    });
    el.addEventListener('dragover', function(e){
        e.preventDefault();
    });
    el.addEventListener('dragleave', function(e){
        e.preventDefault();
        dragCounter--;
        if (dragCounter <= 0) { dragCounter = 0; el.classList.remove('dragover'); }
    });
    // Walk a dropped folder into the files the tool can actually use. Bounded
    // by __folderDrop.walk; whatever it could not take is said out loud rather
    // than silently dropped.
    function handleFolderDrop(snap){
        var t0 = Date.now();
        window.__folderDrop.walk(snap.dirs, {}).then(function(res){
            var pool = (snap.loose || []).concat(res.files);
            var fit = pool.filter(function(f){ return f.size > 0 && matchesAccept(f, accept); });
            var outcome = !res.seen ? 'empty'
                : !fit.length ? 'no_match'
                    : (!multiple && fit.length > 1) ? 'pick_one'
                        : 'ingested';
            try {
                if (typeof window.__ae === 'function') {
                    window.__ae('feature_use', {
                        slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
                        f: 'folder_drop', out: outcome, n: fit.length, seen: res.seen,
                        trunc: res.truncated ? 1 : 0, multi: multiple ? 1 : 0, ms: Date.now() - t0
                    });
                }
            } catch (_) {}
            if (outcome === 'empty')    { rejectFolder(el, 'folder_empty', 0, res.names); return; }
            if (outcome === 'no_match') { rejectFolder(el, 'folder_no_match', 0, res.names); return; }
            if (outcome === 'pick_one') { rejectFolder(el, 'folder_pick_one', fit.length, res.names); return; }
            hideReject(el);
            var take = multiple ? fit : [fit[0]];
            if (res.truncated && typeof window.__toast === 'function') {
                window.__toast(window.__folderDrop.msg('folder_partial', take.length), 'warning');
            }
            emit(take, 'drop');
        }).catch(function(){ rejectFolder(el, 'folder_no_match', 0, snap.dirs.map(function(d){ return d.name; })); });
    }
    el.addEventListener('drop', function(e){
        e.preventDefault();
        dragCounter = 0;
        el.classList.remove('dragover');
        var dt = e.dataTransfer;
        if (!dt || !dt.files) return;
        lastSource = 'drop';
        // Read the entry tree SYNCHRONOUSLY — dataTransfer.items is neutered
        // the moment this handler returns, and it is the only thing that tells
        // a folder from a 0-byte file. allow-empty zones own their own folder
        // handling (archive-creator walks the tree itself into a nested
        // archive); taking the drop from them would double-add.
        var snap = null;
        if (!allowEmpty && window.__folderDrop) {
            try { snap = window.__folderDrop.snapshot(dt); } catch (_) { snap = null; }
        }
        if (snap) { handleFolderDrop(snap); return; }
        emit(dt.files);
    });

    input.addEventListener('change', function(e){
        emit(e.target.files);
    });
    // A file landed by any route (picker, drop, paste, inject) — the help is
    // no longer relevant, so it goes away instead of nagging.
    el.addEventListener('tool-uploader:file', function(){ hidePickHelp(el); });
    // Direct picker-dismiss signal (Safari 16.4+ / Chrome 113+). The 30 s
    // heuristic above stays as the fallback for older engines, but where
    // `cancel` fires we get an INSTANT, unambiguous event — no 30 s wait
    // (which undercounts users who leave the page sooner) and no
    // visibility/blur inference. state:'cancelled' is the precise bucket;
    // 'dismissed' remains the heuristic one. Cohort scale (14 d, Beget):
    // 7785 dismissed / 6226 sids — the biggest silent-abandon signal on
    // file tools, first made user-visible by the add-music-to-video
    // streaming-DRM hint (#824).
    if ('oncancel' in input) {
        input.addEventListener('cancel', function(){
            var wasArmed = !!el.__uploadNoPickTimer;
            var waitedS = pickerWaitS(el);
            el.__pickOpenAt = 0;
            disarmUploadNoPickFollowup(el);
            // Dismissed by the user — the picker demonstrably came back.
            clearPickerOpen('cancel');
            // Neither a tracked open nor one whose 30 s verdict already went
            // out: a cancel on a stale dialog (Esc), nothing to answer.
            if (!wasArmed && waitedS === null) return;
            // The user went to the picker and came back with nothing. Say
            // where the file actually lives and offer the gallery door.
            //
            // ⚠️ A cancel AFTER the 30 s verdict used to end here in silence:
            // the timer had fired, so `wasArmed` was false. On an iPhone that
            // is exactly the person who waited while the phone prepared a
            // video and gave up (the 2026-10-09 report: three such waits on
            // two tools, no help ever shown). It is answered now, and counted
            // as its own state so 'cancelled' keeps its old meaning.
            showPickHelp(el, wasArmed ? 'cancel' : 'cancel_late', waitedS);
            var ev = {
                slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
                f: 'upload_no_pick_followup',
                src: 'cancel_event',
                state: wasArmed ? 'cancelled' : 'cancelled_late',
            };
            if (waitedS !== null) ev.waited_s = waitedS;
            try { if (window.__ae) window.__ae('feature_use', ev); } catch (_) {}
        });
    }

    // Paste handler — pulls files/images out of the clipboard. Useful for
    // screenshot → image tool workflows and audio clips from voice recorders.
    // Only active when the uploader is focused OR the user pasted on body
    // while no other input owns focus.
    function handlePaste(ev){
        var cd = ev.clipboardData;
        if (!cd) return;
        var out = [];
        if (cd.files && cd.files.length) {
            for (var i = 0; i < cd.files.length; i++) out.push(cd.files[i]);
        } else if (cd.items) {
            for (var j = 0; j < cd.items.length; j++) {
                var it = cd.items[j];
                if (it.kind === 'file') {
                    var f = it.getAsFile();
                    if (f) out.push(f);
                }
            }
        }
        if (!out.length) return;
        ev.preventDefault();
        lastSource = 'paste';
        emit(out);
    }
    el.addEventListener('paste', handlePaste);
    // Body-level: only accept paste when focus is on a non-editable element.
    // Otherwise pasting into a textarea would hijack the user's typing.
    //
    // ⚠️ The old rule «only the zone that is on screen takes the paste» had a
    // hole a paying user fell straight into (Sergey, 2026-08-21 13:40 «хотел
    // 40 фото вставить, но реакции — ноль»): on a tool whose workspace grows
    // (slideshow deck), the zone scrolls ABOVE the viewport and Ctrl+V died
    // silently — on a page with exactly ONE uploader. The rule's real job is
    // only to stop double-handling on MULTI-zone pages, so: the first VISIBLE
    // zone wins; when NO zone is visible, the first registered one takes it.
    __tuPasteZones.push(el);
    document.addEventListener('paste', function(ev){
        if (ev.target && (ev.target.isContentEditable ||
            /^(INPUT|TEXTAREA|SELECT)$/.test(ev.target.tagName || ''))) return;
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var winner = null;
        for (var zi = 0; zi < __tuPasteZones.length; zi++) {
            var z = __tuPasteZones[zi];
            if (!z.isConnected) continue;
            if (!winner) winner = z; // fallback: first live zone
            var zr = z.getBoundingClientRect();
            if (zr.bottom >= 0 && zr.top <= vh && (zr.width || zr.height)) { winner = z; break; }
        }
        if (winner !== el) return;
        handlePaste(ev);
    });

    // Tool-facing API — call these on the element for explicit control.
    el.__uploaderReset = function(){ input.value = ''; hideReject(el); };

    /* ⚠️ Тул, закрывающий файл своим крестиком, снимает СВОЙ класс состояния
       (`collapsed`) и зовёт __uploaderReset — а тот чистит только input. Класс
       компонента `tu-collapsed` при этом остаётся, и строка «файл · Другой
       файл · ×» РАЗВОРАЧИВАЕТСЯ на месте только что снятого файла: человек
       нажал ×, а на экране снова имя файла, которого уже нет.

       Замерено 2026-09-03 на /silence-remover: до нажатия строка 0×0 (свёрнута
       родителем), после — 872×37. То же на /ocr-text-extractor. Такая связка
       (общий аплоадер + свой крестик тула) у 126 тулов, поэтому правка ЗДЕСЬ, а
       не 67 правок в блейдах.

       Наблюдаем за собственной зоной: тул вернул её в исходный вид — значит
       файла больше нет, и строка компонента обязана уйти вместе с ним.
       restoreZone(el, true) — молча: событие tool-uploader:clear принадлежит
       крестику КОМПОНЕНТА, а здесь закрытие уже сделал сам тул, и повторное
       событие сбросило бы его состояние второй раз. */
    if (typeof MutationObserver === 'function') {
        var tuLastCollapsed = el.classList.contains('collapsed');
        new MutationObserver(function () {
            var nowCollapsed = el.classList.contains('collapsed');
            if (tuLastCollapsed && !nowCollapsed && el.classList.contains('tu-collapsed')) {
                restoreZone(el, true);
            }
            tuLastCollapsed = nowCollapsed;
        }).observe(el, { attributes: true, attributeFilter: ['class'] });
    }
    el.__uploaderOpen  = function(){ input.click(); };
    el.__uploaderInput = input;  // in case the tool needs raw access
    /* Programmatic file injection — the canonical entry point for share-target
       receivers, inter-tool routing ("send result to <next tool>"), Playwright
       E2E tests, and any future flow that produces a File outside the OS picker.
       Runs the SAME validation / FileBytes priming / telemetry / event dispatch
       pipeline as click & drop. Without this, callers either mutate
       input.files (hard from the outside) or implement their own ingest path
       and bypass the shared gates — which is what convert.blade.php and
       photo-markup currently do for share-target. */
    el.__uploaderInjectFile  = function(file, source){ if (file) emit([file], source || 'programmatic'); };
    el.__uploaderInjectFiles = function(files, source){
        if (!files || !files.length) return;
        var arr = Array.prototype.slice.call(files);
        emit(arr, source || 'programmatic');
    };
    // Tools call this from their own reject paths (decode-fail, codec-unsupported,
    // FFmpegMemoryGuard, empty file, etc.) so the inline banner + typed telemetry
    // are consistent with the component's own size/MIME gates. reason is a
    // ToolErrors.REASONS.* token; if omitted the banner shows but no __reject
    // telemetry fires (banner-only legacy behaviour).
    el.__uploaderReject = function(msg, reason){
        showReject(el, msg);
        if (reason && typeof window.__reject === 'function') {
            window.__reject(msg, reason);
        }
    };
    el.__uploaderHideReject = function(){ hideReject(el); };
    // «The file goes here» — for a press elsewhere on the page that could not
    // work without a file (the intl showcase answers a dead Play / Save that
    // way, layouts/app). The zone's own line says why, in the hint's colours
    // rather than a refusal's, scrolls into view (`nearest` — the only scroll
    // the site allows, and only after the person pressed something) and shakes.
    // The next file hides it like any banner.
    el.__uploaderPointAt = function(msg){
        if (!msg) return;
        showReject(el, msg);
        var banner = el.querySelector('.tu-reject');
        if (banner) banner.classList.add('tu-hint');
        // The whole zone, not only its line: scrolled to the line alone, the
        // zone's own «Choose file» button stayed above the window's edge (/trim,
        // 15.10.2026: the hint at 64 px, the button cut off under the «Get
        // Premium» bar). Overrides the banner's own scroll inside showReject.
        var reduce = false;
        try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) {}
        try { el.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' }); } catch (_) {}
    };
    // A tool whose own type check refuses a video the zone let through (the
    // iPhone picker's widened accept, above) hands the refused files here
    // instead of toasting «select an audio file»: the zone names the tool that
    // pulls the sound out and carries the video there. Returns how many of the
    // files were videos it answered for — 0 means the zone has no such answer
    // (or none of them is a video) and the tool says its own piece.
    // `partial`: the tool is going ahead with other files of the same pick.
    el.__uploaderVideoSignpost = function(files, partial){
        if (!videoSignpostReady(el)) return 0;
        var list = files && typeof files.length === 'number' && !(files instanceof Blob) ? Array.prototype.slice.call(files) : [files];
        var videos = videosForSignpost(list, el.dataset.accept || '');
        if (!videos.length) return 0;
        rejectVideo(el, videos, 'tool', !!partial);
        return videos.length;
    };
    // Public handles for the picked-file row, so a tool that manages its own
    // reset ("Start over" button) puts the zone back through the same path the
    // × does, instead of poking classes.
    el.__uploaderCollapse = function(files){ collapseZone(el, files || []); };
    el.__uploaderRestore  = function(silent){ restoreZone(el, silent === true); };

    // Row controls. Wired once at init; the row itself may be hidden for the
    // whole life of the page (tool opted out, or no file ever picked).
    // Seeded here, not looked up later: keepRowReachable() may move this row
    // out of the zone, after which a querySelector inside the zone finds
    // nothing. (Renamed from `pickedRow` — that name is the module-level
    // accessor now, and a local of the same name would shadow it.)
    var rowEl = el.__tuRow = el.querySelector('.tu-picked');
    if (rowEl) {
        var changeBtn = rowEl.querySelector('.tu-picked-change');
        var removeBtn = rowEl.querySelector('.tu-picked-remove');
        if (changeBtn) changeBtn.addEventListener('click', function(e){
            // The zone is role=button and drag/drop-wired; without stopping here
            // the click bubbles to the zone's own open-picker handler and fires
            // the OS dialog twice.
            e.preventDefault();
            e.stopPropagation();
            // Do NOT restore the zone first: if the user cancels the OS dialog we
            // must still be showing the file they already have. The next accepted
            // pick overwrites the row through collapseZone.
            input.click();
        });
        if (removeBtn) removeBtn.addEventListener('click', function(e){
            e.preventDefault();
            e.stopPropagation();
            input.value = '';
            restoreZone(el, false);
            // Give focus somewhere real — the button the user just clicked is now
            // display:none, and a focused-then-hidden element drops focus to
            // <body>, which strands keyboard users at the top of the document.
            try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
        });
    }

    // Drain picks that landed BEFORE this script hydrated the zone. The
    // static <input> + <label for> pair opens the OS picker with zero JS,
    // so on slow connections (3G: ~1 MB of JS gates hydration, 20-60 s)
    // users legitimately pick files into an unwired zone. Two sources:
    //   1. window.__pendingFile — stashed by the layout's inline
    //      early-capture when change fired on an unwired zone.
    //   2. input.files — a pick the early-capture missed entirely (inline
    //      block stripped by a proxy, or not yet parsed on brutally slow
    //      HTML streaming).
    // This drain OWNS the rescue for shared-uploader zones; the layout's
    // replay ladder only dispatches for legacy (non-.tool-uploader) pages
    // and otherwise acts as a watchdog that surfaces an honest error toast
    // when the uploader never arrives. Feedback 2026-07-01 (fr / Android
    // PWA / 3G "Refus de charger les pistes"): pick evaporated silently.
    //
    // Re-entrant since 2026-07-09 (ru/pitch "выбираю аудио, а оно не
    // появляется"): the boot-time one-shot left timing windows — a stash
    // landing between replay-ladder rungs waited seconds for the next rung,
    // and a stash after the one-shot ran was never drained at all. The
    // layout's inline capture now dispatches `timbrica:prewire-pick` at
    // stash time; we drain on that signal too. `__drainBusy` (replacing the
    // one-shot `__drainDone`) makes repeat drains safe: each run consumes
    // the stash exactly once, and an empty stash is a no-op.
    /* ⚠️ Sentry TIMBRICA-FRONTEND-AC: «FileBytes is not defined» в addFile
       тула. Страховка лейаута ставит фасад на DOMContentLoaded — а ранний
       выбор файла сливается ЗДЕСЬ, при гидрации (defer-скрипт идёт РАНЬШЕ
       DOMContentLoaded). Если file-buffer-cache.js не доехал, первый же файл
       уходит в тул до того, как страховка успела сработать. Гарантия обязана
       стоять там, где файл впервые отдаётся, — то есть тут. Тот же фасад, что
       в лейауте (без кэша и FILE_LOST-защиты, но читающий); idempotent. */
    if (!window.FileBytes) {
        (function(){
            function _read(file){
                if (file && typeof file.arrayBuffer === 'function') return file.arrayBuffer().then(function(ab){ return new Uint8Array(ab); });
                return new Promise(function(res, rej){ var r = new FileReader(); r.onload = function(){ res(new Uint8Array(r.result)); }; r.onerror = function(){ rej(r.error || new Error('read failed')); }; r.readAsArrayBuffer(file); });
            }
            function _ab(file){ return _read(file).then(function(b){ return b.buffer.slice(b.byteOffset, b.byteOffset + b.byteLength); }); }
            window.FileBytes = { read: _read, arrayBuffer: _ab, prime: function(){ return Promise.resolve(); }, spill: function(){}, settle: function(f){ return Promise.resolve(f); }, __facade: true };
            try { window.__ae && window.__ae('feature_use', { f: 'filebytes_facade_at_hydration', slug: (window.__TOOL_SLUG || '') }); } catch(_){}
        })();
    }

    function drainPending(){
        if (el.__drainBusy) return;
        el.__drainBusy = true;
        try {
            var pending = window.__pendingFile;
            // Ownership: the layout stamps the zone whose input took the pick
            // (window.__pendingFileZone). A stash that belongs to ANOTHER
            // zone on this page is not ours — on a two-zone page the first
            // zone to wire used to swallow the second zone's pick and reject
            // it as the wrong kind (/gif-audio, /archive-creator; measured
            // 2026-09-05). No stamp (a stash made by an older inline capture)
            // keeps the first-come rule.
            var owner = window.__pendingFileZone;
            if (pending && !(owner && owner !== el && owner.isConnected)) {
                /* The whole pick. The stash used to be files[0] alone, so a
                   multi-file pick made before this zone wired reached the
                   tool as ONE file — RU 15–29.10: 0 of 95 rescued picks on
                   /join, /merge-images, /slideshow-maker carried more than
                   one, against 34–43 % of their ordinary clicks. The layout
                   now stashes the list beside the first file; the input
                   itself still holds a click's list if an older layout made
                   the stash. emit() keeps only the first on a single zone. */
                var whole = [pending];
                try { whole = pendingPickList(pending, window.__pendingFiles, input.files); } catch (_) {}
                window.__pendingFile = null;
                window.__pendingFiles = null;
                window.__pendingFileZone = null;
                // The stashed File usually still sits in our own static
                // <input>: clear it so the branch below cannot deliver it twice.
                try { if (input.files && input.files[0] === pending) input.value = ''; } catch (_) {}
                emit(whole, 'pending_rescue');
                return;
            }
            if (input.files && input.files.length) {
                emit(input.files, 'pending_rescue');
                // Same reason: a later drain (another zone's prewire-pick)
                // must not find this pick a second time. emit() has already
                // copied the File objects out of the list.
                try { input.value = ''; } catch (_) {}
            }
        } finally {
            el.__drainBusy = false;
        }
    }
    setTimeout(drainPending, 0);
    document.addEventListener('timbrica:prewire-pick', function(){
        // setTimeout(0): the inline capture dispatches this synchronously
        // from inside the input's change event — deferring lets the capture
        // finish its own bookkeeping (zone note, telemetry) first, and lets
        // the uploader's own target-phase change listener (which handles the
        // WIRED case) run before the drain checks for leftovers.
        setTimeout(drainPending, 0);
    });
}

// Localized "до N МБ" / "Max N MB" rendering for the auto-injected size hint.
// Reads from window.__effectiveFileLimit so the number honors the device's RAM
// and stays in sync with the universal DOM scanner in app.blade.php.
// The cap is PER FILE. On an uploader that takes several files at once the bare
// number reads as a budget for the whole batch — user 3450 (2026-07-30) loaded
// 8 tracks into /music-mastering, saw "до 500 МБ" (his 2 GB device × 250) and
// asked us to widen "the limit", while his largest file was 89 MB and nothing
// was ever refused. Only appended where the input actually accepts multiple
// files; on single-file tools it would be noise.
var SIZE_PER_FILE = {
    en: 'per file', ru: 'на файл', uk: 'на файл', ko: '파일당', ar: 'لكل ملف', id: 'per berkas',
    pt: 'por arquivo', de: 'pro Datei', es: 'por archivo', fr: 'par fichier',
};
// ⚠️ Единица — ТОЖЕ перевод, и до 16.09.2026 она знала только ru и ar: тернарник
// `(locale==='ru') ? 'ГБ' : (locale==='ar' ? 'غ.ب' : 'GB')` отдавал английское
// «GB» украинцу («до 4 GB на файл» — half-translated) и французу, хотя отказ
// ТОЙ ЖЕ зоны по-французски уже говорит «Mo» (REJECT_L выше). Пропуск нашёлся
// не глазами: сторож InlineLocaleLabelCompletenessTest сканирует public/js/**,
// а этот тернарник картой не выглядел. Теперь это карта — и она под сторожем.
var SIZE_UNIT_GB = {
    en: 'GB', ru: 'ГБ', uk: 'ГБ', ko: 'GB', ar: 'غ.ب', id: 'GB',
    pt: 'GB', de: 'GB', es: 'GB', fr: 'Go',
};
var SIZE_UNIT_MB = {
    en: 'MB', ru: 'МБ', uk: 'МБ', ko: 'MB', ar: 'م.ب', id: 'MB',
    pt: 'MB', de: 'MB', es: 'MB', fr: 'Mo',
};
// The kind nouns for a zone that prints TWO numbers (data-kind-video):
// «фото до 100 МБ · видео до 4 ГБ на файл». Keys are the size kinds a mixed
// zone can carry (image/audio/video) plus `file` for anything else.
var KIND_NOUN = {
    en: { image: 'photos', audio: 'audio', video: 'video', file: 'files' },
    ru: { image: 'фото', audio: 'аудио', video: 'видео', file: 'файлы' },
    uk: { image: 'фото', audio: 'аудіо', video: 'відео', file: 'файли' },
    ko: { image: '사진', audio: '오디오', video: '동영상', file: '파일' },
    ar: { image: 'الصور', audio: 'الصوت', video: 'الفيديو', file: 'الملفات' },
    id: { image: 'foto', audio: 'audio', video: 'video', file: 'berkas' },
    pt: { image: 'fotos', audio: 'áudio', video: 'vídeo', file: 'arquivos' },
    de: { image: 'Fotos', audio: 'Audio', video: 'Video', file: 'Dateien' },
    es: { image: 'fotos', audio: 'audio', video: 'video', file: 'archivos' },
    fr: { image: 'photos', audio: 'audio', video: 'vidéo', file: 'fichiers' },
};
var SIZE_LABEL = {
    en: function(n, unit){ return 'up to ' + n + ' ' + unit; },
    ru: function(n, unit){ return 'до ' + n + ' ' + unit; },
    uk: function(n, unit){ return 'до ' + n + ' ' + unit; },
    ko: function(n, unit){ return '최대 ' + n + ' ' + unit; },
    ar: function(n, unit){ return 'حتى ' + n + ' ' + unit; },
    id: function(n, unit){ return 'hingga ' + n + ' ' + unit; },
    pt: function(n, unit){ return 'até ' + n + ' ' + unit; },
    de: function(n, unit){ return 'bis zu ' + n + ' ' + unit; },
    es: function(n, unit){ return 'hasta ' + n + ' ' + unit; },
    fr: function(n, unit){ return 'jusqu\'à ' + n + ' ' + unit; },
};

/* force=true clears already-rendered lines first: a tool that publishes
   __TOOL_MAX_MB after DOMContentLoaded needs the stale number replaced, and
   renderSizeHints deliberately skips spans that already have text. */
window.__renderSizeHints = function(force){
    try {
        if (force) document.querySelectorAll('.tu-size-hint').forEach(function(s){ s.textContent = ''; });
        renderSizeHints();
    } catch(_){}
};
function renderSizeHints(){
    var locale = (window.__LOCALE || 'en').substring(0, 2);
    var label = SIZE_LABEL[locale] || SIZE_LABEL.en;
    document.querySelectorAll('.tu-size-hint').forEach(function(span){
        if (span.textContent.trim()) return; // already rendered
        var kind = span.dataset.kind || 'audio';
        // A tool that declares a hard `max-mb` has a SERVER rule behind it, and that rule
        // wins over the device-memory budget. Printing the budget instead made
        // /voice-cover advertise "до 1 ГБ" beside its own "до 40 МБ": a phone user picked a
        // 75 MB WAV, uploaded all of it, and the server refused it (2026-07-09).
        var zone = span.closest('.tool-uploader');
        var hard = zone && zone.dataset.maxMb;
        var bytes;
        if (hard && hard !== 'auto' && parseInt(hard, 10) > 0) {
            bytes = parseInt(hard, 10) * 1048576;
        } else if (window.__effectiveFileLimit) {
            bytes = window.__effectiveFileLimit(kind);
        }
        /* A tool that computes its OWN ceiling publishes it as __TOOL_MAX_MB.
           Precedence has to mirror what actually gates the pick, or the line
           lies in one direction or the other:
             max-mb="N"  → N wins (a server rule; already handled above);
             max-mb="auto" → min(device, tool), which is what wire() enforces;
             max-mb="0"  → the ZONE enforces nothing, so the tool's number IS
                           the limit — taking the device budget here is how
                           /video-compressor came to advertise «до 1 ГБ» while
                           its own gate allows 2-4 GB and refuses with a
                           different number (measured 2026-08-24). */
        if (typeof window.__TOOL_MAX_MB === 'number' && window.__TOOL_MAX_MB > 0) {
            var toolBytes = window.__TOOL_MAX_MB * 1048576;
            bytes = (hard === '0' || !bytes) ? toolBytes : Math.min(bytes, toolBytes);
        }
        if (!bytes) return;
        function fmtLimit(b){
            var mbv = Math.round(b / 1048576);
            if (mbv >= 1024 && (b % 1073741824) === 0) return label(Math.round(b / 1073741824), SIZE_UNIT_GB[locale] || SIZE_UNIT_GB.en);
            return label(mbv, SIZE_UNIT_MB[locale] || SIZE_UNIT_MB.en);
        }
        var text = fmtLimit(bytes);
        /* Two kinds of file, two numbers — «фото до 100 МБ · видео до 4 ГБ».
           The line has to say what the gate does (capMbFor in wire()), or it
           lies for one of the two kinds. */
        var kindVideo = span.dataset.kindVideo || '';
        var kindAudio = span.dataset.kindAudio || '';
        if ((kindVideo || kindAudio) && (!hard || hard === 'auto') && window.__effectiveFileLimit) {
            var nouns = KIND_NOUN[locale] || KIND_NOUN.en;
            var parts = [(nouns[kind] || nouns.file) + ' ' + text];
            [[kindVideo, nouns.video], [kindAudio, nouns.audio]].forEach(function (kv) {
                if (!kv[0]) return;
                var kb = window.__effectiveFileLimit(kv[0]);
                if (typeof window.__TOOL_MAX_MB === 'number' && window.__TOOL_MAX_MB > 0) kb = Math.min(kb, window.__TOOL_MAX_MB * 1048576);
                if (kb && kb !== bytes) parts.push(kv[1] + ' ' + fmtLimit(kb));
            });
            if (parts.length > 1) text = parts.join(' · ');
        }
        var input = zone && zone.querySelector('input[type="file"]');
        if (input && input.multiple && SIZE_PER_FILE[locale]) text += ' ' + SIZE_PER_FILE[locale];
        span.textContent = text;
    });
}


// ── Picker-came-back-empty help ────────────────────────────────────
// The single biggest silent failure on file tools. Measured on prod
// (7 d, Beget): ~591 sessions/day open the OS picker, cancel, and never
// get a file in; 20143 sessions abandoned with had_file:0 after spending
// 1 MINUTE OR MORE with the page hidden — i.e. hunting inside the picker,
// not changing their mind. Bug report #5388 (2026-08-19) is one of them:
// an iPhone user who had just paid for Premium, tried enhance-video,
// video-trim and reels-maker, cancelled seven times, saw nothing from us,
// and wrote "Не загружается видео".
//
// We already HAD the signal — `cancel` fires, and the abandon event even
// carries hidden_ms — we simply never told the user anything.
//
// Why help text ALONE is not enough (measured, don't repeat this):
// add-music-to-video has shipped a 2-cancel hint since #824. Sessions that
// saw it went on to pick a file 99.7% of the time — and sessions that
// cancelled WITHOUT seeing it, 99.5%. It moves nothing, because on that
// tool a cancel is a normal step rather than a dead end. So this block
// does two things instead: it says where the file actually lives, AND
// offers a second door — an input whose accept is the BARE MIME wildcard.
//
// The bare wildcard is the load-bearing part on phones. Our accept lists
// carry explicit extensions (BaseHandler::FILE_ACCEPT_*, plus the iOS
// widener in the layout) because Files.app greys out .ogg/.flac/.opus
// without them. But videos and photos on a phone do not live in Files —
// they live in the photo library, whose assets have no extension to
// match. Measured over 7 d, cancels per successful pick: iOS video tools
// 0.195 vs 0.106 on Android (1.84× worse), audio tools 0.284 vs 0.214
// (1.33×) — the gap is widest exactly where the gallery is the source.
// We do NOT strip extensions from the main input: that would break the
// Files.app case the widener was added for, and the gallery theory is
// plausible but unproven without a physical device. The second door costs
// nothing and works either way.
// ⚠️ 10.10.2026: for iPhones the theory is now settled AGAINST — WebKit's
// WKFileUploadPanel gives any image/video accept the same menu and the same
// photo picker, extensions only add types to «Files». The iPhone gap is the
// phone preparing a video inside the picker (see PICK_PREP_WAIT_S). On Android
// the bare wildcard does change the picker, so the door stays.
/* Copy for the "picker came back empty" help box.
   Split by PLATFORM, not just locale. Until 2026-08-29 there was one set of
   strings and it was written for iPhones — it named iCloud, Apple Music and the
   iOS «Photos»/«Files» apps — while the box is shown on Android too. Measured
   over 14 prod days: 4788 Android sessions saw iOS instructions (vs 3302 iOS).
   A Redmi owner reported it as «сообщение об ошибке» in bug report 2026-08-28,
   and they were right: for them the text named apps that do not exist.
   ⚠️ Fixing the wording is a CORRECTNESS fix, not a conversion one — the button
   was already used at 8.1% on Android vs 7.6% on iOS, i.e. the wrong platform
   text did NOT measurably depress engagement. Do not expect the rate to move.
   The advice follows the rule measured in reference_android_picked_file_can_be
   _unreadable: tell the user how to make the file LOCAL, never «try another
   file» — their next file is the same cloud item.
   Documents (`f`) got platform copy on 2026-10-08 (comment #1818, «не видно
   папку загрузки»). The system picker opens on «Recent» the first time an app
   asks for a file, and Downloads sits behind ☰ (Android) or «Browse» (iOS);
   after one pick it remembers the folder per app — reproduced on an Android 14
   emulator in Chrome and in a WebView host. That first time is exactly the
   in-app browser case (a messenger that never opened a picker before). 30 d,
   RU, document tools: of the phone sessions that cancelled the picker, 51–59 %
   left without a file (desktop 40 %), and only 17–24 % picked one after the
   old «check it finished downloading» line. `iab` is for Android in-app
   browsers only: a host app may not use the system picker at all, and then
   there is no ☰ to find. The top-level `f` stays as the fallback. */
var PICK_HELP = {
    en: { f: "Couldn't find your file? Check that it finished downloading to this device.",
          ios: { v: 'Video on a phone lives in Photos, not Files. If your clip is stored in iCloud, open it in Photos first and let it download fully.',
                 a: 'Tracks inside streaming apps (Apple Music, Spotify) are not available as files. Pick one from Files or Downloads.',
                 i: 'Photos on a phone live in the photo library, not in Files.',
                 f: "Is the file in Downloads? In the picker, tap Browse at the bottom — the Downloads folder is in iCloud Drive or On My iPhone. A file from a messenger has to be saved to Files first.",
                 vp: "Did the picker seem to freeze after you chose the clip? That is the iPhone preparing a video from Photos: it downloads it from iCloud and, if needed, converts it to a compatible format — without showing any progress. A long clip takes a minute or more: choose it again and wait, the picker closes by itself. A clip saved to Files arrives without this step: in Photos tap Share → Save to Files.",
                 btn: { v: 'Open photo library', a: 'Open files', i: 'Open photo library', f: 'Try again', vp: "Choose again" } },
          android: { v: 'Video on a phone lives in your gallery (Google Photos), not in Files. If the clip is only in the cloud, download it to the device first.',
                 a: 'Tracks inside streaming apps (Spotify, YouTube Music) are not available as files. Pick one from Files or your Downloads folder.',
                 i: 'Photos on a phone live in the gallery, not in Files. A cloud-only shot has to be downloaded to the device first.',
                 f: "Is the file in Downloads? In the picker, tap ☰ at the top left and open Downloads — next time your phone opens that folder by itself. A file from a messenger has to be saved to the phone first.",
                 iab: "No ☰ menu? Open this page in your browser.",
                 btn: { v: 'Open gallery', a: 'Open files', i: 'Open gallery', f: 'Try again' } } },
    ru: { f: 'Не нашли файл? Проверьте, что он полностью скачан на это устройство.',
          ios: { v: 'Видео на телефоне лежит в «Фото», а не в «Файлах». Если клип хранится в iCloud, сначала откройте его в «Фото» и дождитесь полной загрузки.',
                 a: 'Треки из стриминговых приложений (Apple Music, Spotify) недоступны как файлы. Выберите из «Файлов» или загрузок.',
                 i: 'Фотографии на телефоне лежат в медиатеке, а не в «Файлах».',
                 f: "Файл в «Загрузках»? В окне выбора нажмите «Обзор» внизу — папка «Загрузки» лежит в iCloud Drive или «На iPhone». Файл из мессенджера сначала сохраните в «Файлы».",
                 vp: "Окно выбора «зависло», когда вы выбрали ролик? Так iPhone готовит видео из «Фото»: докачивает его из iCloud и при необходимости переводит в совместимый формат — без индикатора. Длинный ролик готовится минуту и дольше: выберите его снова и подождите, окно закроется само. Ролик из «Файлов» приходит без этой подготовки: в «Фото» нажмите «Поделиться» → «Сохранить в Файлы».",
                 btn: { v: 'Открыть медиатеку', a: 'Открыть файлы', i: 'Открыть медиатеку', f: 'Попробовать снова', vp: "Выбрать снова" } },
          android: { v: 'Видео на телефоне лежит в «Галерее» (Google Фото), а не в «Файлах». Если клип хранится только в облаке, сначала скачайте его на устройство.',
                 a: 'Треки из стриминговых приложений (Spotify, YouTube Music) недоступны как файлы. Выберите из «Файлов» или папки загрузок.',
                 i: 'Фотографии на телефоне лежат в «Галерее», а не в «Файлах». Снимок из облака сначала скачайте на устройство.',
                 f: "Файл в «Загрузках»? В окне выбора нажмите ☰ вверху слева и откройте «Загрузки» — в следующий раз телефон сам откроет эту папку. Файл из мессенджера сначала сохраните на телефон.",
                 iab: "Меню ☰ нет? Откройте эту страницу в браузере.",
                 btn: { v: 'Открыть галерею', a: 'Открыть файлы', i: 'Открыть галерею', f: 'Попробовать снова' } } },
    uk: { f: 'Не знайшли файл? Перевірте, що він повністю завантажений на цей пристрій.',
          ios: { v: 'Відео на телефоні лежить у «Фото», а не у «Файлах». Якщо кліп зберігається в iCloud, спершу відкрийте його у «Фото» й дочекайтеся повного завантаження.',
                 a: 'Треки зі стрімінгових застосунків (Apple Music, Spotify) недоступні як файли. Оберіть із «Файлів» або завантажень.',
                 i: 'Фотографії на телефоні лежать у медіатеці, а не у «Файлах».',
                 f: "Файл у «Завантаженнях»? У вікні вибору натисніть «Огляд» унизу — тека «Завантаження» лежить в iCloud Drive або «На iPhone». Файл із месенджера спершу збережіть у «Файли».",
                 vp: "Вікно вибору «зависло», коли ви обрали ролик? Так iPhone готує відео з «Фото»: довантажує його з iCloud і за потреби переводить у сумісний формат — без індикатора. Довгий ролик готується хвилину й довше: оберіть його знову й зачекайте, вікно закриється само. Ролик із «Файлів» приходить без цієї підготовки: у «Фото» натисніть «Поширити» → «Зберегти у Файли».",
                 btn: { v: 'Відкрити медіатеку', a: 'Відкрити файли', i: 'Відкрити медіатеку', f: 'Спробувати ще раз', vp: "Обрати знову" } },
          android: { v: 'Відео на телефоні лежить у «Галереї» (Google Фото), а не у «Файлах». Якщо кліп зберігається лише в хмарі, спершу завантажте його на пристрій.',
                 a: 'Треки зі стрімінгових застосунків (Spotify, YouTube Music) недоступні як файли. Оберіть із «Файлів» або теки завантажень.',
                 i: 'Фотографії на телефоні лежать у «Галереї», а не у «Файлах». Знімок із хмари спершу завантажте на пристрій.',
                 f: "Файл у «Завантаженнях»? У вікні вибору натисніть ☰ угорі ліворуч і відкрийте «Завантаження» — наступного разу телефон сам відкриє цю теку. Файл із месенджера спершу збережіть на телефон.",
                 iab: "Меню ☰ немає? Відкрийте цю сторінку в браузері.",
                 btn: { v: 'Відкрити галерею', a: 'Відкрити файли', i: 'Відкрити галерею', f: 'Спробувати ще раз' } } },
    de: { f: 'Datei nicht gefunden? Prüfe, ob sie vollständig auf dieses Gerät geladen wurde.',
          ios: { v: 'Videos liegen auf dem Handy in „Fotos“, nicht in „Dateien“. Ein Clip aus iCloud muss zuerst in Fotos vollständig geladen werden.',
                 a: 'Titel aus Streaming-Apps (Apple Music, Spotify) sind nicht als Dateien verfügbar. Wähle einen aus „Dateien“ oder den Downloads.',
                 i: 'Fotos liegen auf dem Handy in der Mediathek, nicht in „Dateien“.',
                 f: "Liegt die Datei in „Downloads“? Tippe im Auswahlfenster unten auf „Durchsuchen“ – der Ordner „Downloads“ liegt in iCloud Drive oder „Auf meinem iPhone“. Eine Datei aus einem Messenger musst du zuerst in „Dateien“ sichern.",
                 vp: "Hängt das Auswahlfenster, nachdem du den Clip gewählt hast? So bereitet das iPhone ein Video aus „Fotos“ vor: Es lädt es aus iCloud und wandelt es bei Bedarf in ein kompatibles Format um – ohne Fortschrittsanzeige. Ein langer Clip braucht eine Minute oder länger: Wähle ihn erneut und warte, das Fenster schließt sich von selbst. Ein Clip aus „Dateien“ kommt ohne diesen Schritt an: Tippe in Fotos auf Teilen → „In Dateien sichern“.",
                 btn: { v: 'Mediathek öffnen', a: 'Dateien öffnen', i: 'Mediathek öffnen', f: 'Erneut versuchen', vp: "Erneut wählen" } },
          android: { v: 'Videos liegen auf dem Handy in der Galerie (Google Fotos), nicht in „Dateien“. Ein Clip, der nur in der Cloud liegt, muss zuerst auf das Gerät geladen werden.',
                 a: 'Titel aus Streaming-Apps (Spotify, YouTube Music) sind nicht als Dateien verfügbar. Wähle einen aus „Dateien“ oder dem Download-Ordner.',
                 i: 'Fotos liegen auf dem Handy in der Galerie, nicht in „Dateien“. Ein Bild aus der Cloud muss zuerst auf das Gerät geladen werden.',
                 f: "Liegt die Datei in „Downloads“? Tippe im Auswahlfenster oben links auf ☰ und öffne „Downloads“ – beim nächsten Mal öffnet das Handy diesen Ordner von selbst. Eine Datei aus einem Messenger musst du zuerst auf dem Handy speichern.",
                 iab: "Kein ☰-Menü? Öffne diese Seite im Browser.",
                 btn: { v: 'Galerie öffnen', a: 'Dateien öffnen', i: 'Galerie öffnen', f: 'Erneut versuchen' } } },
    es: { f: "¿No encuentras el archivo? Verifica que se haya descargado por completo en este dispositivo.",
          ios: { v: "En el celular, los videos están en «Fotos», no en «Archivos». Si el clip está en iCloud, ábrelo antes en Fotos y espera a que se descargue.",
                 a: "Las canciones de apps de streaming (Apple Music, Spotify) no están disponibles como archivos. Elige una desde Archivos o Descargas.",
                 i: "En el celular, las fotos están en la fototeca, no en «Archivos».",
                 f: "¿El archivo está en Descargas? En la ventana de selección, toca «Explorar» abajo: la carpeta «Descargas» está en iCloud Drive o en «En mi iPhone». Un archivo que llegó por una app de mensajería primero guárdalo en «Archivos».",
                 vp: "¿La ventana de selección se quedó congelada después de elegir el clip? Así prepara el iPhone un video de Fotos: lo descarga de iCloud y, si hace falta, lo convierte a un formato compatible, sin mostrar el progreso. Un clip largo tarda un minuto o más: elígelo de nuevo y espera, la ventana se cierra sola. Un clip guardado en Archivos llega sin este paso: en Fotos toca Compartir → Guardar en Archivos.",
                 btn: { v: "Abrir fototeca", a: "Abrir archivos", i: "Abrir fototeca", f: "Reintentar", vp: "Elegir de nuevo" } },
          android: { v: "En el celular, los videos están en la galería (Google Fotos), no en «Archivos». Si el clip solo está en la nube, descárgalo antes al dispositivo.",
                 a: "Las canciones de apps de streaming (Spotify, YouTube Music) no están disponibles como archivos. Elige una desde Archivos o la carpeta de descargas.",
                 i: "En el celular, las fotos están en la galería, no en «Archivos». Una foto que solo está en la nube hay que descargarla antes.",
                 f: "¿El archivo está en Descargas? En la ventana de selección, toca ☰ arriba a la izquierda y abre «Descargas»; la próxima vez el celular abrirá esa carpeta solo. Un archivo que llegó por una app de mensajería primero guárdalo en el celular.",
                 iab: "¿No aparece el menú ☰? Abre esta página en el navegador.",
                 btn: { v: "Abrir galería", a: "Abrir archivos", i: "Abrir galería", f: "Reintentar" } } },
    fr: { f: 'Fichier introuvable ? Vérifiez qu’il est entièrement téléchargé sur cet appareil.',
          ios: { v: 'Sur téléphone, les vidéos sont dans « Photos », pas dans « Fichiers ». Si le clip est sur iCloud, ouvrez-le d’abord dans Photos et attendez le téléchargement.',
                 a: 'Les titres des applis de streaming (Apple Music, Spotify) ne sont pas accessibles comme fichiers. Choisissez-en un dans Fichiers ou Téléchargements.',
                 i: 'Sur téléphone, les photos sont dans la photothèque, pas dans « Fichiers ».',
                 f: "Le fichier est dans Téléchargements ? Dans la fenêtre de sélection, touchez « Parcourir » en bas : le dossier « Téléchargements » se trouve dans iCloud Drive ou « Sur mon iPhone ». Un fichier reçu dans une messagerie doit d’abord être enregistré dans Fichiers.",
                 vp: "La fenêtre de sélection s’est figée après le choix du clip ? C’est l’iPhone qui prépare une vidéo de Photos : il la télécharge depuis iCloud et la convertit au besoin dans un format compatible, sans afficher de progression. Un long clip demande une minute ou plus : choisissez-le à nouveau et patientez, la fenêtre se ferme toute seule. Un clip enregistré dans Fichiers arrive sans cette étape : dans Photos, touchez Partager → Enregistrer dans Fichiers.",
                 btn: { v: 'Ouvrir la photothèque', a: 'Ouvrir Fichiers', i: 'Ouvrir la photothèque', f: 'Réessayer', vp: "Choisir à nouveau" } },
          android: { v: 'Sur téléphone, les vidéos sont dans la galerie (Google Photos), pas dans « Fichiers ». Si le clip n’est que dans le cloud, téléchargez-le d’abord sur l’appareil.',
                 a: 'Les titres des applis de streaming (Spotify, YouTube Music) ne sont pas accessibles comme fichiers. Choisissez-en un dans Fichiers ou le dossier Téléchargements.',
                 i: 'Sur téléphone, les photos sont dans la galerie, pas dans « Fichiers ». Une photo qui n’est que dans le cloud doit d’abord être téléchargée.',
                 f: "Le fichier est dans Téléchargements ? Dans la fenêtre de sélection, touchez ☰ en haut à gauche et ouvrez « Téléchargements » : la prochaine fois, le téléphone ouvrira ce dossier tout seul. Un fichier reçu dans une messagerie doit d’abord être enregistré sur le téléphone.",
                 iab: "Pas de menu ☰ ? Ouvrez cette page dans le navigateur.",
                 btn: { v: 'Ouvrir la galerie', a: 'Ouvrir Fichiers', i: 'Ouvrir la galerie', f: 'Réessayer' } } },
    pt: { f: "Não encontrou o arquivo? Verifique se ele foi baixado por completo neste dispositivo.",
          ios: { v: "No celular, os vídeos ficam em «Fotos», não em «Arquivos». Se o clipe está no iCloud, abra-o primeiro em Fotos e espere o download terminar.",
                 a: "As faixas de apps de streaming (Apple Music, Spotify) não estão disponíveis como arquivos. Escolha uma em Arquivos ou Downloads.",
                 i: "No celular, as fotos ficam na biblioteca de fotos, não em «Arquivos».",
                 f: "O arquivo está em Downloads? Na janela de seleção, toque em «Explorar» embaixo — a pasta «Downloads» fica no iCloud Drive ou em «No Meu iPhone». Um arquivo recebido em um app de mensagens precisa ser salvo em «Arquivos» primeiro.",
                 vp: "A janela de seleção travou depois que você escolheu o clipe? É o iPhone preparando um vídeo do Fotos: ele baixa do iCloud e, se preciso, converte para um formato compatível, sem mostrar o progresso. Um clipe longo leva um minuto ou mais: escolha-o de novo e espere, a janela fecha sozinha. Um clipe salvo em Arquivos chega sem essa etapa: no Fotos, toque em Compartilhar → Salvar em Arquivos.",
                 btn: { v: "Abrir biblioteca de fotos", a: "Abrir arquivos", i: "Abrir biblioteca de fotos", f: "Tentar de novo", vp: "Escolher de novo" } },
          android: { v: "No celular, os vídeos ficam na galeria (Google Fotos), não em «Arquivos». Se o clipe só está na nuvem, baixe-o primeiro no aparelho.",
                 a: "As faixas de apps de streaming (Spotify, YouTube Music) não estão disponíveis como arquivos. Escolha uma em Arquivos ou na pasta Downloads.",
                 i: "No celular, as fotos ficam na galeria, não em «Arquivos». Uma foto que só está na nuvem precisa ser baixada primeiro.",
                 f: "O arquivo está em Downloads? Na janela de seleção, toque em ☰ no canto superior esquerdo e abra «Downloads» — da próxima vez o celular abre essa pasta sozinho. Um arquivo recebido em um app de mensagens precisa ser salvo no celular primeiro.",
                 iab: "O menu ☰ não aparece? Abra esta página no navegador.",
                 btn: { v: "Abrir galeria", a: "Abrir arquivos", i: "Abrir galeria", f: "Tentar de novo" } } },
    id: { f: 'Tidak menemukan file? Pastikan file sudah terunduh sepenuhnya ke perangkat ini.',
          ios: { v: 'Video di ponsel ada di «Foto», bukan di «File». Jika klip tersimpan di iCloud, buka dulu di Foto dan tunggu sampai terunduh penuh.',
                 a: 'Lagu di aplikasi streaming (Apple Music, Spotify) tidak tersedia sebagai file. Pilih dari File atau Unduhan.',
                 i: 'Foto di ponsel ada di galeri, bukan di «File».',
                 f: "File ada di Unduhan? Di jendela pemilih, ketuk «Telusuri» di bawah — folder «Unduhan» ada di iCloud Drive atau «Di iPhone Saya». File dari aplikasi pesan harus disimpan dulu ke «File».",
                 vp: "Jendela pemilih seperti macet setelah Anda memilih klip? Itu iPhone sedang menyiapkan video dari Foto: mengunduhnya dari iCloud dan, bila perlu, mengubahnya ke format yang kompatibel, tanpa menampilkan kemajuan. Klip panjang butuh satu menit atau lebih: pilih lagi dan tunggu, jendela akan tertutup sendiri. Klip yang disimpan di File datang tanpa langkah ini: di Foto ketuk Bagikan → Simpan ke File.",
                 btn: { v: 'Buka galeri', a: 'Buka file', i: 'Buka galeri', f: 'Coba lagi', vp: "Pilih lagi" } },
          android: { v: 'Video di ponsel ada di galeri (Google Foto), bukan di «File». Jika klip hanya tersimpan di cloud, unduh dulu ke perangkat.',
                 a: 'Lagu di aplikasi streaming (Spotify, YouTube Music) tidak tersedia sebagai file. Pilih dari File atau folder Unduhan.',
                 i: 'Foto di ponsel ada di galeri, bukan di «File». Foto yang hanya ada di cloud harus diunduh dulu ke perangkat.',
                 f: "File ada di Unduhan? Di jendela pemilih, ketuk ☰ di kiri atas lalu buka «Unduhan» — lain kali ponsel akan membuka folder itu sendiri. File dari aplikasi pesan harus disimpan dulu ke ponsel.",
                 iab: "Menu ☰ tidak ada? Buka halaman ini di browser.",
                 btn: { v: 'Buka galeri', a: 'Buka file', i: 'Buka galeri', f: 'Coba lagi' } } },
    ko: { f: '파일을 찾지 못했나요? 이 기기에 완전히 다운로드되었는지 확인하세요.',
          ios: { v: '휴대폰의 동영상은 «파일»이 아니라 «사진»에 있습니다. iCloud에 있는 영상이라면 먼저 사진 앱에서 열어 완전히 내려받으세요.',
                 a: '스트리밍 앱(Apple Music, Spotify)의 음원은 파일로 사용할 수 없습니다. «파일»이나 다운로드에서 선택하세요.',
                 i: '휴대폰의 사진은 «파일»이 아니라 사진 보관함에 있습니다.',
                 f: "파일이 다운로드 폴더에 있나요? 파일 선택 창 아래쪽의 «둘러보기»를 누르세요. «다운로드» 폴더는 iCloud Drive 또는 «나의 iPhone»에 있습니다. 메신저로 받은 파일은 먼저 «파일»에 저장해야 합니다.",
                 vp: "클립을 고른 뒤 선택 창이 멈춘 것 같나요? iPhone이 «사진»의 동영상을 준비하는 중입니다. iCloud에서 내려받고 필요하면 호환 형식으로 변환하는데, 진행 상황은 표시되지 않습니다. 긴 클립은 1분 이상 걸립니다. 다시 선택하고 기다리면 창이 저절로 닫힙니다. «파일»에 저장한 클립은 이 과정 없이 들어옵니다. «사진»에서 공유 → «파일에 저장»을 누르세요.",
                 btn: { v: '사진 보관함 열기', a: '파일 열기', i: '사진 보관함 열기', f: '다시 시도', vp: "다시 선택" } },
          android: { v: '휴대폰의 동영상은 «파일»이 아니라 갤러리(Google 포토)에 있습니다. 클라우드에만 있는 영상이라면 먼저 기기에 내려받으세요.',
                 a: '스트리밍 앱(Spotify, YouTube Music)의 음원은 파일로 사용할 수 없습니다. «파일»이나 다운로드 폴더에서 선택하세요.',
                 i: '휴대폰의 사진은 «파일»이 아니라 갤러리에 있습니다. 클라우드에만 있는 사진은 먼저 기기에 내려받아야 합니다.',
                 f: "파일이 다운로드 폴더에 있나요? 파일 선택 창 왼쪽 위의 ☰를 눌러 «다운로드»를 여세요. 다음부터는 휴대폰이 그 폴더를 바로 엽니다. 메신저로 받은 파일은 먼저 휴대폰에 저장해야 합니다.",
                 iab: "☰ 메뉴가 없나요? 이 페이지를 브라우저에서 여세요.",
                 btn: { v: '갤러리 열기', a: '파일 열기', i: '갤러리 열기', f: '다시 시도' } } },
    ar: { f: 'لم تجد الملف؟ تأكد من تنزيله بالكامل على هذا الجهاز.',
          ios: { v: 'مقاطع الفيديو على الهاتف موجودة في «الصور» وليس في «الملفات». إذا كان المقطع في iCloud فافتحه أولاً في الصور وانتظر تنزيله بالكامل.',
                 a: 'المقاطع داخل تطبيقات البث (Apple Music وSpotify) غير متاحة كملفات. اختر ملفاً من «الملفات» أو من التنزيلات.',
                 i: 'الصور على الهاتف موجودة في مكتبة الصور وليس في «الملفات».',
                 f: "هل الملف في التنزيلات؟ في نافذة الاختيار اضغط «تصفح» في الأسفل، فمجلد «التنزيلات» موجود في iCloud Drive أو «على الـ iPhone». الملف الوارد في تطبيق مراسلة يجب حفظه في «الملفات» أولاً.",
                 vp: "هل بدت نافذة الاختيار متجمّدة بعد أن اخترت المقطع؟ هذا الـ iPhone يجهّز فيديو من «الصور»: ينزّله من iCloud ويحوّله عند الحاجة إلى صيغة متوافقة دون أن يعرض التقدّم. المقطع الطويل يحتاج دقيقة أو أكثر: اختره مرة أخرى وانتظر، وستُغلق النافذة من تلقاء نفسها. المقطع المحفوظ في «الملفات» يصل دون هذه الخطوة: في «الصور» اضغط مشاركة ثم «حفظ في الملفات».",
                 btn: { v: 'فتح مكتبة الصور', a: 'فتح الملفات', i: 'فتح مكتبة الصور', f: 'إعادة المحاولة', vp: "اختيار مرة أخرى" } },
          android: { v: 'مقاطع الفيديو على الهاتف موجودة في المعرض (صور Google) وليس في «الملفات». إذا كان المقطع في السحابة فقط فنزّله أولاً على الجهاز.',
                 a: 'المقاطع داخل تطبيقات البث (Spotify وYouTube Music) غير متاحة كملفات. اختر ملفاً من «الملفات» أو من مجلد التنزيلات.',
                 i: 'الصور على الهاتف موجودة في المعرض وليس في «الملفات». الصورة الموجودة في السحابة فقط يجب تنزيلها أولاً على الجهاز.',
                 f: "هل الملف في التنزيلات؟ في نافذة الاختيار اضغط القائمة ☰ أعلى الشاشة وافتح «التنزيلات»، وفي المرة القادمة سيفتح الهاتف هذا المجلد تلقائياً. الملف الوارد في تطبيق مراسلة يجب حفظه على الهاتف أولاً.",
                 iab: "لا تظهر القائمة ☰؟ افتح هذه الصفحة في المتصفح.",
                 btn: { v: 'فتح المعرض', a: 'فتح الملفات', i: 'فتح المعرض', f: 'إعادة المحاولة' } } },
    tr: { f: 'Dosyanızı bulamadınız mı? Bu cihaza tamamen indirildiğinden emin olun.',
          ios: { v: 'Telefonda videolar Dosyalar’da değil, Fotoğraflar’da bulunur. Klibiniz iCloud’da saklanıyorsa önce Fotoğraflar’da açıp tamamen inmesini bekleyin.',
                 a: 'Akış uygulamalarındaki (Apple Music, Spotify) parçalar dosya olarak kullanılamaz. Dosyalar’dan ya da İndirilenler’den birini seçin.',
                 i: 'Telefonda fotoğraflar Dosyalar’da değil, fotoğraf kitaplığında bulunur.',
                 f: "Dosya İndirilenler’de mi? Seçim penceresinde alttaki «Göz At»a dokunun — «İndirilenler» klasörü iCloud Drive’da ya da «iPhone’umda» bulunur. Mesajlaşma uygulamasından gelen dosyayı önce «Dosyalar»a kaydedin.",
                 vp: "Klibi seçtikten sonra seçim penceresi dondu mu? iPhone, Fotoğraflar’daki videoyu hazırlıyor: iCloud’dan indiriyor ve gerekirse uyumlu bir biçime dönüştürüyor, ilerlemeyi de göstermiyor. Uzun bir klip bir dakika ya da daha fazla sürer: yeniden seçin ve bekleyin, pencere kendiliğinden kapanır. Dosyalar’a kaydedilmiş bir klip bu adım olmadan gelir: Fotoğraflar’da Paylaş → «Dosyalar’a Kaydet»e dokunun.",
                 btn: { v: 'Fotoğraf kitaplığını aç', a: 'Dosyaları aç', i: 'Fotoğraf kitaplığını aç', f: 'Tekrar dene', vp: "Yeniden seç" } },
          android: { v: 'Telefonda videolar Dosyalar’da değil, galeride (Google Fotoğraflar) bulunur. Klip yalnızca bulutta saklanıyorsa önce cihaza indirin.',
                 a: 'Akış uygulamalarındaki (Spotify, YouTube Music) parçalar dosya olarak kullanılamaz. Dosyalar’dan ya da İndirilenler klasöründen birini seçin.',
                 i: 'Telefonda fotoğraflar Dosyalar’da değil, galeride bulunur. Yalnızca bulutta olan bir fotoğrafın önce cihaza indirilmesi gerekir.',
                 f: "Dosya İndirilenler’de mi? Seçim penceresinde sol üstteki ☰ simgesine dokunup «İndirilenler»i açın — bir dahaki sefere telefon bu klasörü kendisi açar. Mesajlaşma uygulamasından gelen dosyayı önce telefona kaydedin.",
                 iab: "☰ menüsü yok mu? Bu sayfayı tarayıcıda açın.",
                 btn: { v: 'Galeriyi aç', a: 'Dosyaları aç', i: 'Galeriyi aç', f: 'Tekrar dene' } } }
};

/* iOS or Android? showPickHelp() already refuses to run anywhere else, so
   "not an iPhone/iPad/iPod" means Android here. */
function pickHelpPlatform(){
    return /iPhone|iPad|iPod/i.test(navigator.userAgent || '') ? 'ios' : 'android';
}

// Which family does this zone accept? Decides both the wording and what
// the second door asks for. An accept made of extensions only names the
// family too: /video-colorize («.mp4,.mov,…»), /cell-counter («.jpg,.png,…»)
// and /spectrogram-lab («.wav,.mp3,…») used to read as documents and got
// the Downloads advice with no gallery door. `.webm` names no family — the
// audio tools list it as well.
var PICK_HELP_EXT = {
    v: /^\.(mp4|m4v|mov|mkv|avi|3gp|ogv|wmv|flv|mts|m2ts|mpg|mpeg|lrv)$/,
    a: /^\.(mp3|wav|ogg|oga|flac|aac|m4a|m4b|aiff|aif|wma|opus|amr|caf)$/,
    i: /^\.(jpe?g|png|webp|gif|bmp|heic|heif|avif|tiff?)$/
};
function pickHelpKind(accept){
    var a = (accept || '').toLowerCase();
    var v = a.indexOf('video/') >= 0, au = a.indexOf('audio/') >= 0, im = a.indexOf('image/') >= 0;
    /* Extensions name the family only when the accept names none by MIME. On an
       iPhone the layout's widener appends `.mp4,.mov,.3gp` to every `audio/*`
       zone (Safari greys out sound in an mp4 otherwise), so from 08.10 every
       audio tool on an iPhone read as VIDEO here: a person who cancelled picking
       a song was told that videos live in Photos and offered a `video/*` door
       (caught 10.10 by uploader-pick-help.mjs on /chord-finder). */
    if (!v && !au && !im) a.split(',').forEach(function(t){
        t = t.trim();
        if (PICK_HELP_EXT.v.test(t)) v = true;
        else if (PICK_HELP_EXT.a.test(t)) au = true;
        else if (PICK_HELP_EXT.i.test(t)) im = true;
    });
    if (v && !au) return 'v';
    if (au && !v) return 'a';
    if (im && !v && !au) return 'i';
    if (v && au) return 'v';   // A/V tools: the phone-gallery case is the confusing one
    return 'f';
}
var PICK_HELP_MIME = { v: 'video/*', a: 'audio/*', i: 'image/*', f: null };

/* A video picker on an iPhone that stayed open this long and came back empty
   is read as «it was preparing the clip», not «the clip was not found».
   Why the iPhone makes people wait at all (bug report 2026-10-09, /dialogue-
   enhancer, iOS 26: «не загружаются файлы с телефона, зависает»): Safari takes
   a video out of Photos through PHPicker in the COMPATIBLE representation —
   WebKit's WKFileUploadPanel asks for it unless the internal preference
   PhotoPickerPrefersOriginalImageFormat is on, and it is off — so the system
   downloads an iCloud clip and converts the video before handing it over,
   inside the picker, with no progress shown (WebKit drops the NSProgress), and
   the picker stays on screen until that is done. The page cannot shorten it:
   `accept` does not change this path (any image/video type gets the same menu
   and the same photo picker), which an independent iPhone test of four accept
   variants also found. Measured, 47 video tools, 21 days to 10.10: the picker
   was still open after 30 s on 12.4 % of iOS opens vs 3.7 % on Android, and
   people left without a file after such a wait 5× as often (2.4 % of picks vs
   0.47 %). 15 s is a judgement, not a measurement — the line is worded «did it
   freeze?», so someone who was only scrolling loses nothing by reading it;
   `waited_s` on the cancel and `pick_s` on the file are there to tune it. */
var PICK_PREP_WAIT_S = 15;

function showPickHelp(el, reason, waitedS){
    if (!el) return;
    /* Only meaningful where the picker is a separate app surface and the
       gallery/Files split exists — i.e. phones. A desktop dialog shows the
       whole filesystem, so the second door would add nothing there. */
    if (!/iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '')) return;
    var loc = (window.__LOCALE || 'en').substring(0, 2);
    var T = PICK_HELP[loc] || PICK_HELP.en;
    /* Platform decides the wording: naming iCloud to a Redmi owner is simply
       false, and that is what the 2026-08-28 bug report was about. */
    var platform = pickHelpPlatform();
    var P = T[platform] || T.ios;
    var input = el.querySelector('input[type=file]')
        || document.querySelector('input[data-uploader-for="' + el.id + '"]');
    var kind = pickHelpKind(input && input.getAttribute('accept'));
    var prep = platform === 'ios' && kind === 'v' && !!P.vp
        && typeof waitedS === 'number' && waitedS >= PICK_PREP_WAIT_S;
    /* One box per zone — except that a long wait after a quick cancel is a
       different diagnosis, and the box must change its answer (once). A box
       already taken down by a file stays down. */
    if (el.__pickHelpShown) {
        if (!prep || el.__pickHelpPrep || !el.__pickHelpBox) return;
        try { el.__pickHelpBox.remove(); } catch (_) {}
        el.__pickHelpBox = null;
    }
    el.__pickHelpShown = true;
    el.__pickHelpPrep = prep;

    var box = document.createElement('div');
    box.className = 'tu-pick-help';
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    var p = document.createElement('p');
    p.className = 'tu-pick-help-text';
    var text = prep ? P.vp : (P[kind] || T.f);
    if (kind === 'f' && platform === 'android' && window.__isInAppBrowser && P.iab) text += ' ' + P.iab;
    p.textContent = text;
    box.appendChild(p);

    /* Documents have no gallery to send people to — the file IS in the
       picker, one menu away. The button reopens the same picker with the
       same bookkeeping as a zone tap (the pick-lost retry does the same), so
       the instruction and the action sit together. A video the iPhone was
       still preparing gets the same button: what it needs is the same pick
       again and patience, and on iOS the bare-wildcard door below opens the
       very same menu and photo picker (WebKit maps any video type to one
       photo picker), so there it would be a second button doing the first
       one's job with fewer types in «Files». */
    if ((kind === 'f' || prep) && input) {
        var usedKind = prep ? 'vp' : kind;
        var again = document.createElement('button');
        again.type = 'button';
        again.className = 'tu-pick-help-btn';
        again.textContent = (P.btn && P.btn[usedKind]) || (P.btn && P.btn.f) || T.f;
        again.addEventListener('click', function(){
            try { window.__ae && window.__ae('feature_use', {
                slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
                f: 'pick_help_used', kind: usedKind }); } catch (_) {}
            el.__pickHelpRetry = usedKind;
            armUploadNoPickFollowup(el, 'pick_help');
            markPickerOpen(el, 'pick_help');
            input.click();
        });
        box.appendChild(again);
    }

    /* Second door: the same uploader, but asking for the BARE wildcard so
       the phone offers its gallery instead of a file browser. Routed
       through __uploaderInjectFile so validation, FileBytes priming and
       telemetry are identical to a normal pick — never a bypass.
       That is real on Android (the bare wildcard is what opens the gallery
       there instead of the Files browser); on an iPhone both inputs get the
       same menu, so there it is only a second try. */
    var mime = prep ? null : PICK_HELP_MIME[kind];
    if (mime && el.__uploaderInjectFile) {
        var alt = document.createElement('input');
        alt.type = 'file';
        alt.accept = mime;
        /* The layout's iOS accept-widener sweeps every input[type=file][accept]
           and appends the extension list. On THIS input that would undo the
           whole point — the bare wildcard is what makes the phone offer its
           gallery. Its skip flag is the honest way to opt out. */
        alt.__iosWidened = true;
        alt.className = 'tu-pick-help-input';
        alt.tabIndex = -1;
        alt.setAttribute('aria-hidden', 'true');
        if (input && input.multiple) alt.multiple = true;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tu-pick-help-btn';
        btn.textContent = (P.btn && P.btn[kind]) || (P.btn && P.btn.f) || T.f;
        btn.addEventListener('click', function(){
            try { window.__ae && window.__ae('feature_use', {
                slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
                f: 'pick_help_used', kind: kind }); } catch (_) {}
            alt.click();
        });
        alt.addEventListener('change', function(){
            var fs = alt.files;
            if (!fs || !fs.length) return;
            try { window.__ae && window.__ae('feature_use', {
                slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
                f: 'pick_help_rescued', kind: kind }); } catch (_) {}
            for (var i = 0; i < fs.length; i++) el.__uploaderInjectFile(fs[i], 'pick_help');
            alt.value = '';
        });
        box.appendChild(btn);
        box.appendChild(alt);
    }

    (el.parentNode || document.body).insertBefore(box, el.nextSibling);
    el.__pickHelpBox = box;
    var shown = { slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
        f: 'pick_help_shown', kind: kind, reason: reason };
    if (typeof waitedS === 'number') shown.waited_s = waitedS;
    if (prep) shown.prep = 1;
    try { window.__ae && window.__ae('feature_use', shown); } catch (_) {}
}

function hidePickHelp(el){
    if (!el) return;
    /* A file after a retry button is that door's rescue — the same event the
       media second door sends, so one query reads both. `kind` says which
       retry: 'f' (documents) or 'vp' (a video the iPhone was preparing). */
    if (el.__pickHelpRetry) {
        var rescuedKind = typeof el.__pickHelpRetry === 'string' ? el.__pickHelpRetry : 'f';
        el.__pickHelpRetry = false;
        try { window.__ae && window.__ae('feature_use', {
            slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
            f: 'pick_help_rescued', kind: rescuedKind }); } catch (_) {}
    }
    if (el.__pickHelpBox) {
        try { el.__pickHelpBox.remove(); } catch (_) {}
        el.__pickHelpBox = null;
    }
}

// ── Fold cue ───────────────────────────────────────────────────────────
// One shared "↓ Загрузить файл" pill for the measured mobile discoverability
// gap: the upload zone sits ~1000px down (ad + H1 + a 488px how-to block above
// it — all fixed conventions), so on a phone it is 2+ screens below the fold and
// mobile users routinely take 15-30s+ to find it (46% within 5s vs 75% desktop;
// bug 2026-08-15 /trim Redmi/Yandex was one such user who reported "не
// загружается" 22s BEFORE selecting a file, then completed fine).
//
// Sanctioned by CLAUDE.md ("a scroll-to-start cue — never auto-scroll"). Shown
// only on coarse-pointer devices while the uploader is out of view; removed the
// instant it scrolls in. Tap = scrollIntoView({block:'nearest'}) — the only
// form the global no-auto-scroll rule permits, and only ever on a user tap.
var _foldCueDone = false;
function installFoldCue(el){
    if (_foldCueDone) return;                    // at most one cue per page
    if (!('IntersectionObserver' in window)) return;
    // Touch/phone only — the gap is a small-screen problem; desktop loads the
    // uploader at 75% within 5s already. A coarse pointer is the honest proxy.
    try { if (!window.matchMedia || !window.matchMedia('(pointer:coarse)').matches) return; }
    catch(_){ return; }
    if (el.classList.contains('tu-collapsed')) return;   // a file is already loaded (restore)
    // An uploader folded away inside a closed <details> is an OPTIONAL input
    // (the stamp maker's logo zone, 2026-09-05), not the page's primary action:
    // a pill pointing at it sends the visitor to upload something the page
    // never asked for. Same for a zone that opts out explicitly.
    if (el.getAttribute('data-fold-cue') === 'off') return;
    try { if (el.closest('details:not([open])')) return; } catch(_){}
    var label = (el.getAttribute('aria-label') || '').trim();
    if (!label) {
        var t = el.querySelector('.upload-zone-title, .tu-content .upload-zone-title');
        label = t ? (t.textContent || '').trim() : '';
    }
    if (!label) return;                          // no honest localized text → no cue
    _foldCueDone = true;

    var cue = null, shown = false, cleaned = false, outOfView = false, bannerMo = null;
    var reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch(_){}

    // The 152-ФЗ cookie-consent banner is fixed to bottom:0 (z 9998) on first
    // visit — exactly where this pill sits. Two stacked bottom bars is wrong and
    // the legal banner takes priority, so the cue defers until consent resolves.
    function consentUp(){
        // NB: the banner is position:fixed, so offsetParent is ALWAYS null for it
        // — that test would report "hidden" while it covers the screen. Visibility
        // is driven purely by the .cc-visible class (CSS: display:flex), so the
        // class match plus a computed-display guard is the honest check.
        var cc = document.querySelector('.cc-banner.cc-visible');
        if (!cc) return false;
        try { return getComputedStyle(cc).display !== 'none'; } catch(_){ return true; }
    }
    function cleanup(){
        if (cleaned) return; cleaned = true;
        try { io.disconnect(); } catch(_){}
        try { if (bannerMo) bannerMo.disconnect(); } catch(_){}
        if (cue && cue.parentNode) cue.parentNode.removeChild(cue);
        el.removeEventListener('tool-uploader:file', cleanup);
    }
    function build(){
        cue = document.createElement('button');
        cue.type = 'button';
        cue.className = 'tu-fold-cue';
        cue.setAttribute('aria-label', label);
        var arrow = document.createElement('span');
        arrow.className = 'tu-fold-cue-arrow'; arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '↓';
        var txt = document.createElement('span'); txt.textContent = label;
        cue.appendChild(arrow); cue.appendChild(txt);
        cue.addEventListener('click', function(){
            try { window.__ae && window.__ae('feature_use', { f: 'upload_fold_cue', act: 'tap' }); } catch(_){}
            try { el.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' }); } catch(_){ el.scrollIntoView(); }
            if (!reduce) {
                el.classList.remove('tu-cue-pulse'); void el.offsetWidth; el.classList.add('tu-cue-pulse');
                setTimeout(function(){ el.classList.remove('tu-cue-pulse'); }, 2400);
            }
            // IO will remove the pill once the zone is actually in view.
        });
        document.body.appendChild(cue);
        el.addEventListener('tool-uploader:file', cleanup);
    }

    // Reveal (or keep hidden while consent is up). Called from the IO callback
    // and again when the consent banner is dismissed.
    function maybeShow(){
        if (cleaned || !outOfView) return;
        /* ⚠️ «Не видно» и «НЕ СУЩЕСТВУЕТ на экране» — разные вещи, а
           IntersectionObserver их не различает: у зоны внутри display:none
           пересечения нет никогда, то есть она вечно числится под сгибом.
           Подсказка тогда зовёт загрузить файл туда, где зоны нет вовсе —
           поймано на пульте /presentation-remote, где колоду открывают на
           компьютере, а панель загрузки на телефоне скрыта. Проверки на
           `tu-collapsed` и закрытый <details> этот случай не ловят: там зона
           СУЩЕСТВУЕТ, просто свёрнута. Пустой список прямоугольников — и есть
           честный признак «коробки нет». */
        if (!el.getClientRects().length) {
            if (cue) cue.classList.remove('show');
            return;
        }
        if (!cue) build();
        if (consentUp()) {
            if (cue) cue.classList.remove('show');
            // Watch for the banner going away, then reveal.
            if (!bannerMo && 'MutationObserver' in window) {
                var cc = document.querySelector('.cc-banner');
                if (cc) {
                    bannerMo = new MutationObserver(function(){ maybeShow(); });
                    bannerMo.observe(cc, { attributes: true, attributeFilter: ['class', 'style'] });
                }
            }
            return;
        }
        if (!shown) {
            shown = true;
            // rAF so the freshly-appended element transitions in instead of
            // snapping — and so a mis-measured first frame can still self-correct.
            requestAnimationFrame(function(){ if (cue && !cleaned) cue.classList.add('show'); });
            try { window.__ae && window.__ae('feature_use', { f: 'upload_fold_cue', act: 'shown' }); } catch(_){}
        } else if (cue) {
            cue.classList.add('show');
        }
    }

    var io = new IntersectionObserver(function(entries){
        var e = entries[0]; if (!e) return;
        if (e.isIntersecting) {
            // Already reachable (tall window / user scrolled there) → cue's job
            // is done; if it was never shown, we never clutter the screen.
            cleanup();
            return;
        }
        outOfView = true;
        maybeShow();
    }, { rootMargin: '0px', threshold: 0 });
    io.observe(el);
}

// ── Pick lost to a reload ──────────────────────────────────────────
// The failure NONE of the sensors above can see, because it kills them.
//
// Bug report 2026-08-14, /audio-metadata, Android 15 / 4 GB, Yandex Search
// app's in-app browser: «Файл не загружается». The session timeline: zone
// tapped 17:31:29 → OS picker in front → the page's own clock restarted at
// 17:31:41 (`time_on_page_s` 47 at the 17:32:28 feedback; `interactionCount`
// back to 1). The tab had been reloaded while the picker was open — Android
// reclaims the browser's renderer as soon as another activity is in front
// and memory is short — and the chosen file arrived at a renderer that no
// longer existed. The person saw a freshly loaded page with nothing on it,
// filed the report, picked again, and finished (cover written, downloaded).
//
// Why it was invisible: the 30 s no-pick timer, the visibility counters and
// the `cancel` listener all live in the page, and the page is what died.
// `pagehide` is not sent for a killed renderer or a discarded tab. So the
// class produced no `upload_no_pick_followup`, no `tool_abandon`, nothing —
// only a person confused enough to write in. A floor for its size, from the
// one sensor that straddles a reload by accident (`inp_slow.n` is
// `performance.interactionCount`, which restarts at 1): RU, 30 d, 632
// sessions reloaded right after a slow interaction on an upload zone, 94 %
// of them phones, Android ~2.4× over-represented against a file_picked
// baseline, in-app and plain Chrome alike. Sparse sensor → true count is
// larger; this block is what measures it properly.
//
// Design: ONE record in sessionStorage — which the browser process keeps
// across a renderer kill and a discard, unlike anything in the page —
// written when a tap plausibly opened the picker, cleared by every honest
// way the picker can come back (a file, the `cancel` event) and by every
// honest way the page can go away (`pagehide` covers reload, navigation,
// close). A record still standing when the page boots means exactly one
// thing: the page was reloaded while the picker was open. Then say so at
// the zone (and as a toast when the zone is below the fold, where it lives
// on phones), offer the picker again in one tap, and emit
// `pick_lost_on_reload` so the class has a number.
//
// What this deliberately does NOT do: promise to recover the file. There
// is no API for that — the File never reached JavaScript, and Chrome does
// not restore file inputs after a reload. Explaining is the whole fix.
// Guard: tests/playwright/uploader-pick-lost-on-reload.mjs (the kill by its
// contract — no pagehide, storage kept, fresh document — plus the honest
// returns that must NOT trigger the note).
//
// ⚠️ This block must stay ABOVE scanAndWire(): the script is deferred, so
// readyState is already 'interactive' when it runs and scanAndWire() is
// called synchronously. `var` initialisers below that call are still
// undefined at boot — checkLostPick read sessionStorage.getItem(undefined)
// and the note never showed, while every tap-time path worked (caught by
// the guard's scenario A, 2026-09-05).
var PICK_OPEN_KEY = '_tu_pick_open';
var PICK_LOST_MAX_AGE_MS = 12 * 3600 * 1000; // a locked phone in the picker overnight still counts
var PICK_LOST_TEXT = {
    en: 'The page was reloaded while the file picker was open, so your file never arrived — this happens when the device runs out of memory. Please choose the file again.',
    ru: 'Страница перезагрузилась, пока был открыт выбор файла, поэтому файл сюда не попал. Так бывает, когда устройству не хватает памяти. Выберите файл ещё раз.',
    uk: 'Сторінка перезавантажилася, поки був відкритий вибір файлу, тому файл сюди не потрапив. Так буває, коли пристрою бракує пам’яті. Оберіть файл ще раз.',
    de: 'Die Seite wurde neu geladen, während die Dateiauswahl geöffnet war – deshalb ist die Datei nicht angekommen. Das passiert, wenn dem Gerät der Speicher ausgeht. Bitte wählen Sie die Datei erneut aus.',
    es: 'La página se recargó mientras el selector de archivos estaba abierto, así que el archivo no llegó. Ocurre cuando al dispositivo se le acaba la memoria. Elige el archivo otra vez.',
    fr: 'La page s’est rechargée pendant que le sélecteur de fichiers était ouvert : le fichier n’est donc pas arrivé. Cela se produit quand l’appareil manque de mémoire. Choisissez le fichier à nouveau.',
    pt: 'A página foi recarregada enquanto o seletor de ficheiros estava aberto, por isso o ficheiro não chegou. Acontece quando o dispositivo fica sem memória. Escolha o ficheiro novamente.',
    id: 'Halaman dimuat ulang saat pemilih file masih terbuka, jadi file tidak sampai. Ini terjadi saat memori perangkat habis. Silakan pilih file lagi.',
    ko: '파일 선택 창이 열려 있는 동안 페이지가 다시 로드되어 파일이 도착하지 않았습니다. 기기 메모리가 부족할 때 생기는 일입니다. 파일을 다시 선택해 주세요.',
    ar: 'أُعيد تحميل الصفحة أثناء فتح نافذة اختيار الملف، لذلك لم يصل الملف. يحدث هذا عندما تنفد ذاكرة الجهاز. يرجى اختيار الملف مرة أخرى.'
};
var PICK_LOST_BTN = {
    en: 'Choose the file again',
    ru: 'Выбрать файл ещё раз',
    uk: 'Обрати файл ще раз',
    de: 'Datei erneut auswählen',
    es: 'Elegir el archivo otra vez',
    fr: 'Choisir le fichier à nouveau',
    pt: 'Escolher o ficheiro novamente',
    id: 'Pilih file lagi',
    ko: '파일 다시 선택',
    ar: 'اختيار الملف مرة أخرى'
};
function pickLostSlug(){
    return (document.querySelector('meta[name="tool-slug"]') || {}).content || null;
}
function markPickerOpen(el, src){
    try {
        sessionStorage.setItem(PICK_OPEN_KEY, JSON.stringify({
            t: Date.now(), slug: pickLostSlug(), zone: (el && el.id) || '', src: src || ''
        }));
    } catch (_) {}
}
function clearPickerOpen(){
    try { sessionStorage.removeItem(PICK_OPEN_KEY); } catch (_) {}
}
// A normal departure settles the record. pagehide fires on reload,
// navigation and close — and is NOT fired for a killed renderer or a
// discarded tab, which is exactly what the record is for.
try { window.addEventListener('pagehide', function(){ clearPickerOpen(); }); } catch (_) {}

var _pickLostChecked = false;
function checkLostPick(el){
    if (_pickLostChecked) return;
    _pickLostChecked = true; // one verdict per page load
    var raw = null;
    try { raw = sessionStorage.getItem(PICK_OPEN_KEY); } catch (_) { return; }
    if (!raw) return;
    clearPickerOpen();
    var rec = null;
    try { rec = JSON.parse(raw); } catch (_) {}
    if (!rec || !rec.t) return;
    var age = Date.now() - rec.t;
    if (age < 0 || age > PICK_LOST_MAX_AGE_MS) return;
    var slug = pickLostSlug();
    // Another tool in the same tab's history is a navigation, not a reload
    // of the page the picker belonged to.
    if (rec.slug && slug && rec.slug !== slug) return;
    var zone = (rec.zone && document.getElementById(rec.zone)) || el;
    if (!zone || !zone.classList || !zone.classList.contains('tool-uploader')) zone = el;
    var nav = '';
    try { var n = performance.getEntriesByType('navigation')[0]; nav = (n && n.type) || ''; } catch (_) {}
    var payload = {
        slug: slug, f: 'pick_lost_on_reload',
        age_s: Math.round(age / 1000),
        nav: nav,                                       // 'reload' / 'navigate' / 'back_forward'
        discarded: document.wasDiscarded ? 1 : 0,       // Chrome desktop discarder; Android reports 0
        src: rec.src || '',
        ia: window.__isInAppBrowser ? 1 : 0,
        mob: /Mobi/i.test(navigator.userAgent || '') ? 1 : 0,
        dm: navigator.deviceMemory || 0
    };
    // telemetry-tier0.js may still be booting at DOMContentLoaded — one retry.
    var send = function(){ try { if (window.__ae) { window.__ae('feature_use', payload); return true; } } catch (_) {} return false; };
    if (!send()) setTimeout(send, 2000);
    showPickLost(zone);
}
function showPickLost(el){
    if (!el || el.__pickLostBox) return;
    var loc = (window.__LOCALE || document.documentElement.lang || 'en').substring(0, 2);
    var text = PICK_LOST_TEXT[loc] || PICK_LOST_TEXT.en;
    var box = document.createElement('div');
    box.className = 'tu-pick-help tu-pick-lost';
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    var p = document.createElement('p');
    p.className = 'tu-pick-help-text';
    p.textContent = text;
    box.appendChild(p);
    var input = el.querySelector('input[type=file]')
        || document.querySelector('input[data-uploader-for="' + el.id + '"]');
    if (input) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tu-pick-help-btn';
        btn.textContent = PICK_LOST_BTN[loc] || PICK_LOST_BTN.en;
        btn.addEventListener('click', function(){
            try { if (window.__ae) window.__ae('feature_use', { slug: pickLostSlug(), f: 'pick_lost_retry' }); } catch (_) {}
            // Same bookkeeping as a zone tap, so a second kill is seen too.
            armUploadNoPickFollowup(el, 'pick_lost');
            markPickerOpen(el, 'pick_lost');
            input.click();
        });
        box.appendChild(btn);
    }
    (el.parentNode || document.body).insertBefore(box, el.nextSibling);
    el.__pickLostBox = box;
    // On phones the zone sits two screens down and a reloaded page opens at
    // the top: a note nobody can see explains nothing. Same phrase as a
    // toast when the box has no pixels in the viewport (the limit-wall
    // lesson of 2026-08-31). No scrolling — the global rule.
    try {
        requestAnimationFrame(function(){
            var r = box.getBoundingClientRect();
            var seen = r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < (window.innerHeight || 0);
            if (!seen && typeof window.__toast === 'function') window.__toast(text, 'warning');
        });
    } catch (_) {}
}
function hidePickLost(el){
    if (!el || !el.__pickLostBox) return;
    try { el.__pickLostBox.remove(); } catch (_) {}
    el.__pickLostBox = null;
}

function scanAndWire(){
    var els = document.querySelectorAll('.tool-uploader');
    for (var i = 0; i < els.length; i++) {
        wire(els[i]);
        // Was this page reloaded while THIS zone's picker was open? Runs
        // once per page, on the first wired zone that matches the record.
        try { checkLostPick(els[i]); } catch(_){}
        // Hand off to DeviceCapabilityGate for any uploader that opted in via
        // `gate="ffmpeg+memory"`. Belt-and-braces: gate also self-scans on its
        // own DOMContentLoaded, but the explicit handoff covers dynamic mounts.
        if (window.DeviceCapabilityGate && typeof window.DeviceCapabilityGate.autoBootstrap === 'function') {
            try { window.DeviceCapabilityGate.autoBootstrap(els[i]); } catch(_){}
        }
    }
    if (els.length) { try { installFoldCue(els[0]); } catch(_){} }
    renderSizeHints();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanAndWire);
} else {
    scanAndWire();
}

// ── Upload no-followup heuristic ───────────────────────────────────
// After a click that plausibly opened the OS file picker, watch for one
// of three terminal states within 30 s:
//   (a) tool-uploader:file dispatches → emit() cleared the timer; done.
//   (b) 30 s elapses with no file picked → emit
//       feature_use{f:'upload_no_pick_followup', src, state}.
//
// `state` is the load-bearing diagnostic field:
//   'dismissed' — page visibility transitioned to hidden during the
//                 window (the OS file picker overlay hides our page on
//                 most mobile platforms). User opened the picker and
//                 dismissed/cancelled without selecting. UX problem,
//                 not a bug.
//   'no_open'  — page stayed continuously visible AND the window never
//                lost focus. The picker never opened — Android Chrome UA
//                quirk, label-for activation failure, or our explicit
//                input.click() rejected.
//
//                ⚠️ MEASURED 2026-08-13: this is NOT a clean bug bucket, and
//                reading it as one costs a day. Of 554 no_open sessions over
//                30 days, 464 (84%) went on to pick a file anyway — yandex
//                148/191, safari 154/187, chrome 116/137. The dominant cause
//                is simply that 30 s is too short: choosing a video out of a
//                phone gallery routinely takes longer, and on mobile browsers
//                where the picker is a sheet rather than a foreground task,
//                neither visibilitychange nor blur fires to move it into
//                'dismissed'. Same blind spot that made silent-click-detector
//                call every upload zone on the site broken.
//
//                So the verdict is now SELF-CORRECTING: when a file arrives on
//                an uploader that already reported no_open, we emit
//                `upload_pick_after_no_open` carrying how long it really took.
//                The honest count is therefore
//                    no_open  −  upload_pick_after_no_open
//                and the `waited_s` distribution tells you what the threshold
//                should have been. The correction is emitted rather than the
//                verdict being withheld until pagehide on purpose: pagehide is
//                exactly what a killed mobile tab never sends, and a metric
//                that silently drops to zero reads as "no bugs left".
//                (window blur matters on DESKTOP: the OS file dialog is
//                a modal that steals window focus WITHOUT firing
//                visibilitychange — pre-2026-06-13 every desktop "picker
//                opened, user closed it" landed in no_open and polluted
//                the real-bug bucket; the 2026-06-12 Edge bug-report
//                trace was one of those.)
//
// Why we need it: pre-2026-06-07 we couldn't distinguish
//   - "picker never opened" (real bug)
//   - "picker opened, user dismissed without picking" (UX)
//   - "user gave up before tapping anywhere" (intent)
// All three looked identical in /pino: upload_zone_clicked as the last
// event, no file_picked, no error, abandon. The Redmi Note 10S /
// Chrome 135 bug-report (potolok62@inbox.ru 2026-06-07) was the trigger:
// 51 s on page, last_action upload_zone_clicked with src:'interactive',
// no follow-up. We could not tell which case it was.
//
// Why visibility-change is the signal: on Android Chrome / iOS Safari,
// when the OS file picker becomes the foreground task, the browser
// fires visibilitychange→hidden because the picker covers the page.
// When the picker closes, visibilitychange→visible. So a 30 s window
// with NO visibility transitions = the picker never came up =
// state:'no_open'. A window WITH a visibility transition (regardless of
// direction) = picker did open at some point = state:'dismissed'.
// (User gave up before any tap: NOT armed — we only arm on click; if
// no click happened, no timer exists. Pre-click intent change is
// observed via session_start without upload_zone_clicked elsewhere.)
var UPLOAD_NO_PICK_MS = 30000;
// The picker clock: when this zone last opened the system picker. Set with the
// no-pick watch below (every door that opens a picker arms it), cleared by
// either end — the file (emit) or the `cancel`. Unlike the watch it outlives
// the 30 s verdict, which is the point: a cancel after a long wait used to be
// unanswerable because the watch was already gone.
function pickerWaitS(el) {
    var t = el && el.__pickOpenAt;
    if (!t) return null;
    var s = Math.round((Date.now() - t) / 1000);
    return s >= 0 ? s : null;
}
function armUploadNoPickFollowup(el, src) {
    disarmUploadNoPickFollowup(el);
    if (el) el.__pickOpenAt = Date.now();
    if (typeof document === 'undefined' || document.visibilityState === 'hidden') return;
    var slug = (document.querySelector('meta[name="tool-slug"]') || {}).content || null;
    var sawHidden = false;
    var onVisChange = function() {
        // ANY visibility transition during the window is evidence the OS
        // picker became foreground. We don't disarm — let the 30 s timer
        // fire its normal followup; the state field carries the diagnostic.
        if (document.visibilityState === 'hidden') sawHidden = true;
    };
    // Desktop counterpart: the OS file dialog is a focus-stealing modal that
    // leaves the page fully visible. window blur within the watch window =
    // the picker (or at least SOMETHING modal) took focus → not the
    // "picker never opened" bug class.
    var onWinBlur = function() { sawHidden = true; };
    var timer = setTimeout(function() {
        el.__uploadNoPickTimer = null;
        document.removeEventListener('visibilitychange', onVisChange);
        window.removeEventListener('blur', onWinBlur);
        el.__uploadNoPickOnHidden = null;
        var state = sawHidden ? 'dismissed' : 'no_open';
        // Remember the verdict so a later pick can correct it (see the
        // self-correcting note above). Only 'no_open' needs correcting —
        // 'dismissed'/'cancelled' already mean the picker demonstrably opened.
        if (state === 'no_open') el.__uploadNoOpenAt = Date.now();
        try {
            if (window.__ae) window.__ae('feature_use', {
                slug: slug,
                f: 'upload_no_pick_followup',
                src: src,
                state: state,
            });
        } catch (_) {}
    }, UPLOAD_NO_PICK_MS);
    el.__uploadNoPickTimer = timer;
    el.__uploadNoPickOnHidden = onVisChange;
    el.__uploadNoPickOnBlur = onWinBlur;
    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('blur', onWinBlur);
}
function disarmUploadNoPickFollowup(el) {
    if (!el) return;
    if (el.__uploadNoPickTimer) {
        clearTimeout(el.__uploadNoPickTimer);
        el.__uploadNoPickTimer = null;
    }
    if (el.__uploadNoPickOnHidden) {
        try { document.removeEventListener('visibilitychange', el.__uploadNoPickOnHidden); } catch (_) {}
        el.__uploadNoPickOnHidden = null;
    }
    if (el.__uploadNoPickOnBlur) {
        try { window.removeEventListener('blur', el.__uploadNoPickOnBlur); } catch (_) {}
        el.__uploadNoPickOnBlur = null;
    }
}

// ── Public helper: __bindUploaderFile ──────────────────────────────
// Race-safe replacement for `el.addEventListener('tool-uploader:file', cb)`.
//
// Why this exists (added 2026-05-08 after vladimir_xakim@mail.ru's bug):
//   On slow networks (3G/4G) with multiple defer-scripts in a per-tool
//   blade, DOMContentLoaded fires LATE — sometimes after the user has
//   already clicked upload + selected a file. tool-uploader.js fires
//   `tool-uploader:file` on the upload zone, but the per-tool listener
//   (typically wrapped in DOMContentLoaded) hasn't attached yet. Event
//   is lost; tool sits dead until user re-picks the file (4 min in
//   vladimir's case before he gave up and submitted a bug-report).
//
// Same race hits content-blocker users (uBlock/AdBlock can delay per-tool
// scripts), Service-Worker first-visit, slow CPU devices, multi-tab
// navigation. ~138 tools using <x-tool-uploader> were vulnerable.
//
// This helper closes the class:
//   1. Attaches the normal `addEventListener` listener — fast path.
//   2. Drains `el.__pendingFile` if a file was picked BEFORE attach —
//      retroactive replay path.
//   3. Idempotent: `__consumedTs` prevents double-fire when the original
//      event reaches the listener in the race-fast case.
//   4. 30-second expiry ON A LOADED PAGE: a pending file older than that is
//      NOT replayed to a binder that appears later (we don't want to
//      surprise-process an old file). While the page is still loading there
//      is no limit: the tool's script is just late, and the zone has been
//      showing the file as loaded the whole time (since 07.10.2026).
//   5. Memory hygiene: clears the stamp once consumed so the File
//      reference doesn't pin OS handles indefinitely.
//   6. An OBSERVER is not a consumer: `__bindUploaderFile(el, cb, {observer:
//      true})` hears every pick (replayed ones too) and takes nothing away —
//      it never clears the stamp and never declares it expired. For a module
//      that only wants to KNOW what was picked.
//
// ⚠️ Point 6 exists because point 5 assumed ONE binder per zone, and that
// stopped being true. Since the end of August 2026 the limit primitive binds
// every zone through this helper (tool-usage-limit.js, bindUploaders) — and
// early, so the first «consumer» of a pick was IT, at dispatch: the stamp
// was gone before the tool, whose scripts were still on their way, could
// bind and be replayed from it. The rescue this helper exists for was
// switched off by its second user, silently — a lost pick leaves no event.
// Measured on production 07.10.2026 (/merge-images, where the primitive was
// the only binder): stamp gone the instant the pick was dispatched on 6 of 9
// returning visits and 1 of 9 first visits — a load race decides whether
// the primitive binds through this helper or through the layout's early
// stub, which clears nothing. With a binder ahead of it, /mute-video —
// moved to this helper in May for exactly this — was handed nothing on
// production (tests/playwright/uploader-early-pick-reaches-a-late-tool.mjs).
//
// Why not simply keep the stamp for everyone: a tool that binds a SECOND
// consumer later (a lazily loaded part, a re-wired panel) would be replayed
// a pick its first consumer has long since taken — the file added twice.
// «One consumer takes the pick» stays; observers stand outside it.
//
// Usage in tool blade:
//   __bindUploaderFile($upload, processFile);
// instead of:
//   $upload.addEventListener('tool-uploader:file', function(e) {
//       var f = e.detail && e.detail.file;
//       if (f) processFile(f);
//   });
//
// Companion: phpunit `UploaderListenerRaceTest` flags any blade still
// using the raw addEventListener pattern + ratchets the baseline as
// tools migrate.
var PENDING_EXPIRY_MS = 30000;
function bindUploaderFile(el, callback, opts) {
    if (!el || typeof callback !== 'function') return;
    var observer = !!(opts && opts.observer);
    var consumedTs = 0;
    var consume = function(detail, source) {
        if (!detail) return;
        var f = detail.file || (detail.files && detail.files[0]);
        if (!f) return;
        try { callback(f, detail); } catch (e) {
            try { console.error('[bindUploaderFile callback]', e); } catch(_){}
        }
        // Clear the stamp on first consume — File reference no longer
        // needs to be held for retroactive replay. An observer is not a
        // consumer (point 6): the tool this pick is for may not have bound.
        if (!observer && el.__pendingFile === detail) {
            el.__pendingFile = null;
            el.__pendingTs = 0;
        }
    };
    // Past the zone's late-listener door (watchLateListeners): this helper replays
    // by its own rule, and a second replay through the door would hand a later
    // consumer a pick the first one has already taken.
    listenPastLateDoor(el, 'tool-uploader:file', function(e) {
        if (el.__pendingTs && consumedTs === el.__pendingTs) return; // skip duplicate
        if (el.__pendingTs) consumedTs = el.__pendingTs;
        consume(e.detail);
    });
    // Retroactive replay: did the user pick before we attached? Defer the
    // callback to a microtask so the calling IIFE finishes its declarations
    // before we invoke its closure. Sentry TIMBRICA-FRONTEND-3Z/47 showed
    // a synchronous replay invoking the callback at the bindUploaderFile()
    // call-site line, BEFORE later `var AUDIO_EXTS = [...]` / `var
    // uploadContent = ...` assignments had executed — closure references
    // resolved to the hoisted `undefined`, throwing `Cannot read properties
    // of undefined (reading 'indexOf' / 'classList')`. queueMicrotask runs
    // after the current sync execution completes, so by the time the
    // callback fires every statement of the calling IIFE has run and all
    // closure refs are initialized. The `consumedTs` bookkeeping is still
    // updated synchronously, so a real-time 'tool-uploader:file' event that
    // races the microtask still de-duplicates correctly.
    if (el.__pendingFile && el.__pendingTs && consumedTs !== el.__pendingTs) {
        var ageMs = Date.now() - el.__pendingTs;
        // The 30 s limit is for a binder that appears on a LOADED page. While the
        // page is still loading, the tool's script is simply late, the zone has
        // been showing this file as loaded all along, and the pick is handed over
        // however long that took (see the late-listener block above wire()).
        if (ageMs <= PENDING_EXPIRY_MS || !pageSettled) {
            consumedTs = el.__pendingTs;
            try {
                if (typeof window.__ae === 'function') {
                    var slug = (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
                    // `observer`: a module that only watches was replayed the pick —
                    // not a tool rescued. Count the rescues where it is 0.
                    window.__ae('feature_use', { slug: slug, f: 'uploader_retroactive_pick', age_ms: ageMs, observer: observer ? 1 : 0 });
                }
            } catch(_){}
            var snapshot = el.__pendingFile;
            var defer = (typeof queueMicrotask === 'function')
                ? queueMicrotask
                : function(fn){ Promise.resolve().then(fn); };
            defer(function(){ consume(snapshot); });
        } else if (!observer) {
            // (an observer leaves a stale stamp where it is: declaring it
            // expired, and telling the zone so, is for the consumer it was
            // meant for — who may be listening for exactly that)
            //
            // Expired — drop the stamp, but fire a symmetric reject event so
            // host tools (and __ae telemetry) can observe the expiry instead
            // of inheriting the previous silent-drop behavior. Audit
            // wf_9d2ce135 #26 — without the event the 30-second cliff was
            // indistinguishable from "the tool froze" in tool_errors logs.
            try {
                el.dispatchEvent(new CustomEvent('tool-uploader:reject', {
                    detail: { reason: 'pending_expired', file: el.__pendingFile, age_ms: ageMs },
                    bubbles: false
                }));
            } catch (_) {}
            try {
                if (typeof window.__ae === 'function') {
                    var slugX = (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
                    window.__ae('feature_use', { slug: slugX, f: 'uploader_pending_expired', age_ms: ageMs });
                }
            } catch(_){}
            el.__pendingFile = null;
            el.__pendingTs = 0;
        }
    }
}
// Make it globally available — tools call __bindUploaderFile($upload, fn)
window.__bindUploaderFile = bindUploaderFile;

// Public API: tools can wire zones added dynamically after initial render.
window.ToolUploader = {
    wire: wire,
    scan: scanAndWire,
    /* The accept matcher, exported so the page-level folder-drop handler in
       layouts/app.blade.php filters a walked folder by exactly the same rule a
       zone would. A second copy over there would drift — the wildcard
       extension fallback below is subtle enough that the two would disagree on
       the first extensionless file. */
    accepts: matchesAccept,
    /* What the zone decided this file IS ('audio' | 'video' | 'image' | 'pdf'),
       from its bytes where the OS stayed silent. Exported so a tool that has to
       branch on kind asks the zone instead of re-deriving it from file.name —
       re-deriving is how «Format audio tidak didukung» happened on a file the
       zone had already accepted. '' for a file that never came through a zone. */
    kindOf: zoneKind,
    /* Byte-level identification, exported for the same reason `accepts` is:
       one table, one answer, testable from outside. */
    identify: identifyBytes,
    /* What the zone does to a file before handing it over: fill in a `type` the
       OS withheld, append the extension its bytes call for. Exported so a tool
       that still owns a raw <input type=file> (no zone in front of it) can ask
       for the same treatment instead of growing its own copy — and so the rules
       are testable from outside (tests/js/uploader-identity-repair.test.mjs). */
    repair: repairIdentity,
    /* Exported for the same reason `accepts` is: the rule that decides WHICH
       noun a refusal uses has to be holdable by a test. `.stl` belongs to two
       different formats, and the tie-break between them is exactly the kind of
       subtlety that rots silently — /subtitle-converter told people it wanted
       a 3D model file for its whole first week in public. */
    neededKind: describeNeededKind,
    /* Static injection helper — same as element.__uploaderInjectFile but lookup
       by id, with the wire-on-demand fallback for elements added late. Returns
       true on dispatch, false on missing/un-wired element. Source defaults to
       'programmatic' but callers should pass a more specific tag where useful
       ('share_target', 'inter_tool_route', 'sample_file', 'test_harness'). */
    inject: function(uploaderId, file, source){
        var el = document.getElementById(uploaderId);
        if (!el) return false;
        if (!el.__toolUploaderWired) wire(el);
        if (typeof el.__uploaderInjectFile !== 'function') return false;
        el.__uploaderInjectFile(file, source);
        return true;
    },
    /* Put a zone back to "waiting for a file" — same path the row's × takes,
       including the tool-uploader:clear event. For tools whose own "Start over"
       control must leave the uploader in a consistent state. */
    clear: function(uploaderId){
        var el = document.getElementById(uploaderId);
        if (!el || typeof el.__uploaderRestore !== 'function') return false;
        el.__uploaderRestore(false);
        return true;
    },
    // Internal — exposed for unit tests (tool-uploader-sniff.mjs). Pure
    // functions: classify magic bytes → kind, and accept-list ↔ kind.
    _classifyBytes: classifyBytes,
    _acceptAdmitsKind: acceptAdmitsKind,
    /* Which files of a pick are videos a sound-only zone answers with the way
       to the tool that pulls the sound out. Exposed so the rule — «judged by
       the accept the blade declared, not the one widened for the iPhone
       picker» — is held by tests/js/uploader-video-signpost.test.mjs. A tool
       does not call it: its door is zone.__uploaderVideoSignpost(files). */
    _videosForSignpost: videosForSignpost,
    /* Exposed for tool-handoff.js: the "send result to the next tool" rail must
       decide whether the TARGET tool would accept this file — and it has to use
       the very gate that target will apply, not a second copy of the format
       lists (we ship .webm fallbacks that some tools refuse). */
    _matchesAccept: matchesAccept,
    version: '1.3.0',
};

if (typeof window.__drainSharedModule === 'function') window.__drainSharedModule('ToolUploader');

})();
